import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toastError, presentMutationError, presentMutationWarning, toastWarning } from "../../../utils/showApiErrorToast";
import { toastSimulateWorkspaceSaveFailure } from "../../../utils/workspaceApiErrors";
import {
  Badge as ChakraBadge,
  Box,
  Button as ChakraButton,
  Checkbox as ChakraCheckbox,
  Collapse as ChakraCollapse,
  Divider as ChakraDivider,
  Flex as ChakraFlex,
  FormControl as ChakraFormControl,
  FormHelperText as ChakraFormHelperText,
  FormLabel as ChakraFormLabel,
  HStack as ChakraHStack,
  IconButton as ChakraIconButton,
  Input as ChakraInput,
  Select as ChakraSelect,
  SimpleGrid as ChakraSimpleGrid,
  Spinner as ChakraSpinner,
  Stack as ChakraStack,
  Switch as ChakraSwitch,
  Text as ChakraText,
  Textarea as ChakraTextarea,
  Tooltip as ChakraTooltip,
  useColorModeValue,
  useDisclosure,
  VStack as ChakraVStack,
} from "@chakra-ui/react";
import { ArrowBackIcon, ChevronDownIcon, ChevronUpIcon, InfoIcon } from "@chakra-ui/icons";
import { Loader2 } from "lucide-react";
import { useHistory } from "react-router-dom";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import CardHeader from "../../../components/Card/CardHeader";
import {
  ConfiguratorStyleMenu,
  ConfiguratorStyleMultiSelectMenu,
  useConfiguratorStyleInputProps,
} from "../../../components/Reports/ConfiguratorStyleMenu";
import { TrashIcon } from "../../../components/ui/trash";
import { TradeFilterSelect, TradeFilterSelectFullWidth } from "@/components/trade-filter-selects";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { DashboardHeaderDateRangePicker } from "../Dashboard/components/DashboardHeaderDateRangePicker";
import type {
  DashboardDatePresetTimeframe,
  DashboardDateRangeChangeOptions,
} from "@/utils/dashboardDateWindowStorage";
import { useTheme } from "next-themes";
import { reportDetailSectionCardProps } from "../Reports/components/ReportsCardChrome";
import { useReportsDetailColors } from "../Reports/reportsDetailTheme";
import { Button as ShadcnButton } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input as ShadcnInput } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TRADE_POLICY_BUCKET_METRIC_DEFINITIONS } from "../../../constants/tradePolicyBucketMetricDefinitions";
import { MAX_SIMULATE_INFLIGHT } from "../../../constants/securityLimits";
import { useActiveBrokerAccount } from "../../../hooks/useActiveBrokerAccount";
import { useRateLimitCooldown } from "../../../hooks/useRateLimitCooldown";
import { formatAccountDisplayLabelShort } from "../../../services/liquidBrokersApi";
import { useDashboardDateWindowListener } from "../../../hooks/useDashboardDateWindowListener";
import { SimulatorPolicyAddButton } from "./SimulatorPolicyAddButton";
import {
  fetchTradePolicyFilterCatalog,
  fetchReportPolicySimConfig,
  ReportPolicySimConfigConflictError,
  simulateTradePolicyStart,
  saveSimulateTradePolicyJob,
  SimulateInflightLimitError,
  upsertReportPolicySimConfig,
  waitForSimulateTradePolicyJob,
  SimulateTradePolicyJobStillRunningError,
} from "../../../services/tradePolicyApi";
import { getCandleBarBudgetExceededInfo } from "../../../utils/candleBarBudgetExceeded";
import { prepareSimulatePolicyWithDualTimeframes, resolveDualCandleTimeframes } from "../../../utils/dualCandleTimeframe";
import { SimulatorCandleBudgetGate } from "./SimulatorCandleBudgetGate";
import {
  createReportStrategyPolicyVersion,
  fetchAvailableInstruments,
  getReport,
  listReportStrategies,
  ReportsApiError,
} from "../../../services/reportsApi";
import type { ReportStrategyResponse } from "../../../types/reports";
import type { AvailableInstrumentRow } from "../../../types/tradeManagementReport";
import { formatAvailableInstrumentOptionLabel } from "../../../utils/marketDataSourceChoice";
import {
  deleteBacktest,
  getBacktestSlim,
  listBacktests,
  parseSimulateBacktestMeta,
  patchBacktest,
} from "../../../services/backtestsApi";
import type { DateRange } from "../../../types/trade";
import {
  isPolicyPayloadV1,
  isPolicyTreeBundleV1,
  type PolicyTreeV2,
  type TradePolicyAction,
  type TradePolicyCatalogField,
  type TradePolicyGroupOp,
  type TradePolicyGroupNode,
  type TradePolicyPolicyNode,
  type TradePolicyPolicyPayload,
  type TradePolicySimulatePolicy,
  type TradePolicySimulateRequest,
  type TradePolicyTreeMeta,
} from "../../../types/tradePolicy";
import type { TradeSideFilter } from "../../../types/tradeManagementReport";
import type { SimulateLegs } from "../../../types/managementExplorer";
import {
  completeExitPlacementSimulation,
  exitPlacementSimMetricsReady,
  startExitPlacementSimulation,
  type ExitPlacementSimulationStart,
  type SimulateExitPlacementSlicesResult,
} from "../../../utils/simulateManagementCandidate";
import { buildAnalyticsWindowSnakeFields } from "../../../utils/analyticsWindowParams";
import { safeRandomUUID } from "../../../utils/safeRandomUUID";
import {
  buildPersistedFromReports,
  deriveTimeframeKeyFromLocalDateRange,
  equalDateRange,
  getActiveDashboardDatePresetId,
  getInitialDashboardDateWindow,
  persistStateFromTradeLog,
  reportsStateFromPersisted,
  writeDashboardDateWindow,
} from "../../../utils/dashboardDateWindowStorage";
import { useUserNotificationTimezone } from "../../../hooks/useUserNotificationTimezone";
import { redirectToBillingPaywall } from "../../../utils/billingPaywall";
import { ApiRequestError } from "../../../utils/apiRequestError";
import { isPaywallRequiredError } from "../../../utils/paywallErrors";
import { WizardMarketDataOhlcGateNotice } from "@/components/MarketData/WizardMarketDataOhlcGateNotice";
import { useInstrumentSetup } from "../../../contexts/InstrumentSetupContext";
import { useWizardMarketDataOhlcGate } from "../../../hooks/useWizardMarketDataOhlcGate";
import { handleMarketDataUnresolvedError } from "../../../utils/handleMarketDataUnresolved";
import { resolveTradeListWindowQuery } from "../../../utils/notificationPeriod";
import { formatReportWindowLabelForDisplay } from "../../../utils/reportWindowLabel";
import { sanitizeSimulateReportId, sanitizeWorkspaceSimulateAttachFields } from "../../../utils/workspacePersistIds";
import {
  addChildToGroup,
  buildSimulatePolicy,
  catalogEnumValuesForPicker,
  countEditorNodes,
  countRuleLeaves,
  clearSimulatorPolicyControlLabel,
  clearedSimulatorPolicyToastTitle,
  defaultPolicyRoot,
  EditorGroupNode,
  editorRulePathChange,
  EditorNode,
  EditorRuleNode,
  exampleToEditorRoot,
  findNodeById,
  genEditorId,
  newEditorGroup,
  newEditorRule,
  policyNodeToEditor,
  removeNodeById,
  updateNodeById,
  formatSimulatePolicySummary,
  getPolicyEditorDisplayRoots,
  getPolicyViewDisplayNodes,
  isPolicyCombineShellGroup,
  parsePolicyTreeToEditorRoot,
  resolvePolicyDefaultSimAction,
  isMixedPolicySegmentActions,
  inferUniformEditorGroupAction,
  extractBundleManagementPolicyTree,
  mergePrefillManagementIntoBuiltPolicy,
  policyTreeV2RuleDisplayNodes,
} from "./policyTreeUtils";
import { workspacePolicyTreesEquivalentForLineage } from "../Workspace/workspacePolicyForkUtils";
import {
  exitPlacementPicksForReport,
  exitPlacementRuleLabelsFromPicks,
  sliceIdFromExitPlacementPick,
  sliceIdsFromExitPlacementPicks,
  simulationRuleFromExitPlacementPick,
  exitPlacementPickDisplayLabel,
} from "../../../utils/exitPlacementPicks";
import {
  detachExitPlacementRuleFromPick,
  findPolicyTreeRuleForExitPlacementPick,
  mergeExitPlacementPicksIntoPolicy,
  buildDetachedPolicyFromExitPlacementPicks,
  migratePresetRulesToDetachedPolicy,
  parseExitPlacementPolicyDraft,
  removePolicyTreeRule,
  resolveExitPlacementPolicyForSimulate,
  sliceIdsForPolicyTreeRules,
} from "../../../utils/exitPlacementPolicy";
import {
  backtestToComparisonRun,
  comparisonRunTitlesFromBacktestSummary,
  hydrateBacktestToComparisonRun,
} from "./backtestToComparisonRun";
import {
  resolveStrategyIdForBacktest,
  stampRunsWithSavedPolicyVersionFlags,
} from "./simulatorPolicyVersionSaved";
import { ComparisonResultsSection } from "./ComparisonResultsSection";
import { SimulationResultsDrawerChrome } from "./SimulationResultsDrawerChrome";
import { SimulatorAlert } from "./SimulatorAlert";
import { statisticsPageMinHeight } from "../Statistics/components/statisticsEmptyState";
import { StatisticsEmptyState } from "../Statistics/components/statisticsEmptyState";
import {
  readReportSimulatorRuns,
  writeReportSimulatorRuns,
} from "./reportSimulatorRunsStorage";
import {
  readStandaloneSimulatorRuns,
  writeStandaloneSimulatorRuns,
} from "./standaloneSimulatorRunsStorage";
import { SimulatorInlineEditActions } from "./simulatorInlineEdit";
import { SimulatorPicksKanban } from "./SimulatorPicksKanban";
import { PolicyGraphCanvas } from "./PolicyGraphCanvas";
import { PolicyTreeNodesAccordion } from "./PolicyTreeAccordion";
import { ExitPlacementPolicyTreeEditor } from "./ExitPlacementPolicyTreeEditor";
import { ExitPlacementSimLegSelector } from "./ExitPlacementSimLegSelector";
import { ExitPlacementPicksRunToolbar } from "./ExitPlacementPicksRunToolbar";
import { ExitPlacementComposeDialog } from "./ExitPlacementComposeDialog";
import { PolicyPickOverlapDialog } from "./PolicyPickOverlapDialog";
import {
  allocateOverlapSerials,
  withOverlapSerial,
} from "./policyPickOverlapPresentation";
import {
  buildPickToBacktestIdMap,
  selectionItemFromBacktest,
  selectionItemFromCombinedBundle,
  selectionItemFromReportPick,
  type OverlapSelectionItem,
} from "../../../utils/policyPickOverlapMappers";
import {
  normalizeMandatorySimulatorRunTitle,
  NextRunTitleField,
} from "./simulatorRunTitleFields";
import { sliceIdsFromComparisonRun } from "../../../utils/exitPlacementCompose";
import {
  clampSimulateLegs,
  defaultSimulateLegs,
  legAvailabilityFromExitPlacementPicks,
  legAvailabilityFromPolicyRules,
} from "../../../utils/exitPlacementSimLegs";
import {
  buildPolicyRuleFieldExplanation,
  numericGlossaryHelperForPath,
  PolicyRuleFieldExplanationBlock,
  type PolicyRuleFieldExplanation,
} from "./policyFieldExplanation";
import { SimulatorPageHero } from "./SimulatorPageHero";
import { SimulatorRunWizardLayout } from "./SimulatorRunWizardLayout";
import {
  SIMULATOR_LIST_PATH,
  SIMULATOR_RESULTS_PATH,
} from "./simulatorRoutes";
import { reportPageFieldFocusSx } from "../Reports/reportPageFieldFocus";
import {
  MAX_COMPARISON_RUNS,
  MAX_COMPARISON_RUN_TITLE_LENGTH,
  mergeHydratedComparisonRuns,
  normalizeComparisonRunSavedTitle,
  normalizeComparisonRunTitleFields,
  comparisonRunTitlesFromPersistedTitle,
  getComparisonRunSavedTitle,
  type ComparisonRun,
  type SimulatorPanelVariant,
  type ComparisonScopeSnapshot,
} from "./simulatorComparisonTypes";
import {
  REPORT_PICK_LIST_CHANGED_EVENT,
  clearAllPicks,
  clearPicksForReport,
  getSimulatorLane,
  incrementPickRanSeparatelyCount,
  incrementPickUsedInCombinedCount,
  markPicksAsRan,
  pickBucketId,
  readAllPicksFlat,
  readPickListForReport,
  removePick,
  setPickSimulatorLane,
  updatePick,
  type PickRuleLeaf,
  type ReportPick,
  type SimulatorPickLane,
} from "../../../utils/reportPickListStorage";
import type { SimulatorPickBundleCard } from "../../../utils/simulatorPickBundlesStorage";
import {
  addBundleCard,
  bundleBucketId,
  incrementBundleUsedAsChildCount,
  onSimulatorPickBundlesChanged,
  readBundlesForBucket,
  removeBundleCard,
  updateBundleCard,
} from "../../../utils/simulatorPickBundlesStorage";
import {
  pickHasImportableContent,
  pickImportDiagnostics,
  pickImportPolicyNodes,
  type PickImportCatalogContext,
  type PickImportDiagnostics,
} from "../../../utils/reportPickSlicePolicy";
import { buildConditionAnalysisSlices } from "../../../utils/conditionAnalysisSelectors";
import { resolvePickQuickStats } from "../../../utils/reportPickQuickStats";
import type { PickQuickStats } from "../../../utils/reportPickQuickStats";
import { mergedMetricDefinitionMap } from "../../../utils/marketContextMetricDefinitions";
import {
  isSimulateInflightErrorSlug,
  reportComparisonLimitToastCopy,
  simulationRunSavedAsStrategyVersionToastCopy,
} from "../../../utils/simulateQuotaToasts";
import { toast } from "sonner";
import { resolveAtomicCardToPolicyNode, collectBundleLeafImportUnits } from "../../../utils/simulatorPickAtomicPolicyResolver";

const MemoizedComparisonResultsSection = React.memo(ComparisonResultsSection);
const MemoizedSimulatorPicksKanban = React.memo(SimulatorPicksKanban);

function toastSimulatorComparisonAtLimit(scopedReportId: string | null): void {
  if (scopedReportId) {
    const copy = reportComparisonLimitToastCopy();
    toastWarning(copy.title, { description: copy.description });
    return;
  }
  toast.info("Comparison limit", {
    description: `Clear runs or remove some (max ${MAX_COMPARISON_RUNS}).`,
  });
}

function shouldSkipSimulateFailureToast(error: unknown): boolean {
  if (error instanceof SimulateInflightLimitError) return true;
  if (error instanceof ApiRequestError && isSimulateInflightErrorSlug(error.slug)) {
    return true;
  }
  if (error instanceof ReportsApiError && isSimulateInflightErrorSlug(error.slug)) {
    return true;
  }
  return false;
}

// Chakra's polymorphic `Button` props can produce “union too complex” IDE diagnostics in large TSX files.
// We keep runtime behavior, but simplify the local JSX typing.
const Button = ChakraButton as unknown as React.FC<any>;
const Checkbox = ChakraCheckbox as unknown as React.FC<any>;
const Collapse = ChakraCollapse as unknown as React.FC<any>;
const Flex = ChakraFlex as unknown as React.FC<any>;
const FormControl = ChakraFormControl as unknown as React.FC<any>;
const FormHelperText = ChakraFormHelperText as unknown as React.FC<any>;
const FormLabel = ChakraFormLabel as unknown as React.FC<any>;
const HStack = ChakraHStack as unknown as React.FC<any>;
const IconButton = ChakraIconButton as unknown as React.FC<any>;
const Input = ChakraInput as unknown as React.FC<any>;
const Select = ChakraSelect as unknown as React.FC<any>;
const SimpleGrid = ChakraSimpleGrid as unknown as React.FC<any>;
const Stack = ChakraStack as unknown as React.FC<any>;
const Switch = ChakraSwitch as unknown as React.FC<any>;
const Text = ChakraText as unknown as React.FC<any>;
const Textarea = ChakraTextarea as unknown as React.FC<any>;
const VStack = ChakraVStack as unknown as React.FC<any>;

const SIMULATOR_OPTION_FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";

const SIMULATOR_OPTION_FIELD_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-sidebar-muted-foreground";

function SimulatorWizardScopeField(props: {
  label: string;
  description: string;
  required?: boolean;
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Field
      className={`w-full min-w-0 content-start [grid-template-rows:auto_minmax(2.25rem,auto)_auto]${
        props.className ? ` ${props.className}` : ""
      }`}
      data-simulator-wizard-scope-fields=""
    >
      <FieldLabel
        htmlFor={props.htmlFor}
        className={`${SIMULATOR_OPTION_FIELD_LABEL_CLASS} min-h-5 leading-5`}
      >
        {props.label}
        {props.required ? (
          <span className="ml-0.5 text-[#E00101]" aria-hidden>
            *
          </span>
        ) : null}
      </FieldLabel>
      <div className="flex min-h-9 w-full min-w-0 flex-col items-stretch gap-1">
        {props.children}
      </div>
      <FieldDescription
        className={`${SIMULATOR_OPTION_FIELD_DESC_CLASS} whitespace-normal md:min-h-[2.5rem]`}
      >
        {props.description}
      </FieldDescription>
    </Field>
  );
}

const SIMULATOR_ACTION_OPTIONS = {
  picks: [
    { value: "veto" as const, label: "Remove" },
    { value: "keep_only" as const, label: "Keep Only" },
  ],
  builder: [
    { value: "veto" as const, label: "Remove Matching Trades" },
    { value: "keep_only" as const, label: "Keep Only Matching Trades" },
  ],
};

function simulatorActionLabel(variant: "picks" | "builder", action: TradePolicyAction | null): string {
  if (action === null) return "Select action";
  const match = SIMULATOR_ACTION_OPTIONS[variant].find((option) => option.value === action);
  return match?.label ?? "Select action";
}

function SimulatorActionField(props: {
  id: string;
  variant: "picks" | "builder";
  value: TradePolicyAction | null;
  onChange: (value: TradePolicyAction) => void;
  disabled?: boolean;
  className?: string;
  /** Combined policy with both Remove and Keep segments — show locked Mixed label. */
  mixedActions?: boolean;
}): React.ReactElement {
  const options = SIMULATOR_ACTION_OPTIONS[props.variant];
  if (props.mixedActions) {
    return (
      <Field
        data-simulator-toolbar-field=""
        data-field="action"
        data-policy-action-mixed=""
        className={props.className ?? "w-fit min-w-0 shrink-0"}
      >
        <FieldLabel htmlFor={props.id} className={SIMULATOR_OPTION_FIELD_LABEL_CLASS}>
          {props.variant === "builder" ? "Default action" : "Action"}
        </FieldLabel>
        <div
          id={props.id}
          data-trade-filter-bar=""
          className="flex h-9 w-full min-w-[12rem] items-center rounded-[6px] border border-border bg-content-canvas px-3 font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-sidebar-muted-foreground"
          title="This combined policy includes both Remove and Keep segments. Each segment keeps its own action."
        >
          Mixed actions
        </div>
      </Field>
    );
  }
  return (
    <Field
      data-simulator-toolbar-field=""
      data-field="action"
      className={props.className ?? "w-fit min-w-0 shrink-0"}
    >
      <FieldLabel htmlFor={props.id} className={SIMULATOR_OPTION_FIELD_LABEL_CLASS}>
        {props.variant === "builder" ? "Default action" : "Action"}
      </FieldLabel>
      <div data-trade-filter-bar="" className="w-fit min-w-0">
        <TradeFilterSelect
          id={props.id}
          layout="fit"
          fitToLongestOption
          value={props.value ?? ""}
          onValueChange={(next) => {
            if (!next) return;
            props.onChange(next as TradePolicyAction);
          }}
          options={options}
          disabled={props.disabled}
          placeholder="Select action"
          emptyValueShowsPlaceholder
        />
      </div>
    </Field>
  );
}

const SIMULATOR_COMBINE_OPTIONS = [
  { value: "and" as const, label: "Combine AND" },
  { value: "or" as const, label: "Combine OR" },
];

function SimulatorCombineField(props: {
  id: string;
  value: TradePolicyGroupOp | null;
  onChange: (value: TradePolicyGroupOp) => void;
  disabled?: boolean;
  className?: string;
}): React.ReactElement {
  return (
    <Field
      data-simulator-toolbar-field=""
      data-field="combine"
      className={props.className ?? "w-fit min-w-0 shrink-0"}
    >
      <FieldLabel htmlFor={props.id} className={SIMULATOR_OPTION_FIELD_LABEL_CLASS}>
        Combine
      </FieldLabel>
      <div data-trade-filter-bar="" className="w-fit min-w-0">
        <TradeFilterSelect
          id={props.id}
          layout="fit"
          fitToLongestOption
          value={props.value ?? ""}
          onValueChange={(next) => {
            if (!next) return;
            props.onChange(next as TradePolicyGroupOp);
          }}
          options={SIMULATOR_COMBINE_OPTIONS}
          disabled={props.disabled}
          placeholder="Select combine"
          emptyValueShowsPlaceholder
        />
      </div>
    </Field>
  );
}

const Divider = ChakraDivider as unknown as React.FC<any>;
const Tooltip = ChakraTooltip as unknown as React.FC<any>;

const TradeLogLossRed = "#E00101";

/** Picker dates and/or dashboard timeframe presets (e.g. 7-days with null dates) still resolve to a valid window. */
function hasUsableTradeWindowForSimulate(dateRange: DateRange, timeframe: string, reportingTimeZone: string): boolean {
  if (dateRange.startDate && dateRange.endDate) return true;
  const w = resolveTradeListWindowQuery(dateRange, timeframe, reportingTimeZone);
  if (w.kind === "local") return Boolean(w.period?.local_start_date && w.period?.local_end_date);
  return w.kind === "timeframe" && Boolean(w.timeframe);
}

/** Session hint when filter-catalog `version` changes (e.g. filter_catalog.v5). */
const TRADE_POLICY_CATALOG_VERSION_STORAGE_KEY = "tessera_trade_policy_filter_catalog_version";
const TRADE_POLICY_EDITOR_UI_STATE_PREFIX = "tessera_trade_policy_editor_ui_state";
const STANDALONE_SIMULATOR_DRAFT_STORAGE_KEY = "tessera_standalone_simulator_draft_v1";
const DEFAULT_POLICY_LIMIT = 5000;
const DEFAULT_MAX_DEPTH = 16;
const DEFAULT_MAX_NODES = 256;

function toLocalDateString(d: Date | null): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function editorUiStateKey(reportId: string): string {
  return `${TRADE_POLICY_EDITOR_UI_STATE_PREFIX}:${reportId}`;
}

type StandaloneSimulatorDraft = {
  brokerAccountCode?: string;
  dateRange?: { startDate: string | null; endDate: string | null };
  symbol?: string;
  candleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
  policyRowVersion?: "policy_row.v1" | "policy_row.pine_v1";
  policyRoot?: EditorNode;
  builderSimAction?: TradePolicyAction | null;
  linkedStrategyId?: string;
};

function attachLinkedStrategyId(run: ComparisonRun, strategyId: string | null | undefined): ComparisonRun {
  const sid = strategyId?.trim();
  return sid ? { ...run, linkedStrategyId: sid } : run;
}

function readStandaloneSimulatorDraft(): StandaloneSimulatorDraft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STANDALONE_SIMULATOR_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return isRecord(parsed) ? (parsed as StandaloneSimulatorDraft) : null;
  } catch {
    return null;
  }
}

function writeStandaloneSimulatorDraft(draft: StandaloneSimulatorDraft): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STANDALONE_SIMULATOR_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // ignore quota / privacy errors
  }
}

function pickRuleLeafToEditorRule(
  leaf: PickRuleLeaf,
  catalogFields: TradePolicyCatalogField[]
): { ok: true; node: EditorRuleNode } | { ok: false; reason: string } {
  const field = catalogFields.find((f) => f.path === leaf.path);
  if (!field) return { ok: false, reason: `Unknown catalog field: ${leaf.path}` };
  if (!(field.allowed_ops ?? []).includes(leaf.op)) return { ok: false, reason: `Operator "${leaf.op}" not allowed for ${field.label}.` };

  const node = newEditorRule(catalogFields);
  node.path = leaf.path;
  node.op = leaf.op;
  if (field.value_kind === "number") {
    if (leaf.valueNumber == null || !Number.isFinite(leaf.valueNumber)) return { ok: false, reason: `Missing numeric value for ${field.label}.` };
    node.valueNumber = String(leaf.valueNumber);
  } else {
    if (leaf.op === "in") {
      const set = (leaf.valueEnumSet ?? []).filter(Boolean);
      if (set.length === 0) return { ok: false, reason: `Missing enum set for ${field.label}.` };
      node.valueEnumSet = set;
      node.valueEnum = "";
    } else {
      const v = (leaf.valueEnum ?? "").trim();
      if (!v) return { ok: false, reason: `Missing enum token for ${field.label}.` };
      node.valueEnum = v;
      node.valueEnumSet = [];
    }
  }
  return { ok: true, node };
}

function SimulatorMainColumn(props: { children: React.ReactNode; layout?: string }): React.ReactElement {
  const embedded = props.layout === "embedded";
  return (
    <Box
      data-reports-simulator-embedded={embedded ? "" : undefined}
      w="100%"
      maxW={embedded ? undefined : "100%"}
      minW={0}
      flex={embedded ? 1 : undefined}
      minH={embedded ? "100%" : undefined}
      h={embedded ? "100%" : undefined}
      display="flex"
      flexDirection="column"
      gap={6}
    >
      {props.children}
    </Box>
  );
}

function VetoExplainCallout(): React.ReactElement {
  const bg = useColorModeValue("blue.50", "rgba(30, 58, 138, 0.22)") as string;
  const border = useColorModeValue("blue.200", "blue.700") as string;
  const color = useColorModeValue("gray.800", "gray.100") as string;
  return (
    <Box borderWidth="1px" borderRadius="md" borderColor={border} bg={bg} p={3}>
      <Box as="p" fontSize="sm" color={color} m={0}>
        <Box as="span" fontWeight="semibold">
          Remove
        </Box>{" "}
        — if the policy tree is true for a trade, that trade is excluded from the filtered set.{" "}
        <Box as="span" fontWeight="semibold">
          Keep Only
        </Box>{" "}
        — if the tree is false, the trade is excluded. The server evaluates AND/OR with short-circuiting.
      </Box>
    </Box>
  );
}

function ReportRulesHelpPanel(props: {
  borderColor: string;
  textColor: string;
  subtleText: string;
  isOpen: boolean;
  onToggle: () => void;
}): React.ReactElement {
  const { borderColor, textColor, subtleText, isOpen, onToggle } = props;
  const ChevronIcon: React.ElementType = isOpen ? ChevronUpIcon : ChevronDownIcon;
  return (
    <Box borderWidth="1px" borderRadius="md" borderColor={borderColor} p={3}>
      <Box
        as="button"
        type="button"
        onClick={onToggle}
        width="100%"
        textAlign="left"
        color={textColor}
        fontWeight="medium"
        fontSize="sm"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={2}
        borderRadius="md"
        _hover={{ bg: "rgba(0,0,0,0.04)" }}
      >
        <Box as="span">How do I turn a written report rule into these controls?</Box>
        <Box as={ChevronIcon} aria-hidden />
      </Box>
      <Collapse in={isOpen} animateOpacity>
        <Stack spacing={3} pt={3} fontSize="sm" color={subtleText}>
          <Text color={textColor} fontWeight="medium">
            1. Use the catalog as the source of truth
          </Text>
          <Text>
            Every rule must use a <strong>Field</strong> from the server. Nested <strong>groups</strong> let you mix AND
            and OR (e.g. long trades AND (regime A OR regime B OR ADX hot)).
          </Text>
          <Text color={textColor} fontWeight="medium">
            2. “Stand down / don’t take / block” → Remove
          </Text>
          <Text>
            If the report says you would skip trades in some situation, use <strong>Remove</strong> and build a tree that is
            true for the trades you want to simulate removing.
          </Text>
          <Text color={textColor} fontWeight="medium">
            3. Numbers
          </Text>
          <Text>
            Use the hint under each numeric input for units and meaning. Operators ≥ / ≤ / = apply to that field’s numeric
            value.
          </Text>
        </Stack>
      </Collapse>
    </Box>
  );
}

function CatalogErrorBanner({ message }: { message: string | null }): React.ReactElement | null {
  if (!message) return null;
  return (
    <SimulatorAlert tone="error" data-simulator-catalog-error="">
      <>
        <strong>Error.</strong> {message}
      </>
    </SimulatorAlert>
  );
}

export type { SimulatorPanelVariant } from "./simulatorComparisonTypes";

/** Link simulate jobs to this report (persist via POST .../jobs/{id}/save after the job succeeds). */
function reportScopedSimulatePersistFields(
  reportId: string | null | undefined
): Pick<TradePolicySimulateRequest, "report_id"> {
  const id = sanitizeSimulateReportId(reportId);
  if (!id) return {};
  return { report_id: id };
}

export type ExecutionRulesSimulatorPanelProps = {
  /** `standalone` = full simulator page (policy + scope only). `report` = embedded in a report (picks + locked scope). */
  variant?: SimulatorPanelVariant;
  /** If set, the simulator scopes picks and saves jobs under this report id. */
  scopedReportId?: string | null;
  /** Optional prefill for the scope window; avoids an extra report fetch in embedded contexts. */
  prefillWindow?: { start_date?: string | null; end_date?: string | null; timeframe?: string | null } | null;
  /** Report generation instant — freezes Trade Review to the report cohort. */
  reportCreatedAt?: string | null;
  /** Optional strategy id prefill (e.g. from ReportDetail’s already-loaded report). */
  prefillStrategyId?: string | null;
  /** Prefill symbol from report / canvas context. Empty string = all symbols. */
  prefillSymbol?: string | null;
  /** Lock strategy picker when running from a canvas policy (strategy is already known). */
  strategyLocked?: boolean;
  /** Hide scope controls (scope is still applied from prefill/defaults). */
  hideScopeControls?: boolean;
  /** Hide the “View all simulations” button in the hero. */
  hideSavedBacktestsButton?: boolean;
  /** When set on the standalone page, hero shows a back control (e.g. return to `/simulator`). */
  wizardExitHref?: string;
  wizardExitLabel?: string;
  /** Prefer over `wizardExitHref` when the wizard lives in a dialog (close without routing). */
  onWizardExit?: () => void;
  /** Target for “View all simulations” (default `/simulator`). */
  savedSimulationsHref?: string;
  /** Standalone: use a multi-step wizard shell (scope → policy → generate). */
  simulatorWizardLayout?: boolean;
  /**
   * Workspace create flows — start with an empty policy tree and do not hydrate or
   * auto-save into the report simulator config / browser drafts.
   */
  startWithEmptyPolicy?: boolean;
  /** Merged into history state when the wizard navigates to standalone results after Generate. */
  resultsLocationState?: Record<string, unknown>;
  /** Standalone results page: comparison + trade review only (hydrated from session). */
  resultsOnlyLayout?: boolean;
  /** Pre-select a run tab on the results page (`?run=` query). */
  initialSelectedRunId?: string | null;
  /** Load a saved simulation by backtest id (results page from simulator list). */
  initialBacktestId?: string;
  /** Back navigation from the results page (defaults to wizardExitHref). */
  onResultsExit?: () => void;
  /** Standalone results page: active simulation title for the inset breadcrumb. */
  onActiveSimulationTitleChange?: (title: string | null) => void;
  /** Use compact padding suitable for embedding (no big top offset). */
  layout?: "page" | "embedded";
  /** When scoped to a report, hydrate saved simulations into comparison cards on load. */
  hydrateSavedBacktests?: boolean;
  /** Report parent already fetched linked simulations — avoids duplicate list calls. */
  linkedBacktests?: import("../../../types/backtests").Backtest[];
  linkedBacktestsLoading?: boolean;
  /** Notify parent when a run is saved as a simulation. */
  onBacktestSaved?: (backtestId: string) => void | Promise<void>;
  /**
   * Workspace wizard: job is queued — show canvas processing placeholder (like report generate)
   * and finish wait/persist on the canvas host. Pass `jobId` when the host should wait+save.
   */
  onWorkspaceSimulationQueued?: (preview: {
    title: string;
    jobId?: string;
    /** Submitted simulate policy — used to decide derived CP lineage forks. */
    policy?: unknown;
    /** Post-hydrate rebuild baseline (same pipeline as Generate). */
    baselinePolicyForLineage?: unknown;
    /**
     * True when submitted rebuild differs from post-hydrate baseline
     * (lineage canonicalize). Date/TF-only changes stay false.
     */
    policyChangedFromBaseline?: boolean;
  }) => void;
  /** Workspace wizard: queued run failed before save — dismiss the canvas placeholder. */
  onWorkspaceSimulationFailed?: () => void;
  /**
   * Canvas wizard: when set, Generate is blocked with this warning (e.g. report
   * simulation cap). Checked before starting the simulate job.
   */
  workspaceSimulationBlockedReason?: string | null;
  /** Notify parent when a saved simulation is deleted (e.g. drop from Trade Review / Trading PnL selectors). */
  onBacktestDeleted?: (backtestId: string) => void;
  /** Notify parent after a strategy policy version is saved from a run (e.g. refresh PineScript version list). */
  onStrategyVersionSaved?: (backtestId?: string) => void | Promise<void>;
  /** When saving policy from workspace simulation drawer, register policy node on canvas. */
  workspacePolicyContext?: import("../../../types/workspace").WorkspaceCreateContext;
  /**
   * Report-drawer Exit Placement: resolve policy attach parent to the simulation node
   * for a backtest (never the report node).
   */
  resolveWorkspacePolicyAttach?: (
    backtestId: string
  ) => import("../../../types/workspace").WorkspaceCreateContext | null | undefined;
  /** When saving a simulation from a workspace report/parent, register simulation node on canvas. */
  workspaceSimulationContext?: import("../../../types/workspace").WorkspaceCreateContext;
  /** Workspace simulation drawer — close the sheet. */
  onClose?: () => void;
  /** Workspace simulation drawer — after delete (close sheet + refresh canvas). */
  onDeleted?: () => void;
  /** Default policy_row_version for simulations (persisted per report when scoped). */
  defaultPolicyRowVersion?: "policy_row.v1" | "policy_row.pine_v1";
  /** Default candle timeframe for simulations (persisted per report when scoped). */
  defaultCandleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
  /** When true (policy Run), candle TF is read-only and must not be coarsened to fit the bar budget. */
  candleTimeframeLocked?: boolean;
  /** Dual-TF RA+EP: entry-filter candle TF. */
  entryCandleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
  /** Dual-TF RA+EP: Exit Placement / management replay TF. */
  managementCandleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
  /**
   * Canvas policy Run: let the user choose the adopted report window (send `report_id`)
   * or any free date range (omit `report_id`). Requires `scopedReportId`.
   */
  allowFreeWindowOutsideReport?: boolean;
  /** Stepped wizard: start on this step (Edit & run opens on Policy so it is not auto-Generate). */
  initialSimulatorWizardStep?: number;
  /** When set, force this version and hide the selector (report-lock). */
  lockedPolicyRowVersion?: "policy_row.v1" | "policy_row.pine_v1";
  /** When set, force broker_account_code for all runs (report-lock). */
  lockedBrokerAccountCode?: string | null;
  /** Report input snapshot — powers market-context definition click-through on segment picks. */
  inputSnapshot?: unknown;
  /** Trade Management: prefill v2 policy from segment preview. */
  prefillPolicy?: import("../../../types/tradePolicy").PolicyTreeV2 | null;
  /** Prefill the v1 / bundle editor tree (e.g. combined policy from workspace). */
  prefillPolicyTree?: unknown;
  /** `v2` for trade-management reports; default v1 entry-filter simulator. */
  policyMode?: "v1" | "v2";
  /** Cohort side filter from report snapshot meta. */
  tradeSideFilter?: TradeSideFilter;
  /** Segment key stored on policy-sim-config after a v2 run. */
  managementSegmentKey?: string;
  /** Notified after v2 simulate completes (job saved + policy-sim-config linked). */
  onSimulateComplete?: (result: SimulateExitPlacementSlicesResult) => void;
  /** Trade-management execution-candles overlay mode for trade review under results. */
  simulationOverlayMode?: string;
  /** Report parent: notify when in-session comparison run count changes (quota pill). */
  onComparisonRunsUsedChange?: (used: number) => void;
};

export function ExecutionRulesSimulatorPanel(props: ExecutionRulesSimulatorPanelProps): JSX.Element {
  const history = useHistory();
  const shadcnInset = useShadcnInsetChrome();
  const pageMinHeight = statisticsPageMinHeight(shadcnInset);
  const scopedReportId = props.scopedReportId ?? null;
  const allowFreeWindowOutsideReport = Boolean(
    props.allowFreeWindowOutsideReport && scopedReportId
  );
  const variant: SimulatorPanelVariant =
    props.variant ?? (scopedReportId ? "report" : "standalone");
  const isStandalone = variant === "standalone";
  const startWithEmptyPolicy = Boolean(props.startWithEmptyPolicy);
  const isManagementV2Mode = props.policyMode === "v2";
  const showPicksSection = variant === "report" && Boolean(scopedReportId);
  const embeddedInReport = props.layout === "embedded";
  const reportsDetail = useReportsDetailColors();
  const { resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const standaloneTextColor = isDarkTheme ? "white" : "gray.800";
  const standaloneSubtleText = isDarkTheme
    ? props.resultsOnlyLayout
      ? "white"
      : "var(--sidebar-muted-foreground)"
    : "gray.500";
  const standaloneBorderColor = isDarkTheme ? "var(--border)" : "gray.200";
  const standaloneEvidencePreBg = isDarkTheme ? "var(--sidebar)" : "gray.50";
  const standaloneEvidencePreBorder = isDarkTheme ? "var(--border)" : "gray.200";
  const standalonePolicyViewSelectedBg = isDarkTheme ? "var(--sidebar-accent)" : "gray.100";
  const textColor = embeddedInReport ? reportsDetail.textColor : standaloneTextColor;
  const subtleText = embeddedInReport ? reportsDetail.subtleText : standaloneSubtleText;
  const borderColor = embeddedInReport ? reportsDetail.borderColor : standaloneBorderColor;
  const evidencePreBg = embeddedInReport ? reportsDetail.insetSurfaceBg : standaloneEvidencePreBg;
  const evidencePreBorder = embeddedInReport ? reportsDetail.evidencePreBorder : standaloneEvidencePreBorder;
  const policyViewSelectedBg = embeddedInReport
    ? reportsDetail.isDark
      ? "var(--sidebar-accent)"
      : "gray.100"
    : standalonePolicyViewSelectedBg;
  const innerSectionCardProps = embeddedInReport
    ? reportDetailSectionCardProps
    : isDarkTheme
      ? reportDetailSectionCardProps
      : { boxShadow: "none" as const, borderWidth: "1px" as const, borderColor };

  const panelBg = embeddedInReport ? reportsDetail.panelBg : isDarkTheme ? "#151517" : "white";

  const { activeAccountCode, accounts, loading: brokerAccountsLoading } = useActiveBrokerAccount();
  const scopeInputProps = useConfiguratorStyleInputProps();
  const scopeMenuRadius = "6px";
  const [standaloneBrokerCode, setStandaloneBrokerCode] = useState<string>("");
  const didHydrateStandaloneDraftRef = useRef(false);

  const resolvedLockedBrokerAccountCode = useMemo(() => {
    const locked = props.lockedBrokerAccountCode?.trim() || "";
    if (!locked) return "";
    if (accounts.some((a) => a.accountCode === locked)) return locked;
    const fuzzy = accounts.find(
      (a) =>
        a.accountCode.endsWith(locked) ||
        locked.endsWith(a.accountCode) ||
        formatAccountDisplayLabelShort(a) === locked
    );
    return fuzzy?.accountCode ?? locked;
  }, [accounts, props.lockedBrokerAccountCode]);

  const effectiveBrokerAccountCode =
    (resolvedLockedBrokerAccountCode ||
      (isStandalone ? standaloneBrokerCode : activeAccountCode) ||
      "") || "";

  const { openSetup } = useInstrumentSetup();
  const tryHandleMarketDataUnresolved = useCallback(
    (e: unknown) => handleMarketDataUnresolvedError(e, openSetup, effectiveBrokerAccountCode),
    [openSetup, effectiveBrokerAccountCode]
  );

  const brokerAccountMenuOptions = useMemo(
    () =>
      accounts.map((a) => ({
        value: a.accountCode,
        label: formatAccountDisplayLabelShort(a),
      })),
    [accounts]
  );

  const brokerAccountMenuLabel = useMemo(() => {
    if (!effectiveBrokerAccountCode) {
      return brokerAccountsLoading ? "Loading accounts…" : "Select account";
    }
    return (
      brokerAccountMenuOptions.find((o) => o.value === effectiveBrokerAccountCode)?.label ??
      effectiveBrokerAccountCode
    );
  }, [brokerAccountMenuOptions, brokerAccountsLoading, effectiveBrokerAccountCode]);
  const brokerAccountLocked = Boolean(props.lockedBrokerAccountCode?.trim());
  const strategyPickerLocked = Boolean(
    props.strategyLocked || (props.prefillStrategyId?.trim() && props.candleTimeframeLocked)
  );
  const { timezone: notificationTimezone, revision: notificationTzRevision } = useUserNotificationTimezone();
  const reportingTimeZone =
    notificationTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";

  const [initialWindow] = useState(() => reportsStateFromPersisted(getInitialDashboardDateWindow()));
  const [useCustomRange, setUseCustomRange] = useState(initialWindow.useCustomRange);
  const [timeframe, setTimeframe] = useState<string>(initialWindow.timeframe);
  const [dateRange, setDateRange] = useState<DateRange>(initialWindow.dateRange);
  const [symbol, setSymbol] = useState(() => props.prefillSymbol?.trim() || "");
  const [availableInstruments, setAvailableInstruments] = useState<AvailableInstrumentRow[]>([]);
  const [instrumentsLoading, setInstrumentsLoading] = useState(false);

  const wizardScopeSymbol = useMemo(
    () => symbol.trim() || props.prefillSymbol?.trim() || "",
    [props.prefillSymbol, symbol]
  );

  const wizardMdGate = useWizardMarketDataOhlcGate({
    brokerAccountCode: effectiveBrokerAccountCode,
    symbol: wizardScopeSymbol || undefined,
    enabled:
      Boolean(props.simulatorWizardLayout) && Boolean(effectiveBrokerAccountCode.trim()),
  });

  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalogFields, setCatalogFields] = useState<TradePolicyCatalogField[]>([]);
  /** `marginal_bucket_import.eq_string_prefixes` from filter-catalog (e.g. session → session_time.session_id). */
  const [marginalEqPrefixMap, setMarginalEqPrefixMap] = useState<Record<string, string>>({});
  const [catalogVersion, setCatalogVersion] = useState<string>("");
  const [policySchemaVersion, setPolicySchemaVersion] = useState<string>("");
  const [policyTreeMeta, setPolicyTreeMeta] = useState<TradePolicyTreeMeta | null>(null);

  const [policyRoot, setPolicyRoot] = useState<EditorNode>(() => defaultPolicyRoot([]));
  /** Option 2 policy builder / Run & add to comparison — no default until chosen. */
  const [builderSimAction, setBuilderSimAction] = useState<TradePolicyAction | null>(null);
  /** Option 1 run-from-picks — independent from builder action. */
  const [picksSimAction, setPicksSimAction] = useState<TradePolicyAction | null>(null);
  const [policyRowVersion, setPolicyRowVersion] = useState<"policy_row.v1" | "policy_row.pine_v1">(
    props.lockedPolicyRowVersion ?? props.defaultPolicyRowVersion ?? "policy_row.pine_v1"
  );
  const effectivePolicyRowVersion = props.lockedPolicyRowVersion ?? policyRowVersion;

  const [candleTimeframe, setCandleTimeframe] = useState<
    "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d"
  >(props.defaultCandleTimeframe ?? "1m");
  const candleTimeframeLocked = Boolean(props.candleTimeframeLocked);
  const [candleBudgetExceeds, setCandleBudgetExceeds] = useState(false);
  /** False until candle-budget-preview settles — blocks wizard Continue on scope. */
  const [candleBudgetPreviewSettled, setCandleBudgetPreviewSettled] = useState(false);
  /** Report cohort vs free account window (canvas policy Run only). */
  const [windowSource, setWindowSource] = useState<"report" | "custom">("report");
  const effectiveSimulateReportId =
    allowFreeWindowOutsideReport && windowSource === "custom" ? null : scopedReportId;

  const defaultV2Policy = useMemo(
    (): PolicyTreeV2 => props.prefillPolicy ?? { schema_version: "policy_tree.v2", rules: [] },
    [props.prefillPolicy]
  );
  const [v2PolicyDraft, setV2PolicyDraft] = useState(() => JSON.stringify(defaultV2Policy, null, 2));
  const v2PolicyDraftRef = useRef(v2PolicyDraft);
  v2PolicyDraftRef.current = v2PolicyDraft;
  const commitV2PolicyDraft = useCallback((next: PolicyTreeV2 | string) => {
    const serialized = typeof next === "string" ? next : JSON.stringify(next, null, 2);
    v2PolicyDraftRef.current = serialized;
    setV2PolicyDraft(serialized);
  }, [incrementPickRanSeparatelyCount]);
  const [exitPlacementSimulateLegs, setExitPlacementSimulateLegs] = useState<SimulateLegs>("tp_only");
  const exitPlacementSimulateLegsRef = useRef(exitPlacementSimulateLegs);
  exitPlacementSimulateLegsRef.current = exitPlacementSimulateLegs;
  const setExitPlacementSimulateLegsSynced = useCallback((next: SimulateLegs) => {
    exitPlacementSimulateLegsRef.current = next;
    setExitPlacementSimulateLegs(next);
  }, []);
  const [exitPlacementSimLegError, setExitPlacementSimLegError] = useState<string | null>(null);
  const [exitPlacementComposeRunId, setExitPlacementComposeRunId] = useState<string | null>(null);

  useEffect(() => {
    if (!props.prefillPolicy) return;
    if (startWithEmptyPolicy) return;
    commitV2PolicyDraft(props.prefillPolicy);
    setPolicySchemaVersion("policy_tree.v2");
  }, [commitV2PolicyDraft, props.prefillPolicy, startWithEmptyPolicy]);

  useEffect(() => {
    if (!isManagementV2Mode) return;
    setPolicySchemaVersion("policy_tree.v2");
  }, [isManagementV2Mode]);

  useEffect(() => {
    if (!props.lockedPolicyRowVersion) return;
    setPolicyRowVersion(props.lockedPolicyRowVersion);
  }, [props.lockedPolicyRowVersion]);

  useEffect(() => {
    if (!props.defaultCandleTimeframe) return;
    setCandleTimeframe((prev) => (prev === props.defaultCandleTimeframe ? prev : props.defaultCandleTimeframe!));
  }, [props.defaultCandleTimeframe]);

  const [simLoading, setSimLoading] = useState(false);
  const simulateRateLimit = useRateLimitCooldown("simulate");
  const lastSimulateAtRef = useRef(0);
  /**
   * Wizard Generate gate: Continue and Generate share one footer slot. A single
   * click can fire Continue then land on the swapped-in Generate. Only allow
   * Generate after the user is on the Review step and the click has settled.
   */
  const wizardGenerateGateRef = useRef({ step: 0, armed: false });
  const [comparisonRuns, setComparisonRuns] = useState<ComparisonRun[]>(() => {
    // Canvas Edit & run wizard: never revive session jobs (autosave was attaching
    // them to the workspace as soon as the dialog opened).
    if (props.simulatorWizardLayout && props.workspaceSimulationContext) return [];
    const reportId = props.scopedReportId?.trim();
    if (!reportId) return [];
    return readReportSimulatorRuns(reportId);
  });
  useEffect(() => {
    props.onComparisonRunsUsedChange?.(comparisonRuns.length);
  }, [comparisonRuns.length, props.onComparisonRunsUsedChange]);
  const [linkedBacktestsRefreshKey, setLinkedBacktestsRefreshKey] = useState(0);
  const didHydrateStandaloneRunsRef = useRef(false);
  const didHydrateInitialBacktestRef = useRef<string | null>(null);
  const [initialBacktestLoading, setInitialBacktestLoading] = useState(false);
  const [initialBacktestError, setInitialBacktestError] = useState<string | null>(null);
  const activePollRunIdsRef = useRef<Set<string>>(new Set());
  const [picks, setPicks] = useState<ReportPick[]>(() => {
    return scopedReportId ? readPickListForReport(scopedReportId) : [];
  });
  const bundlesBucketId = bundleBucketId(scopedReportId);
  const [combinedBundles, setCombinedBundles] = useState<SimulatorPickBundleCard[]>(() =>
    readBundlesForBucket(bundlesBucketId)
  );
  const [reportStrategyId, setReportStrategyId] = useState<string | null>(null);
  const [standaloneLinkedStrategyId, setStandaloneLinkedStrategyId] = useState("");
  const [strategyOptions, setStrategyOptions] = useState<ReportStrategyResponse[]>([]);
  const [strategiesLoading, setStrategiesLoading] = useState(false);
  const didHydrateWindowFromReportRef = useRef<string | null>(null);
  const [importingPicks, setImportingPicks] = useState(false);
  const [pickRunLoadingAction, setPickRunLoadingAction] = useState<"individual" | "combined" | null>(null);
  const [picksRunTitleDraft, setPicksRunTitleDraft] = useState("");
  const [builderRunTitleDraft, setBuilderRunTitleDraft] = useState("");
  const [selectedPickIds, setSelectedPickIds] = useState<Set<string>>(() => new Set());
  const [selectedBundleIds, setSelectedBundleIds] = useState<Set<string>>(() => new Set());
  const pickIdSet = useMemo(() => new Set(picks.map((p) => p.id)), [picks]);
  const bundleIdSet = useMemo(() => new Set(combinedBundles.map((b) => b.id)), [combinedBundles]);
  const selectedAtomicIds = useMemo(
    () => {
      const s = new Set<string>();
      selectedPickIds.forEach((id) => s.add(id));
      selectedBundleIds.forEach((id) => s.add(id));
      return s;
    },
    [selectedPickIds, selectedBundleIds]
  );

  const handleSelectedAtomicIdsChange = useCallback(
    (next: React.SetStateAction<Set<string>>) => {
      const currentAtomic = (() => {
        const s = new Set<string>();
        selectedPickIds.forEach((id) => s.add(id));
        selectedBundleIds.forEach((id) => s.add(id));
        return s;
      })();

      const nextAtomic =
        typeof next === "function" ? (next as (prev: Set<string>) => Set<string>)(currentAtomic) : next;

      const nextPickIds = new Set<string>();
      const nextBundleIds = new Set<string>();
      nextAtomic.forEach((id) => {
        if (pickIdSet.has(id)) nextPickIds.add(id);
        else if (bundleIdSet.has(id)) nextBundleIds.add(id);
      });
      setSelectedPickIds(nextPickIds);
      setSelectedBundleIds(nextBundleIds);
    },
    [bundleIdSet, pickIdSet, selectedPickIds, selectedBundleIds]
  );
  const [combineSelectedOp, setCombineSelectedOp] = useState<TradePolicyGroupOp>("and");
  const [combineExplicitlySelected, setCombineExplicitlySelected] = useState(false);
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(() => new Set());
  const [draggedRuleId, setDraggedRuleId] = useState<string | null>(null);
  const draggedRuleIdRef = useRef<string | null>(null);
  const [policyViewMode, setPolicyViewMode] = useState<"builder" | "graph">("builder");
  const [simConfigRevision, setSimConfigRevision] = useState<number>(0);
  const simConfigRevisionRef = useRef(0);
  simConfigRevisionRef.current = simConfigRevision;
  const [simConfigHydrated, setSimConfigHydrated] = useState<boolean>(!scopedReportId);
  const [linkedPickId, setLinkedPickId] = useState<string | null>(null);
  const linkedPickResetTimerRef = useRef<number | null>(null);
  const lastSimConfigSaveErrorToastAtRef = useRef<number>(0);
  const suppressSimConfigSaveRef = useRef<boolean>(false);
  const picksCardRef = useRef<HTMLDivElement | null>(null);
  const {
    isOpen: overlapDialogOpen,
    onOpen: onOpenOverlapDialog,
    onClose: onCloseOverlapDialog,
  } = useDisclosure();
  const [overlapInitialSelection, setOverlapInitialSelection] = useState<OverlapSelectionItem[]>([]);
  const [overlapEntry, setOverlapEntry] = useState<"picks" | "saved_sims">("picks");
  const overlapPickToBacktestId = useMemo(
    () => buildPickToBacktestIdMap(picks, comparisonRuns),
    [comparisonRuns, picks]
  );

  const { isOpen: reportHelpOpen, onToggle: onToggleReportHelp } = useDisclosure();
  const didHydrateSavedBacktestsRef = useRef<string | null>(null);
  const maxDepth = policyTreeMeta?.limits?.max_depth ?? DEFAULT_MAX_DEPTH;
  const maxNodes = policyTreeMeta?.limits?.max_nodes ?? DEFAULT_MAX_NODES;

  const fieldsByPath = useMemo(() => {
    const m = new Map<string, TradePolicyCatalogField>();
    for (const f of catalogFields) m.set(f.path, f);
    return m;
  }, [catalogFields]);

  const deserializeSavedPolicyRoot = useCallback(
    (raw: unknown): EditorNode | null => {
      if (!isRecord(raw)) return null;

      // Supports payload-shaped persistence: { action, root }.
      if ("root" in raw && isRecord((raw as Record<string, unknown>).root)) {
        const rootCandidate = (raw as { root: unknown }).root;
        if (isRecord(rootCandidate) && (rootCandidate.kind === "group" || rootCandidate.kind === "rule")) {
          try {
            return policyNodeToEditor(rootCandidate as unknown as TradePolicyPolicyNode, fieldsByPath);
          } catch {
            return null;
          }
        }
      }

      // Supports root-only policy-node persistence: { kind, op/path, ... }.
      if ((raw as { kind?: unknown }).kind === "group" || (raw as { kind?: unknown }).kind === "rule") {
        try {
          return policyNodeToEditor(raw as unknown as TradePolicyPolicyNode, fieldsByPath);
        } catch {
          return null;
        }
      }

      return null;
    },
    [fieldsByPath]
  );

  const pickCatalogCtx: PickImportCatalogContext = useMemo(
    () => ({
      fieldsByPath,
      marginalEqPrefixes: marginalEqPrefixMap,
    }),
    [fieldsByPath, marginalEqPrefixMap]
  );

  /** Align simulate window with statistics / bundles: local calendar + timezone, or preset timeframe (no ISO mixing). */
  const buildSimulatorScopeAndWindowFields = useCallback(
    (limitValue: number) => {
      const emptyRange: DateRange = { startDate: null, endDate: null };
      const useDates = isStandalone || useCustomRange;
      const dr = useDates ? dateRange : emptyRange;
      const fields = buildAnalyticsWindowSnakeFields(dr, timeframe, reportingTimeZone);
      const wLocal = fields.local_start_date != null && fields.local_end_date != null;
      const scope: ComparisonScopeSnapshot = {
        useCustomRange: useDates,
        symbol: symbol.trim(),
        limit: limitValue,
        includeTradeIds: true,
        candleTimeframe,
        ...(wLocal
          ? {
              localStartDate: fields.local_start_date,
              localEndDate: fields.local_end_date,
              timezone: fields.timezone,
            }
          : { timeframe: fields.timeframe }),
      };
      return { fields, scope };
    },
    [
      candleTimeframe,
      isStandalone,
      useCustomRange,
      dateRange,
      timeframe,
      reportingTimeZone,
      notificationTzRevision,
      symbol,
    ]
  );

  const importablePicks = useMemo(
    () => picks.filter((p) => pickImportPolicyNodes(p, pickCatalogCtx).length > 0),
    [picks, pickCatalogCtx]
  );

  const exitPlacementPicks = useMemo(
    () => (isManagementV2Mode ? exitPlacementPicksForReport(picks) : []),
    [isManagementV2Mode, picks]
  );

  const importableExitPlacementPicks = useMemo(
    () => exitPlacementPicks.filter((p) => sliceIdFromExitPlacementPick(p) != null),
    [exitPlacementPicks]
  );

  const picksWithMappedContent = useMemo(() => picks.filter((p) => pickHasImportableContent(p)), [picks]);

  const pickDiagnosticsById = useMemo(() => {
    const m = new Map<string, PickImportDiagnostics>();
    for (const p of picks) {
      m.set(p.id, pickImportDiagnostics(p, pickCatalogCtx));
    }
    return m;
  }, [picks, pickCatalogCtx]);

  const pickDiag = useCallback(
    (p: ReportPick): PickImportDiagnostics =>
      pickDiagnosticsById.get(p.id) ?? pickImportDiagnostics(p, pickCatalogCtx),
    [pickCatalogCtx, pickDiagnosticsById]
  );

  const quickStatsForPick = useCallback(
    (pick: ReportPick) => resolvePickQuickStats(pick, props.inputSnapshot),
    [props.inputSnapshot]
  );

  const parsePrefillDateToLocalDate = useCallback((raw: string): Date | null => {
    const s = raw.trim();
    if (!s) return null;
    // Accept "YYYY-MM-DD" as well as ISO-ish strings ("YYYY-MM-DDTHH:mm:ssZ", etc.).
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (!m) return null;
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const d = parseInt(m[3], 10);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
    // Construct in local time to preserve calendar day selection semantics.
    return new Date(y, mo - 1, d, 0, 0, 0, 0);
  }, []);

  const applyPrefillWindowToScope = useCallback(
    (window: { start_date?: string | null; end_date?: string | null; timeframe?: string | null }) => {
      const start = window.start_date
        ? parsePrefillDateToLocalDate(String(window.start_date))
        : null;
      const end = window.end_date ? parsePrefillDateToLocalDate(String(window.end_date)) : null;
      if (start && end) {
        setUseCustomRange(true);
        setDateRange({ startDate: start, endDate: end });
        return;
      }
      if (window.timeframe) {
        setUseCustomRange(false);
        setTimeframe(String(window.timeframe));
        setDateRange({ startDate: null, endDate: null });
      }
    },
    [parsePrefillDateToLocalDate]
  );

  const selectWindowSource = useCallback(
    (next: "report" | "custom") => {
      setWindowSource(next);
      if (next === "report" && props.prefillWindow) {
        applyPrefillWindowToScope(props.prefillWindow);
      }
    },
    [applyPrefillWindowToScope, props.prefillWindow]
  );

  const candleBudgetWindowFields = useMemo(() => {
    // Always prefer explicit dates when present — BE candle-budget-preview rejects
    // report_id / timeframe-only queries ("Provide an explicit date window").
    const hasExplicitDates = Boolean(dateRange.startDate && dateRange.endDate);
    const useDates = hasExplicitDates || isStandalone || useCustomRange;
    const emptyRange: DateRange = { startDate: null, endDate: null };
    return buildAnalyticsWindowSnakeFields(
      useDates ? dateRange : emptyRange,
      timeframe,
      reportingTimeZone
    );
  }, [
    dateRange,
    isStandalone,
    notificationTzRevision,
    reportingTimeZone,
    timeframe,
    useCustomRange,
  ]);

  const handleSelectCandleBudgetPeriod = useCallback(
    (startYmd: string, endYmd: string) => {
      const start = parsePrefillDateToLocalDate(startYmd);
      const end = parsePrefillDateToLocalDate(endYmd);
      if (!start || !end) return;
      setUseCustomRange(true);
      setDateRange({ startDate: start, endDate: end });
      setCandleBudgetExceeds(false);
    },
    [parsePrefillDateToLocalDate]
  );

  const candleTimeframeHints = useCallback(
    () => ({
      preferredCandleTimeframe: candleTimeframe,
      candleTimeframeLocked,
      entryCandleTimeframe: props.entryCandleTimeframe,
      managementCandleTimeframe: props.managementCandleTimeframe,
    }),
    [
      candleTimeframe,
      candleTimeframeLocked,
      props.entryCandleTimeframe,
      props.managementCandleTimeframe,
    ]
  );

  const preparePolicyForSimulate = useCallback(
    (policy: unknown) => prepareSimulatePolicyWithDualTimeframes(policy, candleTimeframeHints()),
    [candleTimeframeHints]
  );

  /**
   * Edit & run lineage: post-hydrate rebuild snapshot. Generate compares the
   * submitted policy to this — not a dirty flag (hydrate churn was forcing forks).
   */
  const editRunLineageBaselineRef = useRef<unknown>(null);

  const dualCandleBudgetFields = useMemo(() => {
    const resolved = resolveDualCandleTimeframes(
      props.prefillPolicyTree ?? props.prefillPolicy ?? null,
      candleTimeframeHints()
    );
    if (!resolved.isDual || !resolved.management_candle_timeframe) return {};
    return {
      entry_candle_timeframe: resolved.entry_candle_timeframe ?? candleTimeframe,
      management_candle_timeframe: resolved.management_candle_timeframe,
    };
  }, [
    candleTimeframe,
    candleTimeframeHints,
    props.prefillPolicy,
    props.prefillPolicyTree,
  ]);

  useEffect(() => {
    setPicksSimAction(null);
    setBuilderSimAction(null);
    setCombineExplicitlySelected(false);
    setCombineSelectedOp("and");
  }, [scopedReportId]);

  useEffect(() => {
    if (!scopedReportId) {
      setSimConfigHydrated(true);
      setSimConfigRevision(0);
      return;
    }
    if (catalogLoading) return;
    if (startWithEmptyPolicy) {
      setPolicyRoot(defaultPolicyRoot(catalogFields));
      setBuilderSimAction(null);
      setPicksSimAction(null);
      setCombineExplicitlySelected(false);
      setCombineSelectedOp("and");
      setCollapsedGroupIds(new Set());
      setPolicyViewMode("builder");
      setSimConfigRevision(0);
      setSimConfigHydrated(true);
      suppressSimConfigSaveRef.current = false;
      commitV2PolicyDraft({ schema_version: "policy_tree.v2", rules: [] });
      return;
    }
    // Canvas / policy-version Run: keep prefilled tree — do not overwrite or autosave
    // into the report's policy-sim-config (would send stale revision: 0 without a GET).
    if (props.prefillPolicyTree != null || props.prefillPolicy != null) {
      setSimConfigHydrated(true);
      suppressSimConfigSaveRef.current = true;
      return;
    }
    let alive = true;
    setSimConfigHydrated(false);
    suppressSimConfigSaveRef.current = true;
    void (async () => {
      try {
        const saved = await fetchReportPolicySimConfig(scopedReportId);
        if (!alive) return;
        let localEditorRoot: EditorNode | null = null;
        try {
          const rawLocal = sessionStorage.getItem(editorUiStateKey(scopedReportId));
          if (rawLocal) {
            const parsed = JSON.parse(rawLocal) as unknown;
            if (isRecord(parsed) && (parsed.kind === "group" || parsed.kind === "rule")) {
              localEditorRoot = parsed as EditorNode;
            }
          }
        } catch {
          // ignore local state read failures
        }
        if (!saved) {
          setPolicyRoot(localEditorRoot ?? defaultPolicyRoot(catalogFields));
          setSimConfigRevision(0);
          return;
        }

        const restoredRoot = deserializeSavedPolicyRoot(saved.policy_tree);
        if (localEditorRoot) {
          setPolicyRoot(localEditorRoot);
        } else if (restoredRoot) {
          setPolicyRoot(restoredRoot);
        } else {
          setPolicyRoot(defaultPolicyRoot(catalogFields));
        }

        const scope = (isRecord(saved.scope_inputs) ? saved.scope_inputs : {}) as Record<string, unknown>;
        if (typeof scope["use_custom_range"] === "boolean") {
          setUseCustomRange(scope["use_custom_range"]);
        }
        if (typeof scope["timeframe"] === "string" && scope["timeframe"].trim()) {
          setTimeframe(scope["timeframe"]);
        }
        const localStartDate =
          typeof scope["local_start_date"] === "string" ? parsePrefillDateToLocalDate(scope["local_start_date"]) : null;
        const localEndDate =
          typeof scope["local_end_date"] === "string" ? parsePrefillDateToLocalDate(scope["local_end_date"]) : null;
        if (localStartDate && localEndDate) {
          setDateRange({ startDate: localStartDate, endDate: localEndDate });
        }
        if (typeof scope["symbol"] === "string") {
          setSymbol(scope["symbol"]);
        }
        if (
          scope["candle_timeframe"] === "1m" ||
          scope["candle_timeframe"] === "5m" ||
          scope["candle_timeframe"] === "15m" ||
          scope["candle_timeframe"] === "30m" ||
          scope["candle_timeframe"] === "1h" ||
          scope["candle_timeframe"] === "4h" ||
          scope["candle_timeframe"] === "1d"
        ) {
          setCandleTimeframe(scope["candle_timeframe"]);
        }
        if (
          !props.lockedPolicyRowVersion &&
          (scope["policy_row_version"] === "policy_row.v1" || scope["policy_row_version"] === "policy_row.pine_v1")
        ) {
          setPolicyRowVersion(scope["policy_row_version"]);
        }

        const view = (isRecord(saved.view_state) ? saved.view_state : {}) as Record<string, unknown>;
        const mode = view["mode"] ?? view["policy_view_mode"];
        if (mode === "builder" || mode === "graph") {
          setPolicyViewMode(mode);
        }
        if (Array.isArray(view["collapsed_group_ids"])) {
          setCollapsedGroupIds(new Set(view["collapsed_group_ids"].filter((x): x is string => typeof x === "string")));
        }

        setSimConfigRevision(typeof saved.revision === "number" ? saved.revision : 0);
      } catch (e) {
        if (!alive) return;
        presentMutationWarning(e, "Could not load saved simulator config");
        setPolicyRoot(defaultPolicyRoot(catalogFields));
        setSimConfigRevision(0);
      } finally {
        if (!alive) return;
        setSimConfigHydrated(true);
        window.setTimeout(() => {
          suppressSimConfigSaveRef.current = false;
        }, 0);
      }
    })();
    return () => {
      alive = false;
    };
  }, [
    catalogFields,
    catalogLoading,
    commitV2PolicyDraft,
    deserializeSavedPolicyRoot,
    parsePrefillDateToLocalDate,
    props.lockedPolicyRowVersion,
    props.prefillPolicy,
    props.prefillPolicyTree,
    scopedReportId,
    startWithEmptyPolicy,
  ]);

  useEffect(() => {
    if (scopedReportId) return;
    const prefill = props.prefillStrategyId?.trim() || null;
    setReportStrategyId(prefill);
  }, [props.prefillStrategyId, scopedReportId]);

  useEffect(() => {
    if (!scopedReportId) {
      didHydrateWindowFromReportRef.current = null;
      return;
    }

    // Embedded mode: report already loaded, so prefill from props (once per report id).
    if (props.prefillWindow && didHydrateWindowFromReportRef.current !== scopedReportId) {
      applyPrefillWindowToScope(props.prefillWindow);
      didHydrateWindowFromReportRef.current = scopedReportId;
      if (allowFreeWindowOutsideReport) {
        setWindowSource("report");
      }
    }

    if (props.prefillStrategyId) {
      setReportStrategyId(props.prefillStrategyId);
      return;
    }

    // Page mode: hydrate from report fetch (and also capture strategy id).
    if (props.prefillWindow) return;
    let alive = true;
    void (async () => {
      try {
        const r = await getReport(scopedReportId);
        if (!alive) return;
        setReportStrategyId(r.strategy_id ?? null);

        // When navigating from a report into the simulator, prefill the window from the report itself.
        // Do this once per report id so we don't clobber user edits after they start tweaking the scope.
        if (didHydrateWindowFromReportRef.current !== scopedReportId) {
          const start = r.start_date ? parsePrefillDateToLocalDate(String(r.start_date)) : null;
          const end = r.end_date ? parsePrefillDateToLocalDate(String(r.end_date)) : null;
          if (start && end) {
            setUseCustomRange(true);
            setDateRange({ startDate: start, endDate: end });
          } else if (r.timeframe) {
            setUseCustomRange(false);
            setTimeframe(String(r.timeframe));
            setDateRange({ startDate: null, endDate: null });
          }
          didHydrateWindowFromReportRef.current = scopedReportId;
        }
      } catch {
        if (!alive) return;
        setReportStrategyId(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [
    allowFreeWindowOutsideReport,
    applyPrefillWindowToScope,
    parsePrefillDateToLocalDate,
    props.prefillStrategyId,
    props.prefillWindow,
    scopedReportId,
  ]);

  useEffect(() => {
    if (!scopedReportId) return;
    const missing = picks.some((p) => (p.kind === "situation" || p.kind === "bucket") && (!p.policyNodes || p.policyNodes.length === 0));
    if (!missing) return;
    let cancelled = false;
    void (async () => {
      try {
        const r = await getReport(scopedReportId);
        if (cancelled) return;
        const slices = buildConditionAnalysisSlices((r as any)?.input_snapshot);
        const nodeBySliceId = new Map<string, any>(slices.map((s) => [s.id, s.node]));
        const clauseSig = (clauses: any): string => {
          if (!Array.isArray(clauses)) return "";
          const parts = clauses
            .map((c) => {
              const k = typeof c?.key === "string" ? c.key : "";
              const v = typeof c?.value === "string" ? c.value : String(c?.value ?? "");
              return `${k}=${v}`;
            })
            .filter(Boolean)
            .sort();
          return parts.join("|");
        };

        for (const p of picks) {
          if (p.policyNodes && p.policyNodes.length > 0) continue;
          if (p.kind !== "situation" && p.kind !== "bucket") continue;
          const sliceId = (typeof p.sliceId === "string" ? p.sliceId : "") || String((p.meta as any)?.slice_id ?? "all");
          const node = nodeBySliceId.get(sliceId) ?? nodeBySliceId.get("all");
          if (!node) continue;

          if (p.kind === "situation") {
            const situation = typeof (p.meta as any)?.situation === "string" ? String((p.meta as any).situation) : p.label;
            const wantClauses = clauseSig((p.meta as any)?.clauses);
            const rows = [
              ...(Array.isArray(node?.market_condition_brackets?.best_situations) ? node.market_condition_brackets.best_situations : []),
              ...(Array.isArray(node?.market_condition_brackets?.worst_situations) ? node.market_condition_brackets.worst_situations : []),
            ];
            const match =
              rows.find((r: any) => String(r?.situation ?? "") === situation && clauseSig(r?.clauses) === wantClauses) ??
              rows.find((r: any) => String(r?.situation ?? "") === situation) ??
              null;
            const sugg = (match as any)?.policy_import_nodes ?? (match as any)?.simulator_suggestions;
            if (Array.isArray(sugg) && sugg.length > 0) {
              updatePick(scopedReportId, p.id, { policyNodes: sugg });
            }
          }

          if (p.kind === "bucket") {
            const bucket = typeof (p.meta as any)?.bucket === "string" ? String((p.meta as any).bucket) : p.label;
            const metricKey = typeof (p.meta as any)?.metric_key === "string" ? String((p.meta as any).metric_key) : "";
            const rows = [
              ...(Array.isArray(node?.best_conditions) ? node.best_conditions : []),
              ...(Array.isArray(node?.worst_conditions) ? node.worst_conditions : []),
            ];
            const match =
              rows.find((r: any) => String(r?.bucket ?? "") === bucket && (!metricKey || String(r?.metric ?? "") === metricKey)) ??
              rows.find((r: any) => String(r?.bucket ?? "") === bucket) ??
              null;
            const sugg = (match as any)?.policy_import_nodes ?? (match as any)?.simulator_suggestions;
            if (Array.isArray(sugg) && sugg.length > 0) {
              updatePick(scopedReportId, p.id, { policyNodes: sugg });
            }
          }
        }
      } catch {
        // ignore (fallback is manual re-add)
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [picks, scopedReportId]);

  const mergeHydratedBacktestsIntoRuns = useCallback(
    async (list: import("../../../types/backtests").Backtest[]) => {
      const slice = list.slice(0, MAX_COMPARISON_RUNS);
      const hydratedRuns = await Promise.all(slice.map((row) => hydrateBacktestToComparisonRun(row)));
      let runs = hydratedRuns
        .filter((run): run is ComparisonRun => run != null)
        .map((run) => normalizeComparisonRunTitleFields(run))
        .map((run) => {
          if (run.linkedStrategyId || !reportStrategyId) return run;
          return { ...run, linkedStrategyId: reportStrategyId };
        });
      runs = await stampRunsWithSavedPolicyVersionFlags(runs, reportStrategyId);
      runs.sort((a, b) => b.createdAt - a.createdAt);
      // Always merge — including an empty hydrated list — so deleted sims leave session/storage.
      setComparisonRuns((prev) => mergeHydratedComparisonRuns(prev, runs));
    },
    [reportStrategyId]
  );

  useEffect(() => {
    if (!props.hydrateSavedBacktests) return;
    if (!scopedReportId) return;
    if (!effectiveBrokerAccountCode) return;
    if (props.linkedBacktestsLoading) return;

    if (props.linkedBacktests != null) {
      void mergeHydratedBacktestsIntoRuns(props.linkedBacktests);
      return;
    }

    if (didHydrateSavedBacktestsRef.current === scopedReportId) return;

    let cancelled = false;
    void (async () => {
      try {
        const resp = await listBacktests({
          broker_account_code: effectiveBrokerAccountCode,
          report_id: scopedReportId,
          limit: 50,
          offset: 0,
          include_result: true,
        });
        if (cancelled) return;
        const list = Array.isArray(resp?.backtests) ? resp.backtests : [];
        await mergeHydratedBacktestsIntoRuns(list);
        didHydrateSavedBacktestsRef.current = scopedReportId;
      } catch {
        if (!cancelled) didHydrateSavedBacktestsRef.current = scopedReportId;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    effectiveBrokerAccountCode,
    mergeHydratedBacktestsIntoRuns,
    props.hydrateSavedBacktests,
    props.linkedBacktests,
    props.linkedBacktestsLoading,
    scopedReportId,
  ]);

  useEffect(() => {
    if (!scopedReportId || isStandalone) return;
    writeReportSimulatorRuns(scopedReportId, comparisonRuns);
  }, [comparisonRuns, isStandalone, scopedReportId]);

  const linkedStrategyIdForNewRuns = useMemo((): string | null => {
    if (scopedReportId) return reportStrategyId?.trim() || null;
    if (isStandalone) {
      return standaloneLinkedStrategyId.trim() || reportStrategyId?.trim() || null;
    }
    return reportStrategyId?.trim() || null;
  }, [isStandalone, reportStrategyId, scopedReportId, standaloneLinkedStrategyId]);

  useEffect(() => {
    const sid = linkedStrategyIdForNewRuns?.trim();
    if (!sid) return;
    setComparisonRuns((prev) => {
      let changed = false;
      const next = prev.map((run) => {
        if (run.linkedStrategyId?.trim()) return run;
        changed = true;
        return { ...run, linkedStrategyId: sid };
      });
      return changed ? next : prev;
    });
  }, [linkedStrategyIdForNewRuns]);

  const stampSavedBacktestOnRun = useCallback(
    (runId: string, backtestId: string | undefined) => {
      const strategyStamp = linkedStrategyIdForNewRuns?.trim() || undefined;
      setComparisonRuns((prev) =>
        prev.map((r) =>
          r.id === runId
            ? {
                ...r,
                isSavedBacktest: true,
                ...(backtestId ? { backtestId } : {}),
                ...(!r.linkedStrategyId?.trim() && strategyStamp ? { linkedStrategyId: strategyStamp } : {}),
              }
            : r
        )
      );
      if (backtestId) {
        // Canvas Edit & run wizard: host attaches via onWorkspaceSimulationQueued —
        // do not also fire onBacktestSaved from background autosave.
        // Report drawer (even with workspaceSimulationContext) still needs this so
        // Trading PnL / Trade Review / canvas refresh without a hard reload.
        const canvasWizardOwnsAttach =
          Boolean(props.simulatorWizardLayout) && Boolean(props.workspaceSimulationContext);
        if (!canvasWizardOwnsAttach) {
          try {
            void props.onBacktestSaved?.(backtestId);
          } catch {
            // ignore
          }
        }
        setLinkedBacktestsRefreshKey((k) => k + 1);
      }
    },
    [linkedStrategyIdForNewRuns, props]
  );

  const applySavedTitlesToRun = useCallback(
    (
      runId: string,
      titles: { savedTitle: string; label: string; titleParseMode?: ComparisonRun["titleParseMode"] },
      backtestId?: string
    ) => {
    setComparisonRuns((prev) =>
      prev.map((r) => {
        if (r.id !== runId) return r;
        return normalizeComparisonRunTitleFields({
          ...r,
          label: titles.label,
          savedTitle: titles.savedTitle,
          ...(titles.titleParseMode ? { titleParseMode: titles.titleParseMode } : {}),
          ...(backtestId ? { backtestId, isSavedBacktest: true } : {}),
        });
      })
    );
  },
  []);

  const syncRunTitleFieldsFromBacktest = useCallback(
    async (runId: string, backtestId: string) => {
      try {
        const b = await getBacktestSlim(backtestId);
        const fromSummary = b.title?.trim() ? comparisonRunTitlesFromBacktestSummary({ id: backtestId, title: b.title }) : null;
        if (fromSummary) {
          applySavedTitlesToRun(runId, fromSummary, backtestId);
          return;
        }
        const fromApi = backtestToComparisonRun(b);
        if (!fromApi) return;
        const savedTitle = fromApi.savedTitle || fromApi.label;
        applySavedTitlesToRun(
          runId,
          {
            savedTitle,
            label: fromApi.label,
            ...(fromApi.titleParseMode ? { titleParseMode: fromApi.titleParseMode } : {}),
          },
          fromApi.backtestId ?? backtestId
        );
      } catch {
        // Non-fatal: title field still uses parsed in-memory titles.
      }
    },
    [applySavedTitlesToRun]
  );

  const autoSaveJobIdsRef = useRef<Set<string>>(new Set());

  /**
   * Auto-persist completed simulate jobs (report tab / standalone results /
   * report drawer on canvas).
   *
   * Canvas Edit & run wizard: never attach here — host saves via
   * `onWorkspaceSimulationQueued` only (session revival was re-attaching stale jobs).
   * Report drawer with workspaceSimulationContext: include attach so the sim
   * lands under the report node without a refresh.
   */
  const persistSimulationAfterRun = useCallback(
    async (runId: string, jobId: string): Promise<boolean> => {
      const jid = jobId.trim();
      if (!jid) return false;
      if (autoSaveJobIdsRef.current.has(jid)) return true;
      autoSaveJobIdsRef.current.add(jid);

      const canvasWizardOwnsAttach =
        Boolean(props.simulatorWizardLayout) && Boolean(props.workspaceSimulationContext);
      const workspaceAttach =
        !canvasWizardOwnsAttach && props.workspaceSimulationContext
          ? {
              workspaceId: props.workspaceSimulationContext.workspaceId,
              parentNodeId: props.workspaceSimulationContext.parentNodeId,
              ...(props.workspaceSimulationContext.position
                ? { position: props.workspaceSimulationContext.position }
                : {}),
              ...(props.workspaceSimulationContext.meta
                ? { meta: props.workspaceSimulationContext.meta }
                : {}),
            }
          : {};

      const saved = await saveSimulateTradePolicyJob(jid, workspaceAttach);
      if (!saved.ok) {
        autoSaveJobIdsRef.current.delete(jid);
        toastSimulateWorkspaceSaveFailure(saved);
        return false;
      }
      stampSavedBacktestOnRun(runId, saved.backtestId);
      const summaryTitles = saved.backtestSummary
        ? comparisonRunTitlesFromBacktestSummary(saved.backtestSummary)
        : null;
      if (summaryTitles) {
        applySavedTitlesToRun(runId, summaryTitles, saved.backtestId ?? saved.backtestSummary?.id);
      } else if (saved.backtestId) {
        await syncRunTitleFieldsFromBacktest(runId, saved.backtestId);
      } else {
        setComparisonRuns((prev) =>
          prev.map((r) => (r.id === runId ? normalizeComparisonRunTitleFields(r) : r))
        );
      }
      return true;
    },
    [
      applySavedTitlesToRun,
      props.simulatorWizardLayout,
      props.workspaceSimulationContext,
      stampSavedBacktestOnRun,
      syncRunTitleFieldsFromBacktest,
    ]
  );

  /** @deprecated Prefer {@link persistSimulationAfterRun}; kept as an alias for call-site clarity. */
  const persistReportSimulationAfterRun = persistSimulationAfterRun;

  const saveBacktestForRun = useCallback(
    async (runId: string, jobIdOverride?: string) => {
      const jobId = jobIdOverride ?? comparisonRuns.find((r) => r.id === runId)?.jobId;
      if (!jobId) return;
      const saved = await saveSimulateTradePolicyJob(jobId, {
        ...(props.workspaceSimulationContext
          ? {
              workspaceId: props.workspaceSimulationContext.workspaceId,
              parentNodeId: props.workspaceSimulationContext.parentNodeId,
              ...(props.workspaceSimulationContext.position
                ? { position: props.workspaceSimulationContext.position }
                : {}),
              ...(props.workspaceSimulationContext.meta
                ? { meta: props.workspaceSimulationContext.meta }
                : {}),
            }
          : {}),
      });
      if (!saved.ok) {
        toastSimulateWorkspaceSaveFailure(saved);
        return;
      }
      stampSavedBacktestOnRun(runId, saved.backtestId);
      const summaryTitles = saved.backtestSummary
        ? comparisonRunTitlesFromBacktestSummary(saved.backtestSummary)
        : null;
      if (summaryTitles) {
        applySavedTitlesToRun(runId, summaryTitles, saved.backtestId ?? saved.backtestSummary?.id);
      } else if (saved.backtestId) {
        await syncRunTitleFieldsFromBacktest(runId, saved.backtestId);
      }
      if (saved.backtestId) {
        toast.success("Simulation saved");
      } else {
        toast.success("Simulation saved", { description: "Saved, but backend did not return a simulation id." });
      }
    },
    [
      applySavedTitlesToRun,
      comparisonRuns,
      props.workspaceSimulationContext,
      stampSavedBacktestOnRun,
      syncRunTitleFieldsFromBacktest,
    ]
  );

  const linkedStrategyMenuLabel = useMemo(() => {
    if (!standaloneLinkedStrategyId.trim()) return "No strategy";
    return strategyOptions.find((s) => s.id === standaloneLinkedStrategyId)?.name ?? "Selected strategy";
  }, [standaloneLinkedStrategyId, strategyOptions]);

  const saveStrategyVersionForRun = useCallback(
    async (runId: string) => {
      const run = comparisonRuns.find((r) => r.id === runId);
      const strategyId =
        run?.linkedStrategyId?.trim() ||
        reportStrategyId?.trim() ||
        linkedStrategyIdForNewRuns?.trim() ||
        null;
      if (!strategyId || !run) return;
      try {
        const backtestId = run.backtestId?.trim() || "";
        const policyAttachRaw =
          props.workspacePolicyContext ??
          (backtestId ? props.resolveWorkspacePolicyAttach?.(backtestId) : null) ??
          null;
        const policyAttach = policyAttachRaw
          ? sanitizeWorkspaceSimulateAttachFields({
              workspaceId: policyAttachRaw.workspaceId,
              parentNodeId: policyAttachRaw.parentNodeId,
              position: policyAttachRaw.position,
              meta: policyAttachRaw.meta,
            })
          : {};
        // Prefer saving from backtest id when we have it; otherwise send the policy tree directly.
        if (backtestId) {
          await createReportStrategyPolicyVersion(strategyId, {
            source_backtest_id: backtestId,
            set_as_current: true,
            policy_row_version: effectivePolicyRowVersion,
            ...policyAttach,
          });
        } else {
          await createReportStrategyPolicyVersion(strategyId, {
            policy_tree: run.policy,
            set_as_current: true,
            policy_row_version: effectivePolicyRowVersion,
            ...policyAttach,
          });
        }
        const strategySavedCopy = simulationRunSavedAsStrategyVersionToastCopy();
        toast.success(strategySavedCopy.title, { description: strategySavedCopy.description });
        setComparisonRuns((prev) =>
          prev.map((r) => (r.id === runId ? { ...r, isSavedStrategyVersion: true } : r))
        );
        try {
          await props.onStrategyVersionSaved?.(backtestId || undefined);
        } catch (refreshErr) {
          presentMutationWarning(
            refreshErr,
            "Policy saved, but the workspace canvas could not refresh. Reload the page if the new policy node is missing."
          );
        }
      } catch (err) {
        presentMutationError(err, "Failed to save policy version");
      }
    },
    [
      comparisonRuns,
      effectivePolicyRowVersion,
      linkedStrategyIdForNewRuns,
      props.onStrategyVersionSaved,
      props.resolveWorkspacePolicyAttach,
      props.workspacePolicyContext,
      reportStrategyId,
    ]
  );

  useEffect(() => {
    // Keep only still-valid user selections when list changes.
    // Default is intentionally "none selected".
    setSelectedPickIds((prev) => {
      const importableIds = new Set(
        (isManagementV2Mode ? importableExitPlacementPicks : importablePicks).map((p) => p.id)
      );
      if (prev.size === 0) return prev;
      const next = new Set<string>();
      for (const id of Array.from(prev)) if (importableIds.has(id)) next.add(id);
      return next;
    });
  }, [importableExitPlacementPicks, importablePicks, isManagementV2Mode]);

  const selectedImportablePicks = useMemo(() => {
    if (selectedPickIds.size === 0) return [];
    const idSet = selectedPickIds;
    return importablePicks.filter((p) => idSet.has(p.id));
  }, [importablePicks, selectedPickIds]);

  const selectedExitPlacementPicks = useMemo(() => {
    if (selectedPickIds.size === 0) return [];
    const idSet = selectedPickIds;
    return importableExitPlacementPicks.filter((p) => idSet.has(p.id));
  }, [importableExitPlacementPicks, selectedPickIds]);

  /** Lane-aware run actions: Single → separate only; Combined → combined only. */
  const selectedImportableLaneModes = useMemo(() => {
    let hasSingleOrRan = false;
    let hasCombined = false;
    for (const pick of selectedImportablePicks) {
      const lane = getSimulatorLane(pick);
      if (lane === "combined") hasCombined = true;
      else hasSingleOrRan = true;
    }
    return { hasSingleOrRan, hasCombined };
  }, [selectedImportablePicks]);

  const selectedExitPlacementLaneModes = useMemo(() => {
    let hasSingleOrRan = false;
    let hasCombined = false;
    for (const pick of selectedExitPlacementPicks) {
      const lane = getSimulatorLane(pick);
      if (lane === "combined") hasCombined = true;
      else hasSingleOrRan = true;
    }
    return { hasSingleOrRan, hasCombined };
  }, [selectedExitPlacementPicks]);

  const picksLegAvailability = useMemo(
    () => legAvailabilityFromExitPlacementPicks(selectedExitPlacementPicks),
    [selectedExitPlacementPicks]
  );

  const cartExitPlacementLegAvailability = useMemo(
    () => legAvailabilityFromExitPlacementPicks(importableExitPlacementPicks),
    [importableExitPlacementPicks]
  );

  const exitPlacementSimLegAvailabilityForUi = useMemo(
    () =>
      selectedExitPlacementPicks.length > 0 ? picksLegAvailability : cartExitPlacementLegAvailability,
    [cartExitPlacementLegAvailability, picksLegAvailability, selectedExitPlacementPicks.length]
  );

  useEffect(() => {
    if (selectedImportablePicks.length <= 1) {
      setCombineExplicitlySelected(false);
    }
  }, [selectedImportablePicks.length]);

  const openOverlapFromPicks = useCallback(() => {
    if (!scopedReportId) return;

    const pickByIdAll = new Map<string, ReportPick>();
    for (const p of picks) pickByIdAll.set(p.id, p);
    const bundleByIdAll = new Map<string, SimulatorPickBundleCard>();
    for (const b of combinedBundles) bundleByIdAll.set(b.id, b);
    const resolverDeps = {
      pickById: pickByIdAll,
      bundleById: bundleByIdAll,
      importPickPolicyNodes: (pick: ReportPick) => pickImportPolicyNodes(pick, pickCatalogCtx),
    };

    const seeds: OverlapSelectionItem[] = [];
    const seenKeys = new Set<string>();
    const pushSeed = (item: OverlapSelectionItem) => {
      if (seenKeys.has(item.key)) return;
      seenKeys.add(item.key);
      seeds.push(item);
    };

    for (const pick of selectedImportablePicks) {
      const linked = overlapPickToBacktestId.get(pick.id);
      pushSeed(
        selectionItemFromReportPick(pick, linked ? { linkedBacktestId: linked } : undefined)
      );
    }

    for (const bundle of combinedBundles) {
      if (!selectedBundleIds.has(bundle.id)) continue;

      const run = comparisonRuns.find((r) => r.id === bundle.comparisonRunId);
      const backtestId = run?.backtestId?.trim() || undefined;

      const collectChildLabels = (cardId: string, visited: Set<string>): string[] => {
        if (visited.has(cardId)) return [];
        visited.add(cardId);
        const pick = pickByIdAll.get(cardId);
        if (pick) {
          return [
            pick.kind === "exit_placement" ? exitPlacementPickDisplayLabel(pick) : pick.label.trim(),
          ].filter(Boolean);
        }
        const childBundle = bundleByIdAll.get(cardId);
        if (!childBundle) return [];
        const out: string[] = [];
        for (const childId of childBundle.children) {
          out.push(...collectChildLabels(childId, visited));
        }
        return out;
      };

      const childLabels = collectChildLabels(bundle.id, new Set()).join(" + ");
      const candidates = [
        bundle.policySummary?.trim(),
        childLabels,
        bundle.title?.trim(),
        run?.savedTitle?.trim(),
        run?.label?.trim(),
      ].filter((v): v is string => Boolean(v && v.trim()));
      const label =
        candidates.find((c) => !/^Overlap\s+(suggestion\s+)?#\d+(\s*·\s*)?$/i.test(c)) ||
        candidates[0] ||
        `Combined ${bundle.id.slice(0, 8)}`;

      if (backtestId) {
        const bt = props.linkedBacktests?.find((b) => b.id === backtestId);
        if (bt) {
          pushSeed(
            selectionItemFromCombinedBundle({
              bundleId: bundle.id,
              label,
              backtestId,
            })
          );
          continue;
        }
      }

      const node = resolveAtomicCardToPolicyNode(bundle.id, resolverDeps, new Set());
      if (!node) {
        toast.warning("Combined pick not ready", {
          description: `${label} could not be converted to a policy for overlap.`,
        });
        continue;
      }
      const action = bundle.action === "keep_only" ? "keep_only" : "veto";
      pushSeed(
        selectionItemFromCombinedBundle({
          bundleId: bundle.id,
          label,
          policyTree: {
            schema_version: "policy_tree.v1",
            action,
            root: node,
          },
        })
      );
    }

    if (seeds.length < 2) {
      toast.info("Need at least two policies", {
        description: "Select two or more single picks and/or combined runs to check overlap.",
      });
      return;
    }

    setOverlapEntry("picks");
    setOverlapInitialSelection(seeds);
    onOpenOverlapDialog();
  }, [
    combinedBundles,
    comparisonRuns,
    onOpenOverlapDialog,
    overlapPickToBacktestId,
    pickCatalogCtx,
    picks,
    props.linkedBacktests,
    scopedReportId,
    selectedBundleIds,
    selectedImportablePicks,
  ]);

  const openOverlapFromExitPlacementPicks = useCallback(() => {
    if (!scopedReportId) return;
    const seeds: OverlapSelectionItem[] = [];
    const seenKeys = new Set<string>();
    const pushSeed = (item: OverlapSelectionItem) => {
      if (seenKeys.has(item.key)) return;
      seenKeys.add(item.key);
      seeds.push(item);
    };

    for (const pick of selectedExitPlacementPicks) {
      const linked = overlapPickToBacktestId.get(pick.id);
      pushSeed(
        selectionItemFromReportPick(pick, linked ? { linkedBacktestId: linked } : undefined)
      );
    }

    for (const bundle of combinedBundles) {
      if (!selectedBundleIds.has(bundle.id)) continue;
      const run = comparisonRuns.find((r) => r.id === bundle.comparisonRunId);
      const backtestId = run?.backtestId?.trim() || undefined;
      const label =
        bundle.policySummary?.trim() ||
        bundle.title?.trim() ||
        run?.savedTitle?.trim() ||
        run?.label?.trim() ||
        `Combined ${bundle.id.slice(0, 8)}`;
      if (backtestId) {
        const bt = props.linkedBacktests?.find((b) => b.id === backtestId);
        if (bt) {
          pushSeed(selectionItemFromBacktest(bt));
          continue;
        }
        pushSeed(selectionItemFromCombinedBundle({ bundleId: bundle.id, label, backtestId }));
        continue;
      }
      // Exit-placement overlap needs slice targets; without a saved sim, skip with notice.
      toast.warning("Combined pick needs a saved simulation", {
        description: `${label} — save the combined run before including it in exit-placement overlap.`,
      });
    }

    if (seeds.length < 2) {
      toast.info("Need at least two policies", {
        description: "Select two or more exit-placement picks (and saved combined sims) to check overlap.",
      });
      return;
    }

    setOverlapEntry("picks");
    setOverlapInitialSelection(seeds);
    onOpenOverlapDialog();
  }, [
    combinedBundles,
    comparisonRuns,
    onOpenOverlapDialog,
    overlapPickToBacktestId,
    props.linkedBacktests,
    scopedReportId,
    selectedBundleIds,
    selectedExitPlacementPicks,
  ]);

  const openOverlapFromSavedSims = useCallback(
    (seedBacktestIds?: string[]) => {
      if (!scopedReportId) return;
      const ids = seedBacktestIds?.length
        ? seedBacktestIds
        : comparisonRuns
            .map((r) => r.backtestId)
            .filter((id): id is string => Boolean(id?.trim()));
      const uniqueIds = Array.from(new Set(ids)).slice(0, 8);
      const seeds: OverlapSelectionItem[] = [];
      for (const id of uniqueIds) {
        const bt = props.linkedBacktests?.find((b) => b.id === id);
        if (bt) seeds.push(selectionItemFromBacktest(bt));
      }
      setOverlapEntry("saved_sims");
      setOverlapInitialSelection(seeds);
      onOpenOverlapDialog();
    },
    [comparisonRuns, onOpenOverlapDialog, props.linkedBacktests, scopedReportId]
  );

  useEffect(() => {
    if (!scopedReportId) return;
    const sync = () => {
      setPicks(readPickListForReport(scopedReportId));
    };
    sync();
    window.addEventListener(REPORT_PICK_LIST_CHANGED_EVENT, sync as any);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(REPORT_PICK_LIST_CHANGED_EVENT, sync as any);
      window.removeEventListener("storage", sync);
    };
  }, [scopedReportId]);

  useEffect(() => {
    const sync = () => {
      setCombinedBundles(readBundlesForBucket(bundlesBucketId));
    };
    sync();
    const off = onSimulatorPickBundlesChanged(sync);
    return off;
  }, [bundlesBucketId]);

  // When comparison runs finish asynchronously, update stored bundle card metrics/status
  // by matching `bundle.comparisonRunId` → `comparisonRun.id`.
  useEffect(() => {
    if (combinedBundles.length === 0) return;
    if (comparisonRuns.length === 0) return;

    const resolveMetrics = (res: any): PickQuickStats | null => {
      const metric = res?.filtered ?? res?.changed ?? res?.removed ?? res?.baseline;
      if (!metric) return null;
      const tradeCount = metric.trade_count;
      const totalPnl = metric.total_pnl;
      const winRate = metric.win_rate;
      const expectancy = metric.expectancy;
      const hasAny =
        (tradeCount != null && Number.isFinite(tradeCount)) ||
        (totalPnl != null && Number.isFinite(totalPnl)) ||
        (winRate != null && Number.isFinite(winRate)) ||
        (expectancy != null && Number.isFinite(expectancy));
      if (!hasAny) return null;
      return { tradeCount, totalPnl, winRate, expectancy };
    };

    const byRunId = new Map<string, ComparisonRun>();
    for (const run of comparisonRuns) byRunId.set(run.id, run);

    for (const bundle of combinedBundles) {
      if (!bundle.comparisonRunId) continue;
      if (bundle.status !== "running") continue;
      if (bundle.metrics != null) continue;

      const run = byRunId.get(bundle.comparisonRunId);
      if (!run) continue;
      if (run.status === "running") continue;

      const nextStatus = run.status === "succeeded" ? "succeeded" : "failed";
      if (nextStatus !== "succeeded" && run.status !== "failed") continue;
      if (nextStatus === "failed" && !run.result) {
        updateBundleCard(bundle.reportBucketId, bundle.id, {
          status: "failed",
          metrics: null,
        });
        continue;
      }
      if (!run.result) continue;

      updateBundleCard(bundle.reportBucketId, bundle.id, {
        status: nextStatus,
        metrics: resolveMetrics(run.result),
      });
    }
  }, [combinedBundles, comparisonRuns, updateBundleCard]);

  useEffect(() => {
    if (!isManagementV2Mode) return;
    setPolicySchemaVersion("policy_tree.v2");
  }, [isManagementV2Mode]);

  useEffect(() => {
    return () => {
      if (linkedPickResetTimerRef.current != null) {
        window.clearTimeout(linkedPickResetTimerRef.current);
      }
    };
  }, []);

  // All catalog fields — display labels come from policyCatalogFieldDisplayLabel in the picker.
  const catalogFieldsForPicker = catalogFields;
  const defaultGroupAction = useMemo((): TradePolicyAction => {
    if (builderSimAction) return builderSimAction;
    // Prefer a Keep/Remove already stamped on the tree over hard-defaulting to Remove.
    return inferUniformEditorGroupAction(policyRoot) ?? "veto";
  }, [builderSimAction, policyRoot]);

  const marketContextDefinitionNode = useMemo(() => {
    if (props.inputSnapshot == null) return null;
    const slices = buildConditionAnalysisSlices(props.inputSnapshot);
    return slices.find((s) => s.kind === "all")?.node ?? null;
  }, [props.inputSnapshot]);

  const policyFieldMergedDefs = useMemo(() => {
    const fromStatic = mergedMetricDefinitionMap(TRADE_POLICY_BUCKET_METRIC_DEFINITIONS, marketContextDefinitionNode);
    const fromReport = mergedMetricDefinitionMap(
      marketContextDefinitionNode?.market_condition_brackets?.bucket_metric_definitions,
      marketContextDefinitionNode
    );
    return { ...fromStatic, ...fromReport };
  }, [marketContextDefinitionNode]);

  const fieldExplanationFor = useCallback(
    (field: TradePolicyCatalogField) => buildPolicyRuleFieldExplanation(field, policyFieldMergedDefs),
    [policyFieldMergedDefs]
  );

  const ruleLeaves = useMemo(() => countRuleLeaves(policyRoot), [policyRoot]);

  /** Editor tree includes a non-editable root `and` shell; review copy counts only what sits under it. */
  const policyNodesBelowRootShell = useMemo(() => {
    if (policyRoot.kind !== "group") return countEditorNodes(policyRoot);
    return Math.max(0, countEditorNodes(policyRoot) - 1);
  }, [policyRoot]);

  const navigateToStandaloneResults = useCallback(
    (runs: ComparisonRun[], selectedRunId: string) => {
      writeStandaloneSimulatorRuns({
        runs,
        brokerAccountCode: effectiveBrokerAccountCode,
        selectedRunId,
      });
      const priorState =
        history.location.state && typeof history.location.state === "object"
          ? (history.location.state as Record<string, unknown>)
          : {};
      history.push({
        pathname: SIMULATOR_RESULTS_PATH,
        search: `?run=${encodeURIComponent(selectedRunId)}`,
        state: {
          ...priorState,
          ...(props.resultsLocationState ?? {}),
        },
      });
    },
    [effectiveBrokerAccountCode, history, props.resultsLocationState]
  );

  useEffect(() => {
    if (!props.resultsOnlyLayout || !isStandalone || props.initialBacktestId) return;
    if (didHydrateStandaloneRunsRef.current) return;
    didHydrateStandaloneRunsRef.current = true;
    const snap = readStandaloneSimulatorRuns();
    if (snap?.runs?.length) setComparisonRuns(snap.runs);
    if (snap?.brokerAccountCode?.trim()) setStandaloneBrokerCode(snap.brokerAccountCode.trim());
  }, [props.initialBacktestId, props.resultsOnlyLayout, isStandalone]);

  useEffect(() => {
    const backtestId = props.initialBacktestId?.trim();
    if (!backtestId || !props.resultsOnlyLayout || !isStandalone) return;
    if (didHydrateInitialBacktestRef.current === backtestId) return;
    let cancelled = false;
    setInitialBacktestLoading(true);
    setInitialBacktestError(null);
    void (async () => {
      try {
        const b = await getBacktestSlim(backtestId);
        let run = backtestToComparisonRun(b);
        if (!run) {
          throw new Error("This simulation has no policy or result data to display.");
        }
        const strategyId =
          run.linkedStrategyId ||
          (await resolveStrategyIdForBacktest(b)) ||
          props.prefillStrategyId?.trim() ||
          "";
        if (strategyId) {
          run = { ...run, linkedStrategyId: run.linkedStrategyId || strategyId };
          const [stampedRun] = await stampRunsWithSavedPolicyVersionFlags([run], strategyId);
          run = stampedRun;
        }
        if (cancelled) return;
        didHydrateInitialBacktestRef.current = backtestId;
        setComparisonRuns([run]);
        if (strategyId) setStandaloneLinkedStrategyId(strategyId);
        const broker = typeof b.broker_account_code === "string" ? b.broker_account_code.trim() : "";
        if (broker) setStandaloneBrokerCode(broker);
      } catch (e) {
        if (!cancelled) {
          setInitialBacktestError(e instanceof Error ? e.message : "Failed to load simulation.");
          setComparisonRuns([]);
        }
      } finally {
        if (!cancelled) setInitialBacktestLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.initialBacktestId, props.prefillStrategyId, props.resultsOnlyLayout, isStandalone]);

  useEffect(() => {
    if (!isStandalone || props.initialBacktestId) return;
    if (comparisonRuns.length === 0 && !props.resultsOnlyLayout) return;
    writeStandaloneSimulatorRuns({
      runs: comparisonRuns,
      brokerAccountCode: effectiveBrokerAccountCode,
      selectedRunId: props.initialSelectedRunId ?? undefined,
    });
  }, [
    comparisonRuns,
    effectiveBrokerAccountCode,
    isStandalone,
    props.initialBacktestId,
    props.initialSelectedRunId,
    props.resultsOnlyLayout,
  ]);

  useEffect(() => {
    if (!isStandalone) return;
    for (const run of comparisonRuns) {
      if (run.status !== "running" || !run.jobId || run.result != null || run.error) continue;
      if (activePollRunIdsRef.current.has(run.id)) continue;
      activePollRunIdsRef.current.add(run.id);
      const jobId = run.jobId;
      const runId = run.id;
      void (async () => {
        try {
          const outcome = await waitForSimulateTradePolicyJob(jobId);
          setComparisonRuns((prev) =>
            prev.map((r) =>
              r.id === runId
                ? normalizeComparisonRunTitleFields({
                    ...r,
                    result: outcome.result,
                    status: "succeeded",
                    error: null,
                    stillRunning: false,
                  })
                : r
            )
          );
          void persistSimulationAfterRun(runId, jobId);
        } catch (e) {
          if (e instanceof SimulateTradePolicyJobStillRunningError) {
            setComparisonRuns((prev) =>
              prev.map((r) => (r.id === runId ? { ...r, stillRunning: true, status: "running", error: null } : r))
            );
            return;
          }
          setComparisonRuns((prev) =>
            prev.map((r) =>
              r.id === runId
                ? {
                    ...r,
                    result: null,
                    status: "failed",
                    error: e instanceof Error ? e.message : "Simulation failed.",
                    stillRunning: false,
                  }
                : r
            )
          );
        } finally {
          activePollRunIdsRef.current.delete(runId);
        }
      })();
    }
  }, [comparisonRuns, isStandalone, persistSimulationAfterRun]);

  /** Persist finished runs that only have a job id. Skip on canvas wizard — host saves. */
  useEffect(() => {
    if (props.simulatorWizardLayout && props.workspaceSimulationContext) return;
    for (const run of comparisonRuns) {
      if (run.status !== "succeeded") continue;
      const jobId = run.jobId?.trim();
      if (!jobId || run.isSavedBacktest || run.backtestId?.trim()) continue;
      void persistSimulationAfterRun(run.id, jobId);
    }
  }, [
    comparisonRuns,
    persistSimulationAfterRun,
    props.simulatorWizardLayout,
    props.workspaceSimulationContext,
  ]);

  useEffect(() => {
    if (!isStandalone || catalogLoading || catalogFields.length === 0) return;
    if (didHydrateStandaloneDraftRef.current) return;
    didHydrateStandaloneDraftRef.current = true;
    const draft = readStandaloneSimulatorDraft();
    if (draft?.brokerAccountCode) setStandaloneBrokerCode(draft.brokerAccountCode);
    else if (activeAccountCode) setStandaloneBrokerCode(activeAccountCode);
    setUseCustomRange(true);
    if (draft?.dateRange) {
      const start = draft.dateRange.startDate ? parsePrefillDateToLocalDate(draft.dateRange.startDate) : null;
      const end = draft.dateRange.endDate ? parsePrefillDateToLocalDate(draft.dateRange.endDate) : null;
      setDateRange({ startDate: start, endDate: end });
    }
    if (typeof draft?.symbol === "string" && props.prefillSymbol == null) {
      setSymbol(draft.symbol);
    }
    if (
      draft?.candleTimeframe === "1m" ||
      draft?.candleTimeframe === "5m" ||
      draft?.candleTimeframe === "15m" ||
      draft?.candleTimeframe === "30m" ||
      draft?.candleTimeframe === "1h" ||
      draft?.candleTimeframe === "4h" ||
      draft?.candleTimeframe === "1d"
    ) {
      if (!props.candleTimeframeLocked && !props.defaultCandleTimeframe) {
        setCandleTimeframe(draft.candleTimeframe);
      }
    }
    if (draft?.policyRowVersion === "policy_row.v1" || draft?.policyRowVersion === "policy_row.pine_v1") {
      setPolicyRowVersion(draft.policyRowVersion);
    }
    const hasExternalPolicyPrefill =
      props.prefillPolicyTree != null || props.prefillPolicy != null;
    if (
      !startWithEmptyPolicy &&
      !hasExternalPolicyPrefill &&
      draft?.policyRoot &&
      (draft.policyRoot.kind === "group" || draft.policyRoot.kind === "rule")
    ) {
      setPolicyRoot(draft.policyRoot);
    }
    if (
      !startWithEmptyPolicy &&
      !hasExternalPolicyPrefill &&
      (draft?.builderSimAction === "veto" || draft?.builderSimAction === "keep_only")
    ) {
      setBuilderSimAction(draft.builderSimAction);
    }
    const prefillSid = props.prefillStrategyId?.trim() || "";
    if (prefillSid) {
      setStandaloneLinkedStrategyId(prefillSid);
    } else if (typeof draft?.linkedStrategyId === "string" && props.layout !== "embedded") {
      setStandaloneLinkedStrategyId(draft.linkedStrategyId);
    } else if (props.layout === "embedded") {
      // Dialog create flow starts with no strategy unless canvas prefilled one.
      setStandaloneLinkedStrategyId("");
    }
  }, [
    activeAccountCode,
    catalogFields.length,
    catalogLoading,
    isStandalone,
    parsePrefillDateToLocalDate,
    props.candleTimeframeLocked,
    props.defaultCandleTimeframe,
    props.layout,
    props.prefillPolicy,
    props.prefillPolicyTree,
    props.prefillStrategyId,
    props.prefillSymbol,
    startWithEmptyPolicy,
  ]);

  useEffect(() => {
    const sid = props.prefillStrategyId?.trim() || "";
    if (!isStandalone || !sid) return;
    setStandaloneLinkedStrategyId(sid);
    setReportStrategyId(sid);
  }, [isStandalone, props.prefillStrategyId]);

  useEffect(() => {
    if (props.prefillSymbol == null) return;
    setSymbol(props.prefillSymbol.trim());
  }, [props.prefillSymbol]);

  useEffect(() => {
    if (!isStandalone || !props.simulatorWizardLayout) return;
    if (!effectiveBrokerAccountCode.trim()) {
      setAvailableInstruments([]);
      return;
    }
    let cancelled = false;
    setInstrumentsLoading(true);
    void (async () => {
      try {
        const windowFields = buildAnalyticsWindowSnakeFields(
          dateRange,
          timeframe,
          reportingTimeZone
        );
        const data = await fetchAvailableInstruments({
          broker_account_code: effectiveBrokerAccountCode,
          ...(windowFields.local_start_date
            ? {
                local_start_date: windowFields.local_start_date,
                local_end_date: windowFields.local_end_date,
                timezone: windowFields.timezone || reportingTimeZone,
              }
            : windowFields.timeframe
              ? { timeframe: windowFields.timeframe }
              : {}),
        });
        if (!cancelled) {
          setAvailableInstruments(Array.isArray(data.instruments) ? data.instruments : []);
        }
      } catch {
        if (!cancelled) setAvailableInstruments([]);
      } finally {
        if (!cancelled) setInstrumentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    dateRange,
    effectiveBrokerAccountCode,
    isStandalone,
    props.simulatorWizardLayout,
    reportingTimeZone,
    timeframe,
  ]);
  useEffect(() => {
    if (!isStandalone) return;
    let cancelled = false;
    setStrategiesLoading(true);
    void (async () => {
      try {
        const accountCode = (props.lockedBrokerAccountCode ?? activeAccountCode ?? "").trim();
        const data = await listReportStrategies({
          include_archived: false,
          limit: 200,
          offset: 0,
          ...(accountCode ? { broker_account_code: accountCode } : {}),
        });
        const options = Array.isArray(data.strategies) ? data.strategies : [];
        if (!cancelled) setStrategyOptions(options);
      } catch {
        if (!cancelled) setStrategyOptions([]);
      } finally {
        if (!cancelled) setStrategiesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeAccountCode, isStandalone, props.lockedBrokerAccountCode]);

  useEffect(() => {
    if (!isStandalone || catalogLoading || !didHydrateStandaloneDraftRef.current) return;
    const handle = window.setTimeout(() => {
      const existing =
        props.layout === "embedded" ? readStandaloneSimulatorDraft() : null;
      writeStandaloneSimulatorDraft({
        brokerAccountCode: standaloneBrokerCode || undefined,
        dateRange: {
          startDate: toLocalDateString(dateRange.startDate),
          endDate: toLocalDateString(dateRange.endDate),
        },
        symbol,
        candleTimeframe,
        policyRowVersion,
        policyRoot: startWithEmptyPolicy ? existing?.policyRoot : policyRoot,
        builderSimAction: startWithEmptyPolicy ? existing?.builderSimAction : builderSimAction,
        // Keep page-wizard strategy preference intact when editing from a dialog.
        linkedStrategyId:
          props.layout === "embedded"
            ? existing?.linkedStrategyId
            : standaloneLinkedStrategyId || undefined,
      });
    }, 400);
    return () => window.clearTimeout(handle);
  }, [
    builderSimAction,
    candleTimeframe,
    dateRange,
    isStandalone,
    catalogLoading,
    policyRoot,
    policyRowVersion,
    props.layout,
    standaloneBrokerCode,
    standaloneLinkedStrategyId,
    symbol,
    startWithEmptyPolicy,
  ]);

  useEffect(() => {
    if (!scopedReportId || !simConfigHydrated || catalogLoading) return;
    if (suppressSimConfigSaveRef.current) return;
    if (startWithEmptyPolicy) return;
    // Canvas policy Run must not PUT report sim-config (revision was never loaded).
    if (props.prefillPolicyTree != null || props.prefillPolicy != null) return;
    try {
      sessionStorage.setItem(editorUiStateKey(scopedReportId), JSON.stringify(policyRoot));
    } catch {
      // ignore storage write failures
    }
    let cancelled = false;
    void (async () => {
      try {
        const builtForSave = buildSimulatePolicy(policyRoot, defaultGroupAction, fieldsByPath, maxDepth, maxNodes);
        if (!builtForSave.ok) return;
        const builtPolicy = builtForSave.policy;
        if (!isPolicyTreeBundleV1(builtPolicy) && !isPolicyPayloadV1(builtPolicy)) return;
        const policyTreeForSave = isPolicyTreeBundleV1(builtPolicy)
          ? (builtPolicy as unknown as Record<string, unknown>)
          : builtPolicy.root;
        const next = await upsertReportPolicySimConfig(scopedReportId, {
          policy_tree: policyTreeForSave,
          scope_inputs: {
            ...(isPolicyPayloadV1(builtPolicy) ? { action: builtPolicy.action } : {}),
            ...(builderSimAction !== null ? { default_action: builderSimAction } : {}),
            timeframe,
            local_start_date: toLocalDateString(dateRange.startDate),
            local_end_date: toLocalDateString(dateRange.endDate),
            timezone: reportingTimeZone,
            symbol: symbol.trim(),
            include_trade_ids: true,
            limit: DEFAULT_POLICY_LIMIT,
            candle_timeframe: candleTimeframe,
          } as any,
          view_state: {
            mode: policyViewMode,
            collapsed_group_ids: Array.from(collapsedGroupIds).filter((id) => allGroupIds.includes(id)),
          },
          policy_schema_version: isPolicyTreeBundleV1(builtPolicy)
            ? "policy_tree.bundle.v1"
            : policySchemaVersion || "policy_tree.v1",
          group_count: countEditorNodes(policyRoot),
          rule_count: countRuleLeaves(policyRoot),
          revision: simConfigRevisionRef.current,
        });
        if (cancelled) return;
        setSimConfigRevision(
          typeof next.revision === "number" ? next.revision : simConfigRevisionRef.current + 1
        );
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ReportPolicySimConfigConflictError) {
          let nextRev =
            e.current && typeof e.current.revision === "number" ? e.current.revision : null;
          if (nextRev == null) {
            try {
              const latest = await fetchReportPolicySimConfig(scopedReportId);
              if (latest && typeof latest.revision === "number") nextRev = latest.revision;
            } catch {
              // ignore refetch failures
            }
          }
          if (nextRev != null) setSimConfigRevision(nextRev);
          toastWarning("Simulator config changed elsewhere", {
            description: "Please refresh to load the latest saved version.",
          });
          return;
        }
        const now = Date.now();
        if (now - lastSimConfigSaveErrorToastAtRef.current > 10_000) {
          lastSimConfigSaveErrorToastAtRef.current = now;
          presentMutationWarning(e, "Simulator auto-save failed");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    builderSimAction,
    defaultGroupAction,
    candleTimeframe,
    catalogLoading,
    collapsedGroupIds,
    dateRange.endDate,
    dateRange.startDate,
    fieldsByPath,
    maxDepth,
    maxNodes,
    policyRoot,
    policySchemaVersion,
    policyViewMode,
    props.prefillPolicy,
    props.prefillPolicyTree,
    scopedReportId,
    simConfigHydrated,
    symbol,
    timeframe,
    reportingTimeZone,
    useCustomRange,
    startWithEmptyPolicy,
  ]);

  const shouldSyncDashboardWindow = !scopedReportId && !props.hideScopeControls;

  useEffect(() => {
    if (props.policyMode === "v2") {
      setPolicySchemaVersion("policy_tree.v2");
    } else if (props.policyMode === "v1") {
      setPolicySchemaVersion("policy_tree.v1");
    }
  }, [props.policyMode]);

  useEffect(() => {
    if (!props.prefillPolicy || props.policyMode !== "v2") return;
    setPolicySchemaVersion("policy_tree.v2");
  }, [props.prefillPolicy, props.policyMode]);

  useEffect(() => {
    if (props.policyMode === "v2") return;
    if (startWithEmptyPolicy) return;
    if (props.prefillPolicyTree == null) {
      editRunLineageBaselineRef.current = null;
      return;
    }
    if (catalogLoading || fieldsByPath.size === 0) return;
    const root = parsePolicyTreeToEditorRoot(props.prefillPolicyTree, fieldsByPath);
    if (!root) return;
    setPolicyRoot(root);
    const action = resolvePolicyDefaultSimAction(props.prefillPolicyTree);
    setBuilderSimAction(action);
    const schema = String(
      (props.prefillPolicyTree as { schema_version?: unknown })?.schema_version ?? ""
    );
    if (schema === "policy_tree.bundle.v1") {
      setPolicySchemaVersion("policy_tree.bundle.v1");
    } else if (schema === "policy_tree.v1" || !schema) {
      setPolicySchemaVersion("policy_tree.v1");
    }
    const actionForBuild =
      action ?? inferUniformEditorGroupAction(root) ?? "veto";
    const built = buildSimulatePolicy(
      root,
      actionForBuild,
      fieldsByPath,
      maxDepth,
      maxNodes
    );
    if (built.ok) {
      const merged = mergePrefillManagementIntoBuiltPolicy(
        built.policy,
        props.prefillPolicyTree
      );
      editRunLineageBaselineRef.current = prepareSimulatePolicyWithDualTimeframes(
        merged,
        candleTimeframeHints()
      ).policy;
    } else {
      editRunLineageBaselineRef.current = null;
    }
    // Omit candleTimeframeHints / maxDepth from deps: re-running would wipe
    // in-progress edits when the user only changes the simulation window or TF.
  }, [catalogLoading, fieldsByPath, props.policyMode, props.prefillPolicyTree, startWithEmptyPolicy]);

  useEffect(() => {
    if (!startWithEmptyPolicy || catalogLoading || scopedReportId) return;
    setPolicyRoot(defaultPolicyRoot(catalogFields));
    commitV2PolicyDraft({ schema_version: "policy_tree.v2", rules: [] });
    setBuilderSimAction(null);
    setPicksSimAction(null);
    setCombineExplicitlySelected(false);
    setCombineSelectedOp("and");
    setCollapsedGroupIds(new Set());
    setPolicyViewMode("builder");
  }, [catalogFields, catalogLoading, commitV2PolicyDraft, scopedReportId, startWithEmptyPolicy]);

  useEffect(() => {
    if (isStandalone) setUseCustomRange(true);
  }, [isStandalone]);

  useEffect(() => {
    if (!shouldSyncDashboardWindow) return;
    if (isStandalone) {
      const derivedTimeframe = deriveTimeframeKeyFromLocalDateRange(dateRange);
      writeDashboardDateWindow(
        persistStateFromTradeLog(dateRange, derivedTimeframe, getActiveDashboardDatePresetId(dateRange))
      );
      return;
    }
    writeDashboardDateWindow(
      buildPersistedFromReports(useCustomRange, timeframe, dateRange)
    );
  }, [shouldSyncDashboardWindow, isStandalone, useCustomRange, timeframe, dateRange]);

  useDashboardDateWindowListener((p) => {
    if (!shouldSyncDashboardWindow) return;
    const next = reportsStateFromPersisted(p);
    if (!isStandalone) {
      setTimeframe((prev) => (prev === next.timeframe ? prev : next.timeframe));
      setUseCustomRange((prev) => (prev === next.useCustomRange ? prev : next.useCustomRange));
    }
    setDateRange((prev) => (equalDateRange(prev, next.dateRange) ? prev : next.dateRange));
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      setCatalogError(null);
      try {
        const data = await fetchTradePolicyFilterCatalog();
        if (cancelled) return;
        const fields = Array.isArray(data.fields) ? data.fields : [];
        setCatalogFields(fields);
        setMarginalEqPrefixMap(
          data.marginal_bucket_import?.eq_string_prefixes && typeof data.marginal_bucket_import.eq_string_prefixes === "object"
            ? (data.marginal_bucket_import.eq_string_prefixes as Record<string, string>)
            : {}
        );
        const ver = data.version ?? "";
        setCatalogVersion(ver);
        setPolicySchemaVersion(data.policy_schema_version ?? "");
        setPolicyTreeMeta(data.policy_tree ?? null);
        if (!scopedReportId) {
          setPolicyRoot(defaultPolicyRoot(fields));
        }
        try {
          const prev = sessionStorage.getItem(TRADE_POLICY_CATALOG_VERSION_STORAGE_KEY);
          if (prev && ver && prev !== ver) {
            toast.info("Filter catalog updated", { description: `Loaded ${ver}. Rebuild rules if you rely on field paths or enums.` });
          }
          if (ver) sessionStorage.setItem(TRADE_POLICY_CATALOG_VERSION_STORAGE_KEY, ver);
        } catch {
          /* storage blocked */
        }
      } catch (e) {
        if (cancelled) return;
        if (isPaywallRequiredError(e)) {
          redirectToBillingPaywall();
          return;
        }
        setCatalogError(e instanceof Error ? e.message : "Failed to load filter catalog.");
        setCatalogFields([]);
        setMarginalEqPrefixMap({});
        setPolicyTreeMeta(null);
        if (!scopedReportId) {
          setPolicyRoot(defaultPolicyRoot([]));
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scopedReportId]);

  const clearAutoPickLabelForEditedRule = useCallback((root: EditorNode, editedRuleId: string): EditorNode => {
    const walk = (n: EditorNode): { next: EditorNode; containsEditedRule: boolean } => {
      if (n.kind === "rule") {
        return { next: n, containsEditedRule: n.id === editedRuleId };
      }

      let containsEditedRule = false;
      const nextChildren = n.children.map((child) => {
        const res = walk(child);
        if (res.containsEditedRule) containsEditedRule = true;
        return res.next;
      });

      let nextGroup: typeof n = { ...n, children: nextChildren };
      if (containsEditedRule && n.autoLabelFromPick) {
        nextGroup = { ...nextGroup, label: "", autoLabelFromPick: false, sourcePickId: undefined };
      }
      return { next: nextGroup, containsEditedRule };
    };

    return walk(root).next;
  }, []);

  const onUpdateRule = useCallback((id: string, patch: Partial<EditorRuleNode>) => {
    setPolicyRoot((prev) => {
      const updated = updateNodeById(prev, id, (n) => (n.kind === "rule" ? { ...n, ...patch } : n));
      return clearAutoPickLabelForEditedRule(updated, id);
    });
  }, [clearAutoPickLabelForEditedRule]);

  const onRulePathChange = useCallback(
    (id: string, path: string) => {
      setPolicyRoot((prev) => {
        const updated = updateNodeById(prev, id, (n) => {
          if (n.kind !== "rule") return n;
          return editorRulePathChange(n, path, fieldsByPath);
        });
        return clearAutoPickLabelForEditedRule(updated, id);
      });
    },
    [clearAutoPickLabelForEditedRule, fieldsByPath]
  );

  const onRuleOpChange = useCallback(
    (id: string, op: string) => {
      setPolicyRoot((prev) => {
        const found = findNodeById(prev, id);
        if (!found || found.kind !== "rule") return prev;
        const row = found;
        const field = fieldsByPath.get(row.path);
        let patch: Partial<EditorRuleNode>;
        if (field?.value_kind === "enum") {
          if (op === "in") {
            const fromSet = row.valueEnumSet.length ? row.valueEnumSet.join(", ") : "";
            const draft = row.valueEnum.trim() !== "" ? row.valueEnum : fromSet;
            patch = {
              op,
              valueEnumSet: [],
              valueEnum: draft,
            };
          } else {
            const fromDraft = row.valueEnum.trim();
            const firstFromList =
              fromDraft !== ""
                ? fromDraft.split(",")[0]?.trim() ?? ""
                : row.valueEnumSet[0] ?? "";
            patch = { op, valueEnumSet: [], valueEnum: firstFromList };
          }
        } else {
          if (field?.value_kind === "number") {
            const prevWasRange = row.op === "range";
            const nextIsRange = op === "range";
            if (nextIsRange) {
              patch = {
                op,
                valueNumberMin: row.valueNumber.trim() !== "" ? row.valueNumber : (row as any).valueNumberMin ?? "",
                valueNumberMax: (row as any).valueNumberMax ?? "",
                valueNumber: "",
              } as any;
            } else if (prevWasRange) {
              const fallback = ((row as any).valueNumberMin ?? "").trim() || ((row as any).valueNumberMax ?? "").trim() || "";
              patch = { op, valueNumber: fallback, valueNumberMin: "", valueNumberMax: "" } as any;
            } else {
              patch = { op };
            }
          } else {
            patch = { op };
          }
        }
        const updated = updateNodeById(prev, id, (n) => (n.kind === "rule" ? { ...n, ...patch } : n));
        return clearAutoPickLabelForEditedRule(updated, id);
      });
    },
    [clearAutoPickLabelForEditedRule, fieldsByPath]
  );

  const onGroupOpChange = useCallback((id: string, op: TradePolicyGroupNode["op"]) => {
    setPolicyRoot((prev) =>
      updateNodeById(prev, id, (n) => (n.kind === "group" ? { ...n, op } : n))
    );
  }, []);
  const onGroupActionChange = useCallback((id: string, action: TradePolicyAction) => {
    setPolicyRoot((prev) =>
      updateNodeById(prev, id, (n) => (n.kind === "group" ? { ...n, action } : n))
    );
  }, []);
  const onGroupLabelChange = useCallback((id: string, label: string) => {
    setPolicyRoot((prev) =>
      updateNodeById(prev, id, (n) => (n.kind === "group" ? { ...n, label, autoLabelFromPick: false, sourcePickId: undefined } : n))
    );
  }, []);

  const startOrPollComparisonRun = useCallback(
    async (runId: string, body: TradePolicySimulateRequest, label: string, policy: TradePolicySimulatePolicy, scope: ComparisonScopeSnapshot) => {
      const started = await simulateTradePolicyStart(body);
      if (started && typeof started === "object" && "job_id" in (started as any)) {
        const jobId = String((started as any).job_id);
        const pending: ComparisonRun = {
          id: runId,
          label,
          createdAt: Date.now(),
          policy,
          scope,
          result: null,
          error: null,
          status: "running",
          jobId,
        };
        setComparisonRuns((prev) => [pending, ...prev].slice(0, MAX_COMPARISON_RUNS));
        void (async () => {
          try {
            const outcome = await waitForSimulateTradePolicyJob(jobId);
            setComparisonRuns((prev) =>
              prev.map((r) =>
                r.id === runId
                  ? normalizeComparisonRunTitleFields({ ...r, result: outcome.result, status: "succeeded", error: null })
                  : r
              )
            );
            void persistReportSimulationAfterRun(runId, jobId);
          } catch (e) {
            if (e instanceof SimulateTradePolicyJobStillRunningError) {
              setComparisonRuns((prev) =>
                prev.map((r) => (r.id === runId ? { ...r, stillRunning: true, status: "running", error: null } : r))
              );
              return;
            }
            setComparisonRuns((prev) =>
              prev.map((r) =>
                r.id === runId
                  ? { ...r, result: null, status: "failed", error: e instanceof Error ? e.message : "Simulation failed." }
                  : r
              )
            );
          }
        })();
        return;
      }

      const res = started as any;
      const doneJobId = typeof res?.job_id === "string" ? String(res.job_id) : undefined;
      const done: ComparisonRun = {
        id: runId,
        label,
        createdAt: Date.now(),
        policy,
        scope,
        result: res,
        error: null,
        status: "succeeded",
        ...(doneJobId ? { jobId: doneJobId } : {}),
      };
      setComparisonRuns((prev) => [done, ...prev].slice(0, MAX_COMPARISON_RUNS));
      if (doneJobId) void persistReportSimulationAfterRun(runId, doneJobId);
    },
    [persistReportSimulationAfterRun]
  );

  const retryComparisonRun = useCallback(
    (runId: string) => {
      const run = comparisonRuns.find((r) => r.id === runId);
      const jobId = run?.jobId;
      if (!jobId) return;
      setComparisonRuns((prev) =>
        prev.map((r) => (r.id === runId ? { ...r, stillRunning: false, error: null, status: "running" } : r))
      );
      void (async () => {
        try {
          const outcome = await waitForSimulateTradePolicyJob(jobId);
          setComparisonRuns((prev) =>
            prev.map((r) =>
              r.id === runId
                ? normalizeComparisonRunTitleFields({
                    ...r,
                    result: outcome.result,
                    status: "succeeded",
                    error: null,
                    stillRunning: false,
                  })
                : r
            )
          );
          void persistReportSimulationAfterRun(runId, jobId);
        } catch (e) {
          if (e instanceof SimulateTradePolicyJobStillRunningError) {
            setComparisonRuns((prev) =>
              prev.map((r) => (r.id === runId ? { ...r, stillRunning: true, status: "running", error: null } : r))
            );
            return;
          }
          setComparisonRuns((prev) =>
            prev.map((r) =>
              r.id === runId
                ? {
                    ...r,
                    result: null,
                    status: "failed",
                    error: e instanceof Error ? e.message : "Simulation failed.",
                    stillRunning: false,
                  }
                : r
            )
          );
        }
      })();
    },
    [comparisonRuns, persistReportSimulationAfterRun]
  );

  const allGroupIds = useMemo(() => {
    const out: string[] = [];
    const walk = (n: EditorNode) => {
      if (n.kind === "group") {
        out.push(n.id);
        for (const c of n.children) walk(c);
      }
    };
    walk(policyRoot);
    return out;
  }, [policyRoot]);

  const allPolicyNodeIds = useMemo(() => {
    const out: string[] = [];
    const walk = (n: EditorNode) => {
      out.push(n.id);
      if (n.kind === "group") for (const c of n.children) walk(c);
    };
    if (policyRoot.kind === "group") {
      for (const c of policyRoot.children) walk(c);
    } else {
      walk(policyRoot);
    }
    return out;
  }, [policyRoot]);

  const collapseAllGroups = useCallback(() => {
    setCollapsedGroupIds(new Set(allPolicyNodeIds));
  }, [allPolicyNodeIds]);

  const expandAllGroups = useCallback(() => setCollapsedGroupIds(new Set()), []);

  const clearPolicyTree = useCallback(() => {
    const toastTitle = clearedSimulatorPolicyToastTitle(policyRoot);
    setPolicyRoot({
      kind: "group",
      id: genEditorId(),
      op: "and",
      label: "",
      children: [],
    });
    setCollapsedGroupIds(new Set());
    toast.info(toastTitle);
  }, [policyRoot]);

  /** True when every group and rule accordion in the tree is expanded. */
  const allPolicyGroupsExpanded = useMemo(
    () => allPolicyNodeIds.length > 0 && allPolicyNodeIds.every((id) => !collapsedGroupIds.has(id)),
    [allPolicyNodeIds, collapsedGroupIds]
  );

  const onRemove = useCallback(
    (id: string) => {
      setPolicyRoot((prev) => {
        const next = removeNodeById(prev, id);
        // Allow true deletion in graph/builder; if the last visible node is removed,
        // keep an empty root container instead of auto-reseeding a new group.
        if (!next) {
          return {
            kind: "group",
            id: genEditorId(),
            op: "and",
            label: "",
            children: [],
          };
        }
        return next;
      });
    },
    []
  );

  const onAddRule = useCallback(
    (groupId: string) => {
      setPolicyRoot((prev) => addChildToGroup(prev, groupId, newEditorRule(catalogFields)));
    },
    [catalogFields]
  );

  const onAddGroup = useCallback(
    (groupId: string) => {
      setPolicyRoot((prev) =>
        addChildToGroup(prev, groupId, newEditorGroup(catalogFields, builderSimAction ?? undefined))
      );
    },
    [builderSimAction, catalogFields]
  );

  const onConnectRuleToGroup = useCallback(
    (ruleId: string, groupId: string) => {
      setPolicyRoot((prev) => {
        const findParentGroupId = (node: EditorNode, childId: string): string | null => {
          if (node.kind !== "group") return null;
          for (const child of node.children) {
            if (child.id === childId) return node.id;
            const nested = findParentGroupId(child, childId);
            if (nested) return nested;
          }
          return null;
        };
        const rule = findNodeById(prev, ruleId);
        const target = findNodeById(prev, groupId);
        if (!rule || !target || rule.kind !== "rule" || target.kind !== "group") return prev;
        if (findParentGroupId(prev, ruleId) === groupId) return prev;

        let moved = removeNodeById(prev, ruleId);
        if (!moved) moved = defaultPolicyRoot(catalogFields);
        const attached = addChildToGroup(moved, groupId, rule);
        return attached;
      });
    },
    [catalogFields]
  );

  const onDetachConnection = useCallback((groupId: string, nodeId: string) => {
    setPolicyRoot((prev) => {
      if (prev.kind !== "group") return prev;
      const node = findNodeById(prev, nodeId);
      if (!node) return prev;

      const detachFromGroup = (tree: EditorNode): { next: EditorNode; detached: EditorNode | null } => {
        if (tree.kind !== "group") return { next: tree, detached: null };
        if (tree.id === groupId) {
          let detached: EditorNode | null = null;
          const nextChildren = tree.children.filter((child) => {
            if (child.id === nodeId) {
              detached = child;
              return false;
            }
            return true;
          });
          return { next: { ...tree, children: nextChildren }, detached };
        }
        let detached: EditorNode | null = null;
        const nextChildren = tree.children.map((child) => {
          const r = detachFromGroup(child);
          if (!detached && r.detached) detached = r.detached;
          return r.next;
        });
        return { next: { ...tree, children: nextChildren }, detached };
      };

      const { next, detached } = detachFromGroup(prev);
      if (!detached || next.kind !== "group") return prev;
      // Keep parent group intact; just move the disconnected node to root so it can be reattached.
      return { ...next, children: [...next.children, detached] };
    });
  }, []);

  const onRuleDragStart = useCallback((ruleId: string, e: React.DragEvent<HTMLElement>) => {
    setDraggedRuleId(ruleId);
    draggedRuleIdRef.current = ruleId;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/x-policy-rule-id", ruleId);
    e.dataTransfer.setData("text/plain", ruleId);
  }, []);

  const onRuleDragEnd = useCallback(() => {
    setDraggedRuleId(null);
    draggedRuleIdRef.current = null;
  }, []);

  const onGroupDragOver = useCallback(
    (_groupId: string, e: React.DragEvent<HTMLDivElement>) => {
      const ruleId = draggedRuleIdRef.current ?? draggedRuleId;
      if (!ruleId) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    },
    [draggedRuleId]
  );

  const onGroupDropRule = useCallback(
    (groupId: string, e: React.DragEvent<HTMLDivElement>) => {
      const droppedRuleId = draggedRuleIdRef.current ?? draggedRuleId;
      if (!droppedRuleId) return;
      e.preventDefault();
      onConnectRuleToGroup(droppedRuleId, groupId);
      setDraggedRuleId(null);
      draggedRuleIdRef.current = null;
    },
    [draggedRuleId, onConnectRuleToGroup]
  );

  const onLoadExample = useCallback(() => {
    if (!policyTreeMeta?.example) return;
    const ex = exampleToEditorRoot(policyTreeMeta.example, fieldsByPath, catalogFields);
    if (!ex) {
      toastWarning("Could not load example", { description: "The catalog example is missing or not in the expected shape." });
      return;
    }
    setBuilderSimAction(null);
    setPolicyRoot(
      ex.kind === "group"
        ? ex
        : {
            kind: "group",
            id: genEditorId(),
            op: "and",
            children: [ex],
          }
    );
    toast.success("Template loaded");
  }, [catalogFields, fieldsByPath, policyTreeMeta]);

  /** Exit Placement rules from a canvas combined-policy prefill (hidden from the v1 tree editor). */
  const prefillManagementPolicy = useMemo(
    () =>
      !isManagementV2Mode && props.prefillPolicyTree != null
        ? extractBundleManagementPolicyTree(props.prefillPolicyTree)
        : null,
    [isManagementV2Mode, props.prefillPolicyTree]
  );

  const prefillManagementDisplayNodes = useMemo(
    () =>
      prefillManagementPolicy
        ? policyTreeV2RuleDisplayNodes(prefillManagementPolicy, fieldsByPath)
        : [],
    [fieldsByPath, prefillManagementPolicy]
  );

  const withPrefillManagement = useCallback(
    (built: import("../../../types/tradePolicy").TradePolicySimulatePolicy) => {
      if (props.prefillPolicyTree == null) return built;
      return mergePrefillManagementIntoBuiltPolicy(built, props.prefillPolicyTree);
    },
    [props.prefillPolicyTree]
  );

  const runSimulation = useCallback(async () => {
    const shouldOpenStandaloneResults =
      isStandalone &&
      Boolean(props.simulatorWizardLayout) &&
      !props.workspaceSimulationContext;

    // Stepped wizard: only generate from Review after the Continue→Generate
    // swap has armed (blocks click-through on the same press).
    if (props.simulatorWizardLayout) {
      const gate = wizardGenerateGateRef.current;
      if (gate.step !== 2 || !gate.armed) {
        toast.info("Continue to Review first", {
          description: "Open Review, then click Generate simulation.",
        });
        return;
      }
    }

    if (simulateRateLimit.isCoolingDown) {
      toastWarning("Rate limit reached", {
        description: `Try again in ${simulateRateLimit.retryAfterSeconds}s.`,
      });
      return;
    }
    const workspaceBlocked = props.workspaceSimulationBlockedReason?.trim();
    if (
      workspaceBlocked &&
      Boolean(props.simulatorWizardLayout) &&
      Boolean(props.workspaceSimulationContext)
    ) {
      toast.warning("Simulation limit reached", { description: workspaceBlocked });
      return;
    }
    if (comparisonRuns.length >= MAX_COMPARISON_RUNS) {
      toastSimulatorComparisonAtLimit(scopedReportId);
      return;
    }
    if (comparisonRuns.filter((run) => run.status === "running").length >= MAX_SIMULATE_INFLIGHT) {
      toastWarning("Simulations in progress", {
        description: `Wait for a running simulation to finish (max ${MAX_SIMULATE_INFLIGHT} at a time).`,
      });
      return;
    }
    const now = Date.now();
    if (now - lastSimulateAtRef.current < 1000) return;
    lastSimulateAtRef.current = now;

    if (isStandalone && !effectiveBrokerAccountCode.trim()) {
      toast.warning("Select broker account", { description: "Choose which trade account to run this simulation against." });
      return;
    }
    const built = buildSimulatePolicy(policyRoot, defaultGroupAction, fieldsByPath, maxDepth, maxNodes);
    if (!built.ok) {
      toast.warning("Check your policy", { description: built.reason });
      return;
    }
    if (
      isPolicyTreeBundleV1(built.policy) &&
      built.policy.meta?.entry_keep_combine === "all" &&
      (built.policy.segments.filter((s) => s.action === "keep_only").length >= 2)
    ) {
      const keepTitles = built.policy.segments
        .filter((s) => s.action === "keep_only")
        .map((s) => s.source_title || "Keep")
        .slice(0, 4);
      toast.warning("Keep stack Match is All of (AND)", {
        description: `BE intersects Keep segments (${keepTitles.join(", ")}). For disagreeing regimes set Combined policies Match to Any of (OR) so entry_keep_combine is "any".`,
      });
      return;
    }
    const mergedPolicy = withPrefillManagement(built.policy);
    const prepared = preparePolicyForSimulate(mergedPolicy);
    const simulatePolicy = prepared.policy as import("../../../types/tradePolicy").TradePolicySimulatePolicy;

    const lineageBaseline = editRunLineageBaselineRef.current;
    const policyChangedFromBaseline =
      lineageBaseline != null
        ? !workspacePolicyTreesEquivalentForLineage(lineageBaseline, simulatePolicy)
        : false;
    const lineageQueueFields = {
      policy: simulatePolicy as unknown,
      baselinePolicyForLineage: lineageBaseline ?? undefined,
      policyChangedFromBaseline,
    };

    const limitValue = DEFAULT_POLICY_LIMIT;

    if (isStandalone || useCustomRange) {
      if (!hasUsableTradeWindowForSimulate(dateRange, timeframe, reportingTimeZone)) {
        toast.warning("Date range required", { description: "Pick a start and end date, or choose a quick range (e.g. Last 7 days) so a simulation window is defined." });
        return;
      }
    }

    const nextRunTitle = normalizeMandatorySimulatorRunTitle(builderRunTitleDraft);
    if (!nextRunTitle) {
      toast.info("Enter a run title", { description: "Add a title for this run before simulating. It appears in results and is used when saving a simulation." });
      return;
    }

    if (candleBudgetExceeds) {
      toast.warning("Candle bar budget exceeded", {
        description:
          "Pick a shorter simulation period that fits under the bar limit, then generate again.",
      });
      return;
    }

    if (props.simulatorWizardLayout && effectiveBrokerAccountCode.trim()) {
      const gate = await wizardMdGate.checkBeforeRun();
      if (gate) return;
    }

    const { fields, scope: scopeSnapshot } = buildSimulatorScopeAndWindowFields(limitValue);

    const body: TradePolicySimulateRequest = {
      policy: simulatePolicy,
      policy_row_version: effectivePolicyRowVersion,
      ...prepared.fields,
      simulate_ohlc_fill_missing: false,
      ...(effectiveBrokerAccountCode ? { broker_account_code: effectiveBrokerAccountCode } : {}),
      ...fields,
      ...(symbol.trim() ? { symbol: symbol.trim() } : {}),
      include_trade_ids: true,
      limit: limitValue,
      ...reportScopedSimulatePersistFields(effectiveSimulateReportId),
      ...(linkedStrategyIdForNewRuns ? { strategy_id: linkedStrategyIdForNewRuns } : {}),
      title: nextRunTitle,
    };

    const runId = genEditorId();
    const resolvedLabel = nextRunTitle;

    const optimisticRun = attachLinkedStrategyId(
      {
        id: runId,
        label: resolvedLabel,
        savedTitle: resolvedLabel,
        createdAt: Date.now(),
        policy: simulatePolicy,
        scope: scopeSnapshot,
        result: null,
        error: null,
        status: "running",
      },
      linkedStrategyIdForNewRuns
    );
    setComparisonRuns((prev) => [optimisticRun, ...prev].slice(0, MAX_COMPARISON_RUNS));

    // Canvas wizard attaches under a parent node — keep the footer in "Running…" until
    // the job finishes and save closes the dialog. Do not clear the run title until then.
    const awaitWorkspaceWizardCompletion =
      Boolean(props.simulatorWizardLayout) && Boolean(props.workspaceSimulationContext);

    setSimLoading(true);
    try {
      const started = await simulateTradePolicyStart(body);
      if (started && typeof started === "object" && "job_id" in (started as any) && !("baseline" in (started as any))) {
        const jobId = String((started as any).job_id);
        setComparisonRuns((prev) => {
          const next = prev.map((r) => (r.id === runId ? { ...r, jobId } : r));
          if (shouldOpenStandaloneResults) navigateToStandaloneResults(next, runId);
          return next;
        });
        if (shouldOpenStandaloneResults) {
          setBuilderRunTitleDraft("");
          return;
        }

        if (awaitWorkspaceWizardCompletion) {
          // Canvas host shows processing node + loading path; wait/persist there.
          props.onWorkspaceSimulationQueued?.({
            title: nextRunTitle,
            jobId,
            ...lineageQueueFields,
          });
          setBuilderRunTitleDraft("");
          return;
        }

        setBuilderRunTitleDraft("");
        void (async () => {
          try {
            const outcome = await waitForSimulateTradePolicyJob(jobId);
            setComparisonRuns((prev) =>
              prev.map((r) =>
                r.id === runId
                  ? normalizeComparisonRunTitleFields({
                      ...r,
                      result: outcome.result,
                      status: "succeeded",
                      error: null,
                    })
                  : r
              )
            );
            void persistReportSimulationAfterRun(runId, jobId);
          } catch (e) {
            if (e instanceof SimulateTradePolicyJobStillRunningError) {
              setComparisonRuns((prev) =>
                prev.map((r) =>
                  r.id === runId ? { ...r, stillRunning: true, status: "running", error: null } : r
                )
              );
              return;
            }
            setComparisonRuns((prev) =>
              prev.map((r) =>
                r.id === runId
                  ? {
                      ...r,
                      result: null,
                      status: "failed",
                      error: e instanceof Error ? e.message : "Simulation failed.",
                    }
                  : r
              )
            );
          }
        })();
        return;
      }

      const res = started as any;
      if (res?.coverage?.trades_total === 0) {
        const failedRun = attachLinkedStrategyId(
          {
            id: runId,
            label: resolvedLabel,
            savedTitle: resolvedLabel,
            createdAt: Date.now(),
            policy: simulatePolicy,
            scope: scopeSnapshot,
            result: null,
            error: "No closed trades in the selected range.",
            status: "failed",
          },
          linkedStrategyIdForNewRuns
        );
        setComparisonRuns((prev) => {
          const has = prev.some((r) => r.id === runId);
          if (has) return prev.map((r) => (r.id === runId ? failedRun : r));
          const next = [failedRun, ...prev];
          if (next.length > MAX_COMPARISON_RUNS) {
            toast.info("Comparison limit", { description: `Keeping the newest ${MAX_COMPARISON_RUNS} runs.` });
            return next.slice(0, MAX_COMPARISON_RUNS);
          }
          return next;
        });
        toast.warning("No trades in range", {
          description: "No closed trades in the selected range. Adjust the date window and try again.",
        });
        return;
      }

      const syncJobId = typeof res?.job_id === "string" ? String(res.job_id) : undefined;
      const syncMeta = parseSimulateBacktestMeta(res);
      const syncSummaryTitles = syncMeta.backtestSummary
        ? comparisonRunTitlesFromBacktestSummary(syncMeta.backtestSummary)
        : null;
      const newRun = attachLinkedStrategyId(
        {
          id: runId,
          label: syncSummaryTitles?.label ?? resolvedLabel,
          savedTitle: syncSummaryTitles?.savedTitle ?? resolvedLabel,
          createdAt: Date.now(),
          policy: simulatePolicy,
          scope: scopeSnapshot,
          result: res,
          error: null,
          status: "succeeded",
          ...(syncJobId ? { jobId: syncJobId } : {}),
          ...(syncMeta.backtestId
            ? { backtestId: syncMeta.backtestId, isSavedBacktest: true }
            : {}),
        },
        linkedStrategyIdForNewRuns
      );
      setComparisonRuns((prev) => {
        const next = prev.map((r) => (r.id === runId ? newRun : r));
        if (shouldOpenStandaloneResults) navigateToStandaloneResults(next, runId);
        return next;
      });
      if (syncMeta.backtestId) {
        if (awaitWorkspaceWizardCompletion) {
          props.onWorkspaceSimulationQueued?.({
            title: nextRunTitle,
            ...lineageQueueFields,
          });
        }
        try {
          props.onBacktestSaved?.(syncMeta.backtestId);
        } catch {
          // ignore
        }
        setLinkedBacktestsRefreshKey((k) => k + 1);
        setBuilderRunTitleDraft("");
      } else if (syncJobId) {
        if (awaitWorkspaceWizardCompletion) {
          props.onWorkspaceSimulationQueued?.({
            title: nextRunTitle,
            jobId: syncJobId,
            ...lineageQueueFields,
          });
          setBuilderRunTitleDraft("");
        } else {
          void persistReportSimulationAfterRun(runId, syncJobId);
          setBuilderRunTitleDraft("");
        }
      } else if (shouldOpenStandaloneResults) {
        setBuilderRunTitleDraft("");
      }
    } catch (e) {
      if (isPaywallRequiredError(e)) {
        setComparisonRuns((prev) => prev.filter((r) => r.id !== runId));
        redirectToBillingPaywall();
        return;
      }
      if (tryHandleMarketDataUnresolved(e)) {
        setComparisonRuns((prev) => prev.filter((r) => r.id !== runId));
        return;
      }
      const budgetInfo = getCandleBarBudgetExceededInfo(e);
      if (budgetInfo) {
        setCandleBudgetExceeds(true);
        const first = budgetInfo.suggestedPeriods?.[0];
        if (first) {
          handleSelectCandleBudgetPeriod(
            first.window_start.slice(0, 10),
            first.window_end.slice(0, 10)
          );
        }
        toast.warning("Candle bar budget exceeded", {
          description: budgetInfo.message,
        });
        setComparisonRuns((prev) => prev.filter((r) => r.id !== runId));
        return;
      }
      const errRun = attachLinkedStrategyId(
        {
          id: runId,
          label: resolvedLabel,
          savedTitle: resolvedLabel,
          createdAt: Date.now(),
          policy: simulatePolicy,
          scope: scopeSnapshot,
          result: null,
          error: e instanceof Error ? e.message : "Simulation failed.",
          status: "failed",
        },
        linkedStrategyIdForNewRuns
      );
      setComparisonRuns((prev) => {
        const has = prev.some((r) => r.id === runId);
        if (has) return prev.map((r) => (r.id === runId ? errRun : r));
        const next = [errRun, ...prev];
        if (next.length > MAX_COMPARISON_RUNS) {
          toast.info("Comparison limit", { description: `Keeping the newest ${MAX_COMPARISON_RUNS} runs.` });
          return next.slice(0, MAX_COMPARISON_RUNS);
        }
        return next;
      });
    } finally {
      setSimLoading(false);
    }
  }, [
    defaultGroupAction,
    buildSimulatorScopeAndWindowFields,
    builderRunTitleDraft,
    candleBudgetExceeds,
    candleTimeframe,
    preparePolicyForSimulate,
    comparisonRuns,
    handleSelectCandleBudgetPeriod,
    simulateRateLimit.isCoolingDown,
    simulateRateLimit.retryAfterSeconds,
    dateRange.endDate,
    dateRange.startDate,
    effectiveBrokerAccountCode,
    effectivePolicyRowVersion,
    fieldsByPath,
    isStandalone,
    linkedStrategyIdForNewRuns,
    maxDepth,
    maxNodes,
    policyRoot,
    effectiveSimulateReportId,
    symbol,
    timeframe,
    useCustomRange,
    props.workspaceSimulationBlockedReason,
    props.simulatorWizardLayout,
    props.workspaceSimulationContext,
    wizardMdGate.checkBeforeRun,
    withPrefillManagement,
    props.onWorkspaceSimulationQueued,
    navigateToStandaloneResults,
    persistReportSimulationAfterRun,
  ]);

  const saveComparisonRunLabel = useCallback(
    async (id: string, label: string) => {
      const title = normalizeComparisonRunSavedTitle(String(label ?? "").trim() || "Untitled simulation");
      const run = comparisonRuns.find((r) => r.id === id);
      if (!run) return;

      setComparisonRuns((prev) =>
        prev.map((r) =>
          r.id === id
            ? {
                ...r,
                savedTitle: title,
                label: title,
                titleParseMode: "literal" as const,
              }
            : r
        )
      );

      let backtestId = run.backtestId?.trim() || undefined;
      if (!backtestId && run.jobId) {
        const saved = await saveSimulateTradePolicyJob(run.jobId, {
          ...(props.workspaceSimulationContext
            ? {
                workspaceId: props.workspaceSimulationContext.workspaceId,
                parentNodeId: props.workspaceSimulationContext.parentNodeId,
                ...(props.workspaceSimulationContext.position
                  ? { position: props.workspaceSimulationContext.position }
                  : {}),
              }
            : {}),
        });
        if (!saved.ok) {
          toastSimulateWorkspaceSaveFailure(saved, "Could not save simulation");
          return;
        }
        if (saved.backtestId) {
          backtestId = saved.backtestId;
          setComparisonRuns((prev) =>
            prev.map((r) =>
              r.id === id
                ? {
                    ...r,
                    savedTitle: title,
                    label: title,
                    titleParseMode: "literal" as const,
                    backtestId,
                    isSavedBacktest: true,
                  }
                : r
            )
          );
          try {
            props.onBacktestSaved?.(backtestId);
          } catch {
            // ignore
          }
        }
      }

      if (!backtestId) {
        toast.info("Save simulation to keep this name", {
          description:
            "The title is shown for this session. Use Save simulation (or run again) so the name persists when you leave this page.",
        });
        return;
      }

      const bid = backtestId;
      try {
        await patchBacktest(bid, { title });
        try {
          props.onBacktestSaved?.(bid);
        } catch {
          // ignore
        }
      } catch (e) {
        presentMutationWarning(e, "Could not save simulation name");
        try {
          const b = await getBacktestSlim(bid);
          const restored =
            typeof b?.title === "string" && b.title.trim() ? b.title.trim() : `Saved simulation ${bid.slice(0, 8)}…`;
          setComparisonRuns((prev) =>
            prev.map((row) =>
              row.id === id ? { ...row, savedTitle: restored, label: restored } : row
            )
          );
        } catch {
          // ignore
        }
      }
    },
    [comparisonRuns, props]
  );

  const removeComparisonRun = useCallback((id: string) => {
    const run = comparisonRuns.find((r) => r.id === id);
    const backtestId = run?.backtestId?.trim() || "";
    setComparisonRuns((prev) => prev.filter((r) => r.id !== id));
    if (!backtestId) return;
    // Drop from Trade Review / Trading PnL immediately; confirm delete in the background.
    props.onBacktestDeleted?.(backtestId);
    setLinkedBacktestsRefreshKey((k) => k + 1);
    void deleteBacktest(backtestId).then(
      () =>
        toast.success("Simulation deleted", {
          description: "Removed from comparison and deleted from saved simulations.",
        }),
      (e) => {
        presentMutationWarning(e, "Simulation not deleted");
        // Re-sync selectors if the server still has the row.
        setLinkedBacktestsRefreshKey((k) => k + 1);
        props.onBacktestSaved?.(backtestId);
      }
    );
  }, [comparisonRuns, props]);

  const isEmbeddedResultsOnly = props.layout === "embedded" && props.resultsOnlyLayout;
  const openPolicyModalRef = useRef<(() => void) | null>(null);

  const registerOpenPolicyModal = useCallback((open: (() => void) | null) => {
    openPolicyModalRef.current = open;
  }, []);

  const handleWorkspaceShowPolicy = useCallback(() => {
    openPolicyModalRef.current?.();
  }, []);

  const workspaceResultsRun = useMemo(() => {
    if (!isEmbeddedResultsOnly || comparisonRuns.length === 0) return null;
    const selectedId = props.initialSelectedRunId?.trim();
    if (selectedId) {
      return comparisonRuns.find((run) => run.id === selectedId) ?? comparisonRuns[0];
    }
    return comparisonRuns[0];
  }, [comparisonRuns, isEmbeddedResultsOnly, props.initialSelectedRunId]);

  const handleWorkspaceSimulationRename = useCallback(
    async (title: string) => {
      const run = workspaceResultsRun;
      if (!run) return;
      await saveComparisonRunLabel(run.id, title);
      toast.success("Simulation renamed");
    },
    [saveComparisonRunLabel, workspaceResultsRun]
  );

  const canWorkspaceSaveAsPolicyVersion = Boolean(
    (workspaceResultsRun?.linkedStrategyId?.trim() || linkedStrategyIdForNewRuns?.trim()) &&
      (workspaceResultsRun?.backtestId?.trim() || workspaceResultsRun?.isSavedBacktest)
  );

  const handleWorkspaceSaveAsPolicyVersion = useCallback(async () => {
    const run = workspaceResultsRun;
    if (!run) return;
    await saveStrategyVersionForRun(run.id);
  }, [saveStrategyVersionForRun, workspaceResultsRun]);

  const handleWorkspaceSimulationDelete = useCallback(async () => {
    const backtestId =
      workspaceResultsRun?.backtestId?.trim() || props.initialBacktestId?.trim() || "";
    if (!backtestId) {
      presentMutationWarning(new Error("Missing simulation id"), "Simulation not deleted");
      return;
    }
    try {
      await deleteBacktest(backtestId);
      setComparisonRuns((prev) =>
        prev.filter((r) => (r.backtestId?.trim() || "") !== backtestId && r.id !== workspaceResultsRun?.id)
      );
      toast.success("Simulation deleted");
      props.onBacktestDeleted?.(backtestId);
      props.onDeleted?.();
    } catch (e) {
      presentMutationWarning(e, "Simulation not deleted");
      throw e;
    }
  }, [props, workspaceResultsRun]);

  const exportWorkspaceSimulationJson = useCallback(() => {
    const run = workspaceResultsRun;
    if (!run) return;
    const backtestId = run.backtestId?.trim() || props.initialBacktestId?.trim() || "simulation";
    const payload = {
      id: backtestId,
      title: getComparisonRunSavedTitle(run),
      broker_account_code: effectiveBrokerAccountCode,
      linked_strategy_id: run.linkedStrategyId?.trim() || null,
      scope: run.scope,
      policy: run.policy,
      result: run.result,
    };
    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: "application/json;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `simulation_${backtestId}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }, [effectiveBrokerAccountCode, props.initialBacktestId, workspaceResultsRun]);


  const goToSourcePick = useCallback(
    (pickId: string) => {
      const exists = picks.some((p) => p.id === pickId);
      if (!exists) {
        toast.info("Pick not found", { description: "This linked pick is no longer available." });
        return;
      }
      setLinkedPickId(pickId);
      picksCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.requestAnimationFrame(() => {
        picksCardRef.current
          ?.querySelector<HTMLElement>('[data-slot="table-row"][data-state="active"]')
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      if (linkedPickResetTimerRef.current != null) {
        window.clearTimeout(linkedPickResetTimerRef.current);
      }
      linkedPickResetTimerRef.current = window.setTimeout(() => {
        setLinkedPickId(null);
        linkedPickResetTimerRef.current = null;
      }, 2400);
    },
    [picks]
  );

  const insertPicksIntoTree = useCallback(() => {
    if (catalogLoading || catalogFields.length === 0) {
      toast.info("Catalog not ready", { description: "Wait for the catalog to load first." });
      return;
    }
    const selectedBundles = combinedBundles.filter((b) => selectedBundleIds.has(b.id));
    if (selectedImportablePicks.length === 0 && selectedBundles.length === 0) {
      toast.info("Nothing selected", {
        description: "Select single picks and/or a Combined ran card to insert.",
      });
      return;
    }

    const pickByIdAll = new Map<string, ReportPick>();
    for (const p of picks) pickByIdAll.set(p.id, p);
    const bundleByIdAll = new Map<string, SimulatorPickBundleCard>();
    for (const b of combinedBundles) bundleByIdAll.set(b.id, b);
    const resolverDeps = {
      pickById: pickByIdAll,
      bundleById: bundleByIdAll,
      importPickPolicyNodes: (pick: ReportPick) => pickImportPolicyNodes(pick, pickCatalogCtx),
    };

    let skipped = 0;
    const units: EditorGroupNode[] = [];
    /** When inserting only from combined cards that share one Match op, apply it to an empty root. */
    let suggestedRootOp: TradePolicyGroupOp | null = null;

    const pushPickUnit = (p: ReportPick, nodes: TradePolicyPolicyNode[], action: TradePolicyAction) => {
      if (nodes.length === 0) {
        skipped += 1;
        return;
      }
      const pickLabel = p.label.trim() || `Pick ${p.id.slice(0, 8)}`;
      const unitNode: TradePolicyPolicyNode =
        nodes.length === 1 ? nodes[0]! : { kind: "group", op: "and", children: nodes };
      const unitEditor = policyNodeToEditor(unitNode, fieldsByPath);
      if (unitEditor.kind === "group") {
        units.push({
          ...unitEditor,
          action,
          label: pickLabel,
          autoLabelFromPick: true,
          sourcePickId: p.id,
        });
      } else {
        units.push({
          kind: "group",
          id: genEditorId(),
          op: "and",
          action,
          label: pickLabel,
          autoLabelFromPick: true,
          sourcePickId: p.id,
          children: [unitEditor],
        });
      }
    };

    const defaultPickAction = picksSimAction ?? defaultGroupAction;
    for (const p of selectedImportablePicks) {
      pushPickUnit(p, pickImportPolicyNodes(p, pickCatalogCtx), defaultPickAction);
    }

    if (selectedBundles.length > 0) {
      const sharedCombineOp = selectedBundles.every((b) => b.combineOp === selectedBundles[0]!.combineOp)
        ? selectedBundles[0]!.combineOp
        : null;
      if (selectedImportablePicks.length === 0 && sharedCombineOp) {
        suggestedRootOp = sharedCombineOp;
      }
      for (const bundle of selectedBundles) {
        // Bundle's own run action wins — picks toolbar must not overwrite a Keep card to Remove.
        const bundleAction = bundle.action ?? picksSimAction ?? defaultGroupAction;
        const leafUnits = collectBundleLeafImportUnits(bundle.id, resolverDeps);
        if (leafUnits.length === 0) {
          skipped += 1;
          continue;
        }
        for (const leaf of leafUnits) {
          pushPickUnit(leaf.pick, leaf.nodes, bundleAction);
        }
      }
    }

    if (units.length === 0) {
      toast.info("No convertible picks", {
        description:
          selectedBundles.length > 0
            ? "Selected combined cards could not be converted with the current catalog (leaf picks may be missing)."
            : "Selected picks could not be converted with the current catalog.",
      });
      return;
    }

    const existingPickIds = new Set<string>();
    const collectImportedPickIds = (node: EditorNode) => {
      if (node.kind !== "group") return;
      if (node.sourcePickId) existingPickIds.add(node.sourcePickId);
      for (const child of node.children) collectImportedPickIds(child);
    };
    collectImportedPickIds(policyRoot);

    // Deduplicate by leaf pick id (combined cards expand to the same leaves).
    const seenInBatch = new Set<string>();
    const unitsToAdd = units.filter((unit) => {
      const id = unit.sourcePickId;
      if (!id) return true;
      if (existingPickIds.has(id) || seenInBatch.has(id)) return false;
      seenInBatch.add(id);
      return true;
    });
    const skippedAlreadyPresent = units.length - unitsToAdd.length;
    if (unitsToAdd.length === 0) {
      toast.info("Picks already in policy tree", {
        description: "Selected picks/combined cards are already present as separate groups.",
      });
      return;
    }

    setPolicyRoot((prev) => {
      const root: EditorGroupNode =
        prev.kind === "group"
          ? prev
          : {
              kind: "group",
              id: genEditorId(),
              op: "and",
              label: "",
              autoLabelFromPick: false,
              sourcePickId: undefined,
              children: [prev],
            };
      const nextChildren = [...root.children, ...unitsToAdd];
      const existingActions = root.children
        .filter((c): c is EditorGroupNode => c.kind === "group")
        .map((c) => c.action ?? defaultGroupAction);
      const addedActions = unitsToAdd.map((u) => u.action ?? defaultGroupAction);
      const allActions = [...existingActions, ...addedActions];
      const mixedActions =
        allActions.length > 1 && !allActions.every((a) => a === allActions[0]);
      let nextOp: TradePolicyGroupOp = root.op;
      if (root.children.length === 0) {
        nextOp =
          suggestedRootOp ??
          (mixedActions ? "or" : unitsToAdd.length > 1 ? combineSelectedOp : "and");
      } else if (nextChildren.length === 1) {
        nextOp = "and";
      } else if (mixedActions || suggestedRootOp === "or") {
        // Keep-only OR stack + Remove (or other mixed) → Combined Match Any of (OR).
        nextOp = "or";
      }
      return {
        ...root,
        op: nextOp,
        label:
          nextChildren.length > 1 && !root.label?.trim()
            ? "Combined policies"
            : root.label,
        children: nextChildren,
      };
    });

    const inserted = unitsToAdd.length;
    toast.success("Imported into policy tree", {
      description: `Inserted ${inserted} group${inserted === 1 ? "" : "s"}${
        skipped ? ` (skipped ${skipped} unmapped)` : ""
      }${skippedAlreadyPresent ? ` (${skippedAlreadyPresent} already in tree)` : ""}.`,
    });
  }, [
    catalogFields,
    catalogFields.length,
    catalogLoading,
    combineSelectedOp,
    combinedBundles,
    defaultGroupAction,
    fieldsByPath,
    pickCatalogCtx,
    picks,
    picksSimAction,
    policyRoot,
    selectedBundleIds,
    selectedImportablePicks,
    setPolicyRoot,
  ]);

  const insertExitPlacementPicksIntoPolicyTree = useCallback(() => {
    if (selectedExitPlacementPicks.length === 0) {
      toast.info("No picks selected", { description: "Select one or more contexts from the picks table first." });
      return;
    }

    let current: PolicyTreeV2 = { schema_version: "policy_tree.v2", rules: [] };
    try {
      current = JSON.parse(v2PolicyDraftRef.current) as PolicyTreeV2;
    } catch {
      current = { schema_version: "policy_tree.v2", rules: [] };
    }

    const { policy, added, skipped } = mergeExitPlacementPicksIntoPolicy(
      current,
      selectedExitPlacementPicks
    );

    if (added === 0) {
      toast.info("Picks already in policy tree", {
        description:
          skipped > 0
            ? "Selected picks are already present as rules in the policy tree."
            : "Selected picks could not be converted into policy rules.",
      });
      return;
    }

    commitV2PolicyDraft(policy);
    setPolicySchemaVersion("policy_tree.v2");
    setExitPlacementSimulateLegsSynced(
      defaultSimulateLegs(legAvailabilityFromPolicyRules(policy.rules))
    );
    toast.success("Inserted picks", {
      description: `Added ${added} rule${added === 1 ? "" : "s"} to the policy tree${
        skipped > 0 ? ` (${skipped} skipped)` : ""
      }.`,
    });
  }, [commitV2PolicyDraft, selectedExitPlacementPicks, setExitPlacementSimulateLegsSynced]);

  const markPicksRanAndClearSelection = useCallback((ranPicks: ReportPick[]) => {
    if (ranPicks.length === 0) return;
    markPicksAsRan(ranPicks);
    // Separate-run count only increments when the user explicitly runs picks separately.
    for (const p of ranPicks) {
      incrementPickRanSeparatelyCount(pickBucketId(p), p.id, 1);
    }
    const ranIds = new Set(ranPicks.map((p) => p.id));
    setSelectedPickIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (!ranIds.has(id)) next.add(id);
      });
      return next;
    });
    setSelectedBundleIds(new Set());
  }, [incrementPickRanSeparatelyCount]);

  const markLeafPicksRanAndClearSelection = useCallback((ranPicks: ReportPick[]) => {
    // Combined runs must not move available singles into the Executed single lane —
    // that lane is only for picks run separately. Combined usage is tracked via
    // used_as_child_count on the card while it stays in Available Single Picks.
    if (ranPicks.length === 0) return;
    const ranIds = new Set(ranPicks.map((p) => p.id));
    setSelectedPickIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (!ranIds.has(id)) next.add(id);
      });
      return next;
    });
    setSelectedBundleIds(new Set());
  }, []);

  const queuePickComparisonRuns = useCallback(async () => {
    if (picksSimAction === null) {
      toast.info("Select action", { description: "Choose Remove or Keep only before running picks." });
      return;
    }
    const separatePicks = selectedImportablePicks.filter((p) => getSimulatorLane(p) !== "combined");
    if (separatePicks.length === 0) {
      toast.info("No convertible picks", {
        description: "Select picks from the Single lane (or Ran) to run separately.",
      });
      return;
    }
    if (comparisonRuns.length >= MAX_COMPARISON_RUNS) {
      toastSimulatorComparisonAtLimit(scopedReportId);
      return;
    }

    const nextRunTitle = normalizeMandatorySimulatorRunTitle(picksRunTitleDraft);
    if (!nextRunTitle) {
      toast.info("Enter a run title", { description: "Add a title for this run before simulating. It appears in results and is used when saving a simulation." });
      return;
    }

    setPickRunLoadingAction("individual");
    setImportingPicks(true);
    try {
      // Build scope once; each run uses the same scope, different policy root.
      const limitValue = DEFAULT_POLICY_LIMIT;

      if (useCustomRange) {
        if (!dateRange.startDate || !dateRange.endDate) {
          toast.warning("Date range required", { description: "Pick a start and end date, or switch to a timeframe preset." });
          return;
        }
      }

      const { fields, scope: scopeSnapshot } = buildSimulatorScopeAndWindowFields(limitValue);

      for (const p of separatePicks) {
        if (comparisonRuns.length + 1 >= MAX_COMPARISON_RUNS) break;
        const nodes = pickImportPolicyNodes(p, pickCatalogCtx);
        if (nodes.length === 0) continue;
        const rootNode: TradePolicyPolicyNode =
          nodes.length === 1 ? nodes[0] : { kind: "group", op: "and", children: nodes };
        const policy: TradePolicyPolicyPayload = { action: picksSimAction, root: rootNode };
        const prepared = preparePolicyForSimulate(policy);
        const simulatePolicy = prepared.policy as TradePolicyPolicyPayload;
        const body: TradePolicySimulateRequest = {
          policy: simulatePolicy,
          policy_row_version: effectivePolicyRowVersion,
          ...prepared.fields,
          simulate_ohlc_fill_missing: false,
          ...(effectiveBrokerAccountCode ? { broker_account_code: effectiveBrokerAccountCode } : {}),
          ...fields,
          ...(symbol.trim() ? { symbol: symbol.trim() } : {}),
          include_trade_ids: true,
          limit: limitValue,
          ...reportScopedSimulatePersistFields(effectiveSimulateReportId),
          title: nextRunTitle,
        };

        const runId = genEditorId();
        try {
          const started = await simulateTradePolicyStart(body);
          if (started && typeof started === "object" && "job_id" in (started as any)) {
            const jobId = String((started as any).job_id);
            const pending: ComparisonRun = normalizeComparisonRunTitleFields({
              id: runId,
              label: nextRunTitle,
              savedTitle: nextRunTitle,
              createdAt: Date.now(),
              policy: simulatePolicy,
              scope: scopeSnapshot,
              result: null,
              error: null,
              status: "running",
              jobId,
            });
            setComparisonRuns((prev) => [pending, ...prev].slice(0, MAX_COMPARISON_RUNS));
            void (async () => {
              try {
                const outcome = await waitForSimulateTradePolicyJob(jobId);
                setComparisonRuns((prev) =>
                  prev.map((r) =>
                r.id === runId
                  ? normalizeComparisonRunTitleFields({ ...r, result: outcome.result, status: "succeeded", error: null })
                  : r
              )
                );
                void persistReportSimulationAfterRun(runId, jobId);
              } catch (e) {
            if (e instanceof SimulateTradePolicyJobStillRunningError) {
              setComparisonRuns((prev) =>
                prev.map((r) => (r.id === runId ? { ...r, stillRunning: true, status: "running", error: null } : r))
              );
              return;
            }
                setComparisonRuns((prev) =>
                  prev.map((r) =>
                    r.id === runId
                      ? { ...r, result: null, status: "failed", error: e instanceof Error ? e.message : "Simulation failed." }
                      : r
                  )
                );
              }
            })();
          } else {
            const res = started as any;
            const pickSyncJobId = typeof res?.job_id === "string" ? String(res.job_id) : undefined;
            const newRun: ComparisonRun = normalizeComparisonRunTitleFields({
              id: runId,
              label: nextRunTitle,
              savedTitle: nextRunTitle,
              createdAt: Date.now(),
              policy,
              scope: scopeSnapshot,
              result: res,
              error: null,
              status: "succeeded",
              ...(pickSyncJobId ? { jobId: pickSyncJobId } : {}),
            });
            setComparisonRuns((prev) => [newRun, ...prev].slice(0, MAX_COMPARISON_RUNS));
            if (pickSyncJobId) void persistReportSimulationAfterRun(runId, pickSyncJobId);
          }
        } catch (e) {
          if (isPaywallRequiredError(e)) {
            redirectToBillingPaywall();
            return;
          }
          if (tryHandleMarketDataUnresolved(e)) {
            return;
          }
          const errRun: ComparisonRun = {
            id: runId,
            label: nextRunTitle,
            savedTitle: nextRunTitle,
            createdAt: Date.now(),
            policy,
            scope: scopeSnapshot,
            result: null,
            error: e instanceof Error ? e.message : "Simulation failed.",
            status: "failed",
          };
          setComparisonRuns((prev) => [errRun, ...prev].slice(0, MAX_COMPARISON_RUNS));
        }
      }

      toast.success("Queued pick runs", { description: "Added pick runs to the comparison list." });
      markPicksRanAndClearSelection(separatePicks);
      setPicksRunTitleDraft("");
    } finally {
      setImportingPicks(false);
      setPickRunLoadingAction(null);
    }
  }, [
    picksSimAction,
    buildSimulatorScopeAndWindowFields,
    picksRunTitleDraft,
    comparisonRuns.length,
    dateRange.endDate,
    dateRange.startDate,
    effectiveBrokerAccountCode,
    fieldsByPath,
    pickCatalogCtx,
    selectedImportablePicks,
    symbol,
    useCustomRange,
    effectiveSimulateReportId,
    persistReportSimulationAfterRun,
    effectivePolicyRowVersion,
    candleTimeframe,
    preparePolicyForSimulate,
    markLeafPicksRanAndClearSelection,
    combinedBundles,
    selectedBundleIds,
    bundlesBucketId,
    addBundleCard,
    updateBundleCard,
    incrementPickUsedInCombinedCount,
    incrementBundleUsedAsChildCount,
  ]);

  const queueCombinedPickRun = useCallback(async (opts?: {
    pickIds?: string[];
    action?: import("../../../types/policyPickOverlap").PolicyPickOverlapAction;
    combineOp?: TradePolicyGroupOp;
    skipCombineExplicitCheck?: boolean;
    /** When set, used instead of the picks run-title draft. */
    title?: string;
  }) => {
    const runAction = opts?.action ?? picksSimAction;
    if (runAction === null) {
      toast.info("Select action", { description: "Choose Remove or Keep only before running combined picks." });
      return;
    }
    const atomicLeafPicks = opts?.pickIds?.length
      ? importablePicks.filter((p) => opts.pickIds!.includes(p.id))
      : selectedImportablePicks;
    const atomicChildBundles = opts?.pickIds?.length
      ? combinedBundles.filter((b) => opts.pickIds!.includes(b.id))
      : combinedBundles.filter((b) => selectedBundleIds.has(b.id));

    const atomicChildIds = [
      ...atomicLeafPicks.map((p) => p.id),
      ...atomicChildBundles.map((b) => b.id),
    ];

    const combineOp = opts?.combineOp ?? combineSelectedOp;
    const skipCombineCheck = opts?.skipCombineExplicitCheck === true;

    if (atomicChildIds.length > 1 && !skipCombineCheck && !combineExplicitlySelected) {
      toast.info("Select combine", {
        description: "Choose Combine AND or Combine OR before running combined picks.",
      });
      return;
    }
    if (atomicChildIds.length === 0) {
      toast.info("No picks selected", {
        description: opts?.pickIds?.length
          ? "Overlap picks are not in your pick list — re-add them from Market and try again."
          : "Select two or more cards (single picks, executed singles, or combined runs).",
      });
      return;
    }

    const nextRunTitle = normalizeMandatorySimulatorRunTitle(
      opts?.title?.trim() ? opts.title : picksRunTitleDraft
    );
    if (!nextRunTitle) {
      toast.info("Enter a run title", { description: "Add a title for this run before simulating. It appears in results and is used when saving a simulation." });
      picksCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    const policySummaryFromTitle = (() => {
      const m =
        nextRunTitle.match(/^Overlap\s+#\d+\s*·\s*(.+)$/i) ??
        nextRunTitle.match(/^Overlap\s+suggestion\s+#\d+\s*·\s*(.+)$/i);
      return m?.[1]?.trim() || "";
    })();
    const policySummaryFromLeaves = [
      ...atomicLeafPicks.map((p) =>
        p.kind === "exit_placement" ? exitPlacementPickDisplayLabel(p) : p.label.trim()
      ),
      ...atomicChildBundles.map((b) => b.policySummary?.trim() || b.title?.trim() || b.id),
    ]
      .filter(Boolean)
      .join(" + ");
    const policySummary = policySummaryFromTitle || policySummaryFromLeaves || undefined;

    setPickRunLoadingAction("combined");
    setImportingPicks(true);
    try {
      const limitValue = DEFAULT_POLICY_LIMIT;

      if (useCustomRange) {
        if (!dateRange.startDate || !dateRange.endDate) {
          toast.warning("Date range required", { description: "Pick a start and end date, or switch to a timeframe preset." });
          return;
        }
      }

      const { fields, scope: scopeSnapshot } = buildSimulatorScopeAndWindowFields(limitValue);

      const pickByIdAll = new Map<string, ReportPick>();
      for (const p of picks) pickByIdAll.set(p.id, p);
      const bundleByIdAll = new Map<string, SimulatorPickBundleCard>();
      for (const b of combinedBundles) bundleByIdAll.set(b.id, b);

      const resolverDeps = {
        pickById: pickByIdAll,
        bundleById: bundleByIdAll,
        importPickPolicyNodes: (pick: ReportPick) => pickImportPolicyNodes(pick, pickCatalogCtx),
      };

      const combinedChildren: TradePolicyPolicyNode[] = [];
      for (const childId of atomicChildIds) {
        const node = resolveAtomicCardToPolicyNode(childId, resolverDeps, new Set<string>());
        if (node) combinedChildren.push(node);
      }

      if (combinedChildren.length === 0) {
        toast.info("No convertible picks", {
          description: "Selected cards could not be converted with the current catalog.",
        });
        return;
      }

      const combinedRoot: TradePolicyPolicyNode =
        combinedChildren.length === 1
          ? combinedChildren[0]
          : { kind: "group", op: combineOp, children: combinedChildren };

      const policy: TradePolicyPolicyPayload = { action: runAction, root: combinedRoot };
      const prepared = preparePolicyForSimulate(policy);
      const simulatePolicy = prepared.policy as TradePolicyPolicyPayload;
      const body: TradePolicySimulateRequest = {
        policy: simulatePolicy,
        policy_row_version: effectivePolicyRowVersion,
        ...prepared.fields,
        simulate_ohlc_fill_missing: false,
        ...(effectiveBrokerAccountCode ? { broker_account_code: effectiveBrokerAccountCode } : {}),
        ...fields,
        ...(symbol.trim() ? { symbol: symbol.trim() } : {}),
        include_trade_ids: true,
        limit: limitValue,
        ...reportScopedSimulatePersistFields(effectiveSimulateReportId),
        title: nextRunTitle,
      };

      const resolveBundleQuickStats = (res: any): PickQuickStats | null => {
        const metric = res?.filtered ?? res?.changed ?? res?.removed ?? res?.baseline;
        if (!metric) return null;
        const tradeCount = metric.trade_count;
        const totalPnl = metric.total_pnl;
        const winRate = metric.win_rate;
        const expectancy = metric.expectancy;
        const hasAny =
          (tradeCount != null && Number.isFinite(tradeCount)) ||
          (totalPnl != null && Number.isFinite(totalPnl)) ||
          (winRate != null && Number.isFinite(winRate)) ||
          (expectancy != null && Number.isFinite(expectancy));
        if (!hasAny) return null;
        return { tradeCount, totalPnl, winRate, expectancy };
      };

      const runId = genEditorId();
      const bundleCardId = genEditorId();
      const label = nextRunTitle;
      const started = await simulateTradePolicyStart(body);
      if (started && typeof started === "object" && "job_id" in (started as any)) {
        const jobId = String((started as any).job_id);
        const pending: ComparisonRun = {
          id: runId,
          label,
          savedTitle: nextRunTitle,
          createdAt: Date.now(),
          policy: simulatePolicy,
          scope: scopeSnapshot,
          result: null,
          error: null,
          status: "running",
          jobId,
        };
        setComparisonRuns((prev) => [pending, ...prev].slice(0, MAX_COMPARISON_RUNS));
        // Persist bundle card + increment immediate children usage counts.
        for (const p of atomicLeafPicks) {
          incrementPickUsedInCombinedCount(pickBucketId(p), p.id, 1);
        }
        for (const b of atomicChildBundles) {
          incrementBundleUsedAsChildCount(bundlesBucketId, b.id, 1);
        }
        addBundleCard({
          id: bundleCardId,
          createdAtMs: Date.now(),
          reportBucketId: bundlesBucketId,
          title: nextRunTitle,
          ...(policySummary ? { policySummary } : {}),
          children: atomicChildIds,
          combineOp,
          action: runAction,
          status: "running",
          comparisonRunId: runId,
          jobId,
          metrics: null,
          used_as_child_count: 0,
        });
        void (async () => {
          try {
            const outcome = await waitForSimulateTradePolicyJob(jobId);
            setComparisonRuns((prev) =>
              prev.map((r) =>
                r.id === runId
                  ? normalizeComparisonRunTitleFields({ ...r, result: outcome.result, status: "succeeded", error: null })
                  : r
              )
            );
            updateBundleCard(bundlesBucketId, bundleCardId, {
              status: "succeeded",
              metrics: resolveBundleQuickStats(outcome.result),
            });
            void persistReportSimulationAfterRun(runId, jobId);
          } catch (e) {
            if (e instanceof SimulateTradePolicyJobStillRunningError) {
              setComparisonRuns((prev) =>
                prev.map((r) => (r.id === runId ? { ...r, stillRunning: true, status: "running", error: null } : r))
              );
              return;
            }
            setComparisonRuns((prev) =>
              prev.map((r) =>
                r.id === runId
                  ? { ...r, result: null, status: "failed", error: e instanceof Error ? e.message : "Simulation failed." }
                  : r
              )
            );
            updateBundleCard(bundlesBucketId, bundleCardId, {
              status: "failed",
              metrics: null,
            });
          }
        })();
      } else {
        const res = started as any;
        const combinedSyncJobId = typeof res?.job_id === "string" ? String(res.job_id) : undefined;
        const newRun: ComparisonRun = {
          id: runId,
          label,
          savedTitle: nextRunTitle,
          createdAt: Date.now(),
          policy,
          scope: scopeSnapshot,
          result: res,
          error: null,
          status: "succeeded",
          ...(combinedSyncJobId ? { jobId: combinedSyncJobId } : {}),
        };
        setComparisonRuns((prev) => [newRun, ...prev].slice(0, MAX_COMPARISON_RUNS));
        if (combinedSyncJobId) void persistReportSimulationAfterRun(runId, combinedSyncJobId);

        // Persist bundle card + immediate child usage counters.
        for (const p of atomicLeafPicks) {
          incrementPickUsedInCombinedCount(pickBucketId(p), p.id, 1);
        }
        for (const b of atomicChildBundles) {
          incrementBundleUsedAsChildCount(bundlesBucketId, b.id, 1);
        }
        addBundleCard({
          id: bundleCardId,
          createdAtMs: Date.now(),
          reportBucketId: bundlesBucketId,
          title: nextRunTitle,
          ...(policySummary ? { policySummary } : {}),
          children: atomicChildIds,
          combineOp,
          action: runAction,
          status: "succeeded",
          comparisonRunId: runId,
          jobId: combinedSyncJobId,
          metrics: resolveBundleQuickStats(res),
          used_as_child_count: 0,
        });
      }
      toast.success("Ran combined picks", { description: "Added a combined run to the comparison list." });
      markLeafPicksRanAndClearSelection(atomicLeafPicks);
      setPicksRunTitleDraft("");
    } catch (e) {
      if (isPaywallRequiredError(e)) {
        redirectToBillingPaywall();
        return;
      }
      if (tryHandleMarketDataUnresolved(e)) {
        return;
      }
      presentMutationError(e, "Combined run failed");
    } finally {
      setImportingPicks(false);
      setPickRunLoadingAction(null);
    }
  }, [
    picksSimAction,
    combineExplicitlySelected,
    combineSelectedOp,
    buildSimulatorScopeAndWindowFields,
    picksRunTitleDraft,
    dateRange.endDate,
    dateRange.startDate,
    effectiveBrokerAccountCode,
    fieldsByPath,
    importablePicks,
    pickCatalogCtx,
    selectedImportablePicks,
    symbol,
    timeframe,
    useCustomRange,
    effectiveSimulateReportId,
    persistReportSimulationAfterRun,
    effectivePolicyRowVersion,
    candleTimeframe,
    preparePolicyForSimulate,
    markLeafPicksRanAndClearSelection,
    combinedBundles,
    selectedBundleIds,
    bundlesBucketId,
    addBundleCard,
    updateBundleCard,
    incrementPickUsedInCombinedCount,
    incrementBundleUsedAsChildCount,
  ]);

  const handleOverlapRunCombinedSim = useCallback(
    (pickIds: string[], action: import("../../../types/policyPickOverlap").PolicyPickOverlapAction) => {
      const bundleIdSet = new Set(combinedBundles.map((b) => b.id));
      const leafIds = pickIds.filter((id) => !bundleIdSet.has(id));
      const bundleIds = pickIds.filter((id) => bundleIdSet.has(id));
      setSelectedPickIds(new Set(leafIds));
      setSelectedBundleIds(new Set(bundleIds));
      setPicksSimAction(action);
      setCombineSelectedOp("or");
      setCombineExplicitlySelected(true);
      onCloseOverlapDialog();
      void queueCombinedPickRun({
        pickIds,
        action,
        combineOp: "or",
        skipCombineExplicitCheck: true,
      });
    },
    [combinedBundles, onCloseOverlapDialog, queueCombinedPickRun]
  );

  /** Run each selected Top suggestion as its own combined simulation. */
  const handleOverlapRunSuggestionSims = useCallback(
    (
      combos: Array<{
        pickIds: string[];
        action: import("../../../types/policyPickOverlap").PolicyPickOverlapAction;
        title: string;
      }>
    ) => {
      if (combos.length === 0) return;
      setCombineSelectedOp("or");
      setCombineExplicitlySelected(true);
      onCloseOverlapDialog();
      void (async () => {
        const bundleIdSet = new Set(combinedBundles.map((b) => b.id));
        const existingTitles = [
          ...combinedBundles.map((b) => b.title),
          ...combinedBundles.map((b) => b.policySummary),
          ...comparisonRuns.map((r) => r.savedTitle),
          ...comparisonRuns.map((r) => r.label),
        ];
        const serials = allocateOverlapSerials(existingTitles, combos.length);
        for (let i = 0; i < combos.length; i++) {
          const combo = combos[i]!;
          const serial = serials[i] ?? i + 1;
          const title = withOverlapSerial(combo.title, serial);
          const leafIds = combo.pickIds.filter((id) => !bundleIdSet.has(id));
          const bundleIds = combo.pickIds.filter((id) => bundleIdSet.has(id));
          setSelectedPickIds(new Set(leafIds));
          setSelectedBundleIds(new Set(bundleIds));
          setPicksSimAction(combo.action);
          await queueCombinedPickRun({
            pickIds: combo.pickIds,
            action: combo.action,
            combineOp: "or",
            skipCombineExplicitCheck: true,
            title,
          });
        }
      })();
    },
    [combinedBundles, comparisonRuns, onCloseOverlapDialog, queueCombinedPickRun]
  );

  /** Combined Remove+Keep policy: Default action is display-only Mixed, not a single veto/keep. */
  const builderActionsMixed = useMemo(
    () =>
      props.prefillPolicyTree != null && isMixedPolicySegmentActions(props.prefillPolicyTree),
    [props.prefillPolicyTree]
  );

  /** Stable roots for Builder/Graph — avoid remounting React Flow on unrelated state (run title). */
  const policyEditorDisplayRoots = useMemo(
    () => getPolicyEditorDisplayRoots(policyRoot),
    [policyRoot]
  );

  const onAddGroupToPolicyRoot = useCallback(() => {
    onAddGroup(policyRoot.id);
  }, [onAddGroup, policyRoot.id]);

  const builderActionMenuLabel = builderActionsMixed
    ? "Mixed actions"
    : simulatorActionLabel("builder", builderSimAction);

  const picksNextRunTitleReady = useMemo(
    () => normalizeMandatorySimulatorRunTitle(picksRunTitleDraft).length > 0,
    [picksRunTitleDraft]
  );

  const picksComparisonAtLimit = comparisonRuns.length >= MAX_COMPARISON_RUNS;

  const simulateInflightCount = useMemo(
    () => comparisonRuns.filter((run) => run.status === "running").length,
    [comparisonRuns]
  );

  const picksSeparateRunDisabled =
    importingPicks ||
    !selectedImportableLaneModes.hasSingleOrRan ||
    selectedImportablePicks.length === 0 ||
    picksSimAction === null ||
    !picksNextRunTitleReady ||
    picksComparisonAtLimit;

  const picksCombinedAtomicCount = selectedImportablePicks.length + selectedBundleIds.size;

  const picksCombinedRunDisabled =
    importingPicks ||
    picksCombinedAtomicCount < 2 ||
    picksSimAction === null ||
    !combineExplicitlySelected ||
    !picksNextRunTitleReady ||
    picksComparisonAtLimit;

  const exitPlacementSeparateRunDisabled =
    simulateInflightCount >= MAX_SIMULATE_INFLIGHT ||
    !selectedExitPlacementLaneModes.hasSingleOrRan ||
    selectedExitPlacementPicks.length === 0 ||
    picksComparisonAtLimit;

  const exitPlacementCombinedAtomicCount = selectedExitPlacementPicks.length + selectedBundleIds.size;

  const exitPlacementCombinedRunDisabled =
    simulateInflightCount >= MAX_SIMULATE_INFLIGHT ||
    exitPlacementCombinedAtomicCount < 2 ||
    picksComparisonAtLimit;

  const builderNextRunTitleReady = useMemo(
    () => normalizeMandatorySimulatorRunTitle(builderRunTitleDraft).length > 0,
    [builderRunTitleDraft]
  );

  const builderPolicyRunnable = useMemo(() => {
    const built = buildSimulatePolicy(policyRoot, defaultGroupAction, fieldsByPath, maxDepth, maxNodes);
    if (!built.ok) return false;
    return Boolean(withPrefillManagement(built.policy));
  }, [defaultGroupAction, fieldsByPath, maxDepth, maxNodes, policyRoot, withPrefillManagement]);

  const builderRunDisabled =
    simLoading ||
    simulateRateLimit.isCoolingDown ||
    simulateInflightCount >= MAX_SIMULATE_INFLIGHT ||
    catalogLoading ||
    catalogFields.length === 0 ||
    !builderPolicyRunnable ||
    !builderNextRunTitleReady ||
    picksComparisonAtLimit ||
    candleBudgetExceeds;

  const clearPolicyLabel = clearSimulatorPolicyControlLabel(policyRoot);
  const canClearPolicy = policyRoot.kind === "group" && (policyRoot.children.length > 0 || ruleLeaves > 0);

  const useSimulatorWizardLayout = Boolean(props.simulatorWizardLayout);
  const [simulatorWizardStep, setSimulatorWizardStep] = useState(() => {
    const raw = props.initialSimulatorWizardStep;
    if (typeof raw !== "number" || !Number.isFinite(raw)) return 0;
    return Math.max(0, Math.min(2, Math.floor(raw)));
  });
  const [wizardGenerateArmed, setWizardGenerateArmed] = useState(false);
  wizardGenerateGateRef.current.step = simulatorWizardStep;
  wizardGenerateGateRef.current.armed = wizardGenerateArmed;

  useEffect(() => {
    setWizardGenerateArmed(false);
    if (simulatorWizardStep !== 2) return;
    // Arm after the activating click (Continue) has fully settled.
    const timer = window.setTimeout(() => setWizardGenerateArmed(true), 400);
    return () => window.clearTimeout(timer);
  }, [simulatorWizardStep]);

  const simulatorWizardCardBg = isDarkTheme ? "var(--sidebar)" : "white";
  const simulatorWizardCardShadow = isDarkTheme
    ? "0 20px 40px rgba(0, 0, 0, 0.3)"
    : "0 20px 40px rgba(15, 23, 42, 0.06)";

  const exitSimulatorWizard = useCallback(() => {
    if (props.onWizardExit) {
      props.onWizardExit();
      return;
    }
    if (props.wizardExitHref) history.push(props.wizardExitHref);
  }, [history, props.onWizardExit, props.wizardExitHref]);

  const simulatorWizardCanContinueScope = useMemo(
    () =>
      Boolean(effectiveBrokerAccountCode.trim()) &&
      hasUsableTradeWindowForSimulate(dateRange, timeframe, reportingTimeZone) &&
      candleBudgetPreviewSettled &&
      !candleBudgetExceeds &&
      !wizardMdGate.runBlocked,
    [
      candleBudgetExceeds,
      candleBudgetPreviewSettled,
      dateRange,
      effectiveBrokerAccountCode,
      reportingTimeZone,
      timeframe,
      wizardMdGate.runBlocked,
    ]
  );

  const simulatorWizardPolicyStepComplete = useMemo(() => {
    if (catalogLoading || catalogFields.length === 0) return false;
    if (!normalizeMandatorySimulatorRunTitle(builderRunTitleDraft)) return false;
    const built = buildSimulatePolicy(policyRoot, defaultGroupAction, fieldsByPath, maxDepth, maxNodes);
    return built.ok;
  }, [
    builderRunTitleDraft,
    catalogFields.length,
    catalogLoading,
    defaultGroupAction,
    fieldsByPath,
    maxDepth,
    maxNodes,
    policyRoot,
  ]);

  /** Policy-step alert: only real blockers (no catalog / invalid policy), not “form not filled yet”. */
  const simulatorWizardPolicyContinueBlockedReason = useMemo(() => {
    if (simulatorWizardStep !== 1) return null;
    if (catalogLoading) return null;
    if (catalogFields.length === 0) {
      return "No filter fields are available. Try again later.";
    }
    if (!normalizeMandatorySimulatorRunTitle(builderRunTitleDraft)) return null;
    const built = buildSimulatePolicy(policyRoot, defaultGroupAction, fieldsByPath, maxDepth, maxNodes);
    return built.ok ? null : built.reason;
  }, [
    simulatorWizardStep,
    catalogLoading,
    catalogFields.length,
    defaultGroupAction,
    builderRunTitleDraft,
    policyRoot,
    fieldsByPath,
    maxDepth,
    maxNodes,
  ]);

  const simulatorWizardGoNext = useCallback(() => {
    if (simulatorWizardStep === 0) {
      if (!effectiveBrokerAccountCode.trim()) {
        toast.warning("Select broker account", { description: "Choose which trade account to run this simulation against." });
        return;
      }
      if (!hasUsableTradeWindowForSimulate(dateRange, timeframe, reportingTimeZone)) {
        toast.warning("Date range required", { description: "Pick a start and end date in the date control, or choose a quick range (e.g. Last 7 days) so a simulation window is defined." });
        return;
      }
      if (!candleBudgetPreviewSettled) {
        toast.info("Checking candle budget", {
          description: "Wait until the candle bar budget check finishes before continuing.",
        });
        return;
      }
      if (candleBudgetExceeds) {
        toast.warning("Candle budget exceeded", {
          description: "Shorten the date window or pick a suggested period before continuing.",
        });
        return;
      }
      // Defer step change so this click cannot land on the next footer button.
      window.setTimeout(() => setSimulatorWizardStep(1), 0);
      return;
    }
    if (simulatorWizardStep === 1) {
      if (!normalizeMandatorySimulatorRunTitle(builderRunTitleDraft)) {
        toast.info("Enter a run title", { description: "Add a title for this run before continuing." });
        return;
      }
      const built = buildSimulatePolicy(policyRoot, defaultGroupAction, fieldsByPath, maxDepth, maxNodes);
      if (!built.ok) {
        toast.warning("Check your policy", { description: built.reason });
        return;
      }
      // Defer so Continue → Generate button swap cannot receive the same click.
      window.setTimeout(() => setSimulatorWizardStep(2), 0);
    }
  }, [
    simulatorWizardStep,
    effectiveBrokerAccountCode,
    dateRange,
    timeframe,
    reportingTimeZone,
    candleBudgetPreviewSettled,
    candleBudgetExceeds,
    defaultGroupAction,
    builderRunTitleDraft,
    policyRoot,
    fieldsByPath,
    maxDepth,
    maxNodes,
  ]);

  const simulatorWizardGoBack = useCallback(() => {
    if (simulatorWizardStep <= 0) return;
    setSimulatorWizardStep((s) => Math.max(0, s - 1));
  }, [simulatorWizardStep]);

  const simulatorWizardOnStepClick = useCallback((idx: number) => {
    if (idx < simulatorWizardStep) setSimulatorWizardStep(idx);
  }, [simulatorWizardStep]);

  const simulatorWizardContinueDisabled =
    simulatorWizardStep === 0
      ? !simulatorWizardCanContinueScope
      : simulatorWizardStep === 1
        ? !simulatorWizardPolicyStepComplete
        : true;

  const formatSimReviewLocalDate = useCallback((d: Date | null) => {
    if (!d) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }, []);

  const handleSimulatorScopeDateRangeChange = useCallback(
    (next: DateRange, options?: DashboardDateRangeChangeOptions) => {
      if (allowFreeWindowOutsideReport && windowSource === "report") {
        setWindowSource("custom");
      }
      setDateRange(next);
      if (options?.presetId) {
        setTimeframe(options.presetId);
        return;
      }
      if (!next.startDate && !next.endDate) {
        setTimeframe("all-time");
      }
    },
    [allowFreeWindowOutsideReport, windowSource]
  );

  const simulatorScopeWindowSummaryLabel = useMemo(() => {
    if (dateRange.startDate && dateRange.endDate) {
      const start = formatSimReviewLocalDate(dateRange.startDate);
      const end = formatSimReviewLocalDate(dateRange.endDate);
      return start === end ? start : `${start} – ${end}`;
    }
    if (!dateRange.startDate && !dateRange.endDate) {
      return formatReportWindowLabelForDisplay({ timeframe }) ?? "All time";
    }
    return "Select dates";
  }, [dateRange.endDate, dateRange.startDate, formatSimReviewLocalDate, timeframe]);

  /** Stepped wizard scope step — same field chrome as generate-report (not the old Chakra card). */
  const simulatorWizardDialogScope = Boolean(props.simulatorWizardLayout);

  const simulatorDatePickerSelectedPreset = useMemo((): DashboardDatePresetTimeframe | null => {
    if (dateRange.startDate || dateRange.endDate) return null;
    if (
      timeframe === "today" ||
      timeframe === "7-days" ||
      timeframe === "14-days" ||
      timeframe === "30-days" ||
      timeframe === "all-time" ||
      timeframe === "this-week" ||
      timeframe === "this-month" ||
      timeframe === "this-year"
    ) {
      return timeframe as DashboardDatePresetTimeframe;
    }
    return "all-time";
  }, [dateRange.endDate, dateRange.startDate, timeframe]);

  const simulatorScopeDateRangeControl = (
    <div data-simulator-wizard-date-range="" className="w-full min-w-0">
      <DashboardHeaderDateRangePicker
        dateRange={dateRange}
        selectedPresetId={simulatorDatePickerSelectedPreset}
        onDateRangeChange={handleSimulatorScopeDateRangeChange}
        className="w-full"
        triggerSurface="field"
      />
    </div>
  );

  const scopeFieldsBox =
    props.hideScopeControls ? null : simulatorWizardDialogScope ? (
      <div className="grid w-full min-w-0 grid-cols-1 gap-5 sm:grid-cols-2">
        <SimulatorWizardScopeField
          label="Broker account"
          required={isStandalone}
          description={
            brokerAccountLocked
              ? "Locked to the workspace / executing account for this run."
              : "Choose which trade account to run this simulation against."
          }
        >
          {brokerAccountLocked ? (
            <div
              data-simulator-locked-broker-account=""
              className="flex h-9 w-full min-w-0 items-center rounded-[6px] border border-border bg-content-canvas px-3 font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-sidebar-muted-foreground"
            >
              {brokerAccountMenuLabel}
            </div>
          ) : (
            <TradeFilterSelectFullWidth
              value={effectiveBrokerAccountCode}
              onValueChange={setStandaloneBrokerCode}
              options={brokerAccountMenuOptions}
              disabled={
                brokerAccountsLoading ||
                brokerAccountMenuOptions.length === 0 ||
                !isStandalone
              }
              placeholder={
                brokerAccountsLoading
                  ? "Loading accounts…"
                  : brokerAccountMenuOptions.length === 0
                    ? "No trade accounts"
                    : "Select account"
              }
              emptyValueShowsPlaceholder
            />
          )}
        </SimulatorWizardScopeField>

        <SimulatorWizardScopeField
          label="Date range"
          description={
            allowFreeWindowOutsideReport
              ? windowSource === "report"
                ? "Uses this policy’s adopted report cohort."
                : "Any date range on this account; not limited to the report."
              : "Choose a preset or custom window for this simulation."
          }
        >
          {allowFreeWindowOutsideReport ? (
            <div
              data-simulator-window-source=""
              className="grid h-9 w-full grid-cols-2 gap-1 rounded-[6px] border border-border bg-content-canvas p-0.5"
              role="tablist"
              aria-label="Simulation window source"
            >
              <button
                type="button"
                role="tab"
                aria-selected={windowSource === "report"}
                className={
                  windowSource === "report"
                    ? "rounded-[4px] bg-background px-2 text-sm font-medium text-foreground shadow-sm"
                    : "rounded-[4px] px-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                }
                onClick={() => selectWindowSource("report")}
              >
                Report window
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={windowSource === "custom"}
                className={
                  windowSource === "custom"
                    ? "rounded-[4px] bg-background px-2 text-sm font-medium text-foreground shadow-sm"
                    : "rounded-[4px] px-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                }
                onClick={() => selectWindowSource("custom")}
              >
                Custom window
              </button>
            </div>
          ) : null}
          {simulatorScopeDateRangeControl}
        </SimulatorWizardScopeField>

        <SimulatorWizardScopeField
          label="Symbol"
          description="Optional. Leave blank to include all symbols in the window."
        >
          <TradeFilterSelectFullWidth
            value={symbol}
            onValueChange={setSymbol}
            options={[
              { value: "", label: "All symbols" },
              ...availableInstruments.map((row) => ({
                value: row.symbol,
                label: formatAvailableInstrumentOptionLabel(row),
              })),
            ]}
            disabled={instrumentsLoading && availableInstruments.length === 0}
            placeholder={instrumentsLoading ? "Loading symbols…" : "All symbols"}
            emptyValueShowsPlaceholder
          />
        </SimulatorWizardScopeField>

        <SimulatorWizardScopeField
          label="Candle timeframe"
          description={
            candleTimeframeLocked
              ? "Locked to the finest candle size required by this policy. Shorten the date window if over the bar budget."
              : "Bar size used when evaluating candle-based policy rules."
          }
        >
          <TradeFilterSelectFullWidth
            value={candleTimeframe}
            onValueChange={(v) =>
              setCandleTimeframe(v as "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d")
            }
            disabled={candleTimeframeLocked}
            options={[
              { value: "1m", label: "1m" },
              { value: "5m", label: "5m" },
              { value: "15m", label: "15m" },
              { value: "30m", label: "30m" },
              { value: "1h", label: "1h" },
              { value: "4h", label: "4h" },
              { value: "1d", label: "1d" },
            ]}
          />
        </SimulatorWizardScopeField>

        <SimulatorCandleBudgetGate
          candleTimeframe={candleTimeframe}
          windowFields={candleBudgetWindowFields}
          symbol={symbol}
          brokerAccountCode={effectiveBrokerAccountCode}
          reportId={effectiveSimulateReportId}
          candleTimeframeLocked={candleTimeframeLocked}
          entryCandleTimeframe={dualCandleBudgetFields.entry_candle_timeframe}
          managementCandleTimeframe={dualCandleBudgetFields.management_candle_timeframe}
          activeLocalStart={candleBudgetWindowFields.local_start_date ?? null}
          activeLocalEnd={candleBudgetWindowFields.local_end_date ?? null}
          onSelectPeriod={handleSelectCandleBudgetPeriod}
          onExceedsChange={setCandleBudgetExceeds}
          onPreviewSettledChange={setCandleBudgetPreviewSettled}
        />

        {wizardMdGate.connectGate ? (
          <div className="sm:col-span-2">
            <WizardMarketDataOhlcGateNotice
              gate={wizardMdGate.connectGate}
              history={history}
              onOpenMarketDataSetup={() => wizardMdGate.openMarketDataSetup()}
            />
          </div>
        ) : null}

        {isStandalone ? (
          <SimulatorWizardScopeField
            label="Strategy"
            className="sm:col-span-2"
            description={
              strategyPickerLocked
                ? "Locked to the strategy this policy belongs to."
                : "When set, you can save this run as a policy version on that strategy from the results page."
            }
          >
            <TradeFilterSelectFullWidth
              value={standaloneLinkedStrategyId}
              onValueChange={setStandaloneLinkedStrategyId}
              options={[
                ...(strategyPickerLocked ? [] : [{ value: "", label: "No strategy" }]),
                ...strategyOptions.map((s) => ({ value: s.id, label: s.name })),
                ...(strategyPickerLocked &&
                standaloneLinkedStrategyId &&
                !strategyOptions.some((s) => s.id === standaloneLinkedStrategyId)
                  ? [{ value: standaloneLinkedStrategyId, label: "Linked strategy" }]
                  : []),
              ]}
              disabled={strategiesLoading || strategyPickerLocked}
              placeholder={strategiesLoading ? "Loading strategies…" : "No strategy"}
              emptyValueShowsPlaceholder
            />
          </SimulatorWizardScopeField>
        ) : null}
      </div>
    ) : (
      <Box borderWidth="1px" borderRadius="lg" borderColor={borderColor} p={{ base: 3, md: 4 }}>
        <SimpleGrid
          w="100%"
          templateColumns={{
            base: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          }}
          columnGap={{ base: 3, md: 4 }}
          rowGap={4}
          alignItems="end"
        >
          <FormControl isRequired={isStandalone} minW={0} w="100%">
            <FormLabel
              color={subtleText}
              fontSize="xs"
              textTransform="uppercase"
              mb={1}
              requiredIndicator={
                isStandalone ? (
                  <Text as="span" color={TradeLogLossRed} ml={0.5} aria-hidden>
                    *
                  </Text>
                ) : undefined
              }
            >
              Broker account
            </FormLabel>
            {isStandalone ? (
              <ConfiguratorStyleMenu
                w="100%"
                triggerWidth="full"
                borderRadius={scopeMenuRadius}
                currentLabel={brokerAccountMenuLabel}
                options={brokerAccountMenuOptions}
                isDisabled={brokerAccountsLoading || brokerAccountMenuOptions.length === 0}
                onSelect={(value) => setStandaloneBrokerCode(value)}
              />
            ) : (
              <Input value={effectiveBrokerAccountCode} isReadOnly placeholder="Select a trade account" />
            )}
          </FormControl>

          <FormControl minW={0} w="100%">
            <Text
              as="label"
              display="block"
              color={subtleText}
              fontSize="xs"
              fontWeight="medium"
              fontFamily="'IBM Plex Sans', sans-serif"
              textTransform="uppercase"
              letterSpacing="0.02em"
              lineHeight="1"
              mb={1}
            >
              Date range
            </Text>
            {simulatorScopeDateRangeControl}
          </FormControl>

          <FormControl minW={0} w="100%">
            <Text
              as="label"
              display="block"
              color={subtleText}
              fontSize="xs"
              fontWeight="medium"
              fontFamily="'IBM Plex Sans', sans-serif"
              textTransform="uppercase"
              letterSpacing="0.02em"
              lineHeight="1"
              mb={1}
            >
              Symbol (optional)
            </Text>
            <Input
              {...scopeInputProps}
              w="100%"
              value={symbol}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSymbol(e.target.value)}
              placeholder="e.g. BTCUSD"
              borderRadius={scopeMenuRadius}
            />
          </FormControl>

          <FormControl minW={0} w="100%">
            <Text
              as="label"
              display="block"
              color={subtleText}
              fontSize="xs"
              fontWeight="medium"
              fontFamily="'IBM Plex Sans', sans-serif"
              textTransform="uppercase"
              letterSpacing="0.02em"
              lineHeight="1"
              mb={1}
            >
              Candle timeframe
            </Text>
            <ConfiguratorStyleMenu
              w="100%"
              triggerWidth="full"
              borderRadius={scopeMenuRadius}
              currentLabel={candleTimeframe}
              isDisabled={candleTimeframeLocked}
              options={[
                { value: "1m", label: "1m" },
                { value: "5m", label: "5m" },
                { value: "15m", label: "15m" },
                { value: "30m", label: "30m" },
                { value: "1h", label: "1h" },
                { value: "4h", label: "4h" },
                { value: "1d", label: "1d" },
              ]}
              onSelect={(v) =>
                setCandleTimeframe(v as "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d")
              }
            />
          </FormControl>
        </SimpleGrid>
        <Box mt={3}>
          <SimulatorCandleBudgetGate
            candleTimeframe={candleTimeframe}
            windowFields={candleBudgetWindowFields}
            symbol={symbol}
            brokerAccountCode={effectiveBrokerAccountCode}
            reportId={effectiveSimulateReportId}
            candleTimeframeLocked={candleTimeframeLocked}
            entryCandleTimeframe={dualCandleBudgetFields.entry_candle_timeframe}
            managementCandleTimeframe={dualCandleBudgetFields.management_candle_timeframe}
            activeLocalStart={candleBudgetWindowFields.local_start_date ?? null}
            activeLocalEnd={candleBudgetWindowFields.local_end_date ?? null}
            onSelectPeriod={handleSelectCandleBudgetPeriod}
            onExceedsChange={setCandleBudgetExceeds}
            onPreviewSettledChange={setCandleBudgetPreviewSettled}
          />
        </Box>
        {isStandalone ? (
          <FormControl mt={4} minW={0} w="100%">
            <FormLabel color={subtleText} fontSize="xs" textTransform="uppercase" mb={1}>
              Strategy (optional)
            </FormLabel>
            <ConfiguratorStyleMenu
              w="100%"
              triggerWidth="full"
              borderRadius={scopeMenuRadius}
              currentLabel={strategiesLoading ? "Loading strategies…" : linkedStrategyMenuLabel}
              options={[
                { value: "", label: "No strategy" },
                ...strategyOptions.map((s) => ({ value: s.id, label: s.name })),
              ]}
              isDisabled={strategiesLoading}
              onSelect={(value) => setStandaloneLinkedStrategyId(value)}
            />
            <FormHelperText color={subtleText} fontSize="xs" mt={1}>
              When set, you can save this run as a policy version on that strategy from the results page.
            </FormHelperText>
          </FormControl>
        ) : null}
      </Box>
    );

  const v2PolicyParsed = useMemo((): PolicyTreeV2 | null => {
    if (!isManagementV2Mode) return null;
    try {
      return JSON.parse(v2PolicyDraft) as PolicyTreeV2;
    } catch {
      return null;
    }
  }, [isManagementV2Mode, v2PolicyDraft]);

  const builderLegAvailability = useMemo(
    () => legAvailabilityFromPolicyRules(v2PolicyParsed?.rules),
    [v2PolicyParsed?.rules]
  );

  const simulateLegAvailability = useMemo(
    () =>
      v2PolicyParsed?.rules?.length
        ? builderLegAvailability
        : exitPlacementSimLegAvailabilityForUi,
    [builderLegAvailability, exitPlacementSimLegAvailabilityForUi, v2PolicyParsed?.rules?.length]
  );

  useEffect(() => {
    setExitPlacementSimulateLegs((prev) => {
      const next = clampSimulateLegs(prev, simulateLegAvailability);
      exitPlacementSimulateLegsRef.current = next;
      return next;
    });
  }, [simulateLegAvailability.hasSl, simulateLegAvailability.hasTp]);

  const exitPlacementRuleLabels = useMemo(
    () => exitPlacementRuleLabelsFromPicks(picks),
    [picks]
  );

  const v2RuleConditionDisplayNodes = useMemo((): EditorNode[][] => {
    if (!v2PolicyParsed?.rules?.length) return [];
    return v2PolicyParsed.rules.map((rule) => {
      const root = parsePolicyTreeToEditorRoot({ root: rule.condition }, fieldsByPath);
      if (!root) return [];
      return getPolicyViewDisplayNodes(root);
    });
  }, [fieldsByPath, v2PolicyParsed]);

  const v2AllPolicyNodeIds = useMemo(() => {
    const out: string[] = [];
    const walk = (node: EditorNode) => {
      out.push(node.id);
      if (node.kind === "group") {
        for (const child of node.children) walk(child);
      }
    };
    for (const displayNodes of v2RuleConditionDisplayNodes) {
      for (const node of displayNodes) walk(node);
    }
    return out;
  }, [v2RuleConditionDisplayNodes]);

  const v2AllPolicyGroupsExpanded = useMemo(
    () => v2AllPolicyNodeIds.length > 0 && v2AllPolicyNodeIds.every((id) => !collapsedGroupIds.has(id)),
    [collapsedGroupIds, v2AllPolicyNodeIds]
  );

  const appendManagementV2ComparisonRun = useCallback(
    (
      sliceIds: string[],
      title: string,
      fallbackPolicy?: PolicyTreeV2 | null
    ): string => {
      if (!scopedReportId) {
        throw new Error("Report required");
      }

      const draftPolicyRaw = parseExitPlacementPolicyDraft(v2PolicyDraftRef.current);
      const draftPolicy = draftPolicyRaw
        ? migratePresetRulesToDetachedPolicy(draftPolicyRaw, picks)
        : null;
      if (draftPolicy && draftPolicy !== draftPolicyRaw) {
        commitV2PolicyDraft(draftPolicy);
      }

      const policyForRun = resolveExitPlacementPolicyForSimulate({
        draft: draftPolicy,
        picks,
        sliceIds,
        fallbackPolicy,
      });
      if (!policyForRun?.rules?.length) {
        throw new Error("Policy tree is empty. Insert picks and edit SL/TP distances before simulating.");
      }
      const legsForRun = clampSimulateLegs(
        exitPlacementSimulateLegsRef.current,
        legAvailabilityFromPolicyRules(policyForRun.rules)
      );
      const simParams = {
        reportId: scopedReportId,
        sliceIds,
        policy: policyForRun,
        title,
        symbol: symbol.trim() || undefined,
        simulateLegs: legsForRun,
        ...(props.workspaceSimulationContext
          ? {
              workspaceId: props.workspaceSimulationContext.workspaceId,
              parentNodeId: props.workspaceSimulationContext.parentNodeId,
              ...(props.workspaceSimulationContext.position
                ? { position: props.workspaceSimulationContext.position }
                : {}),
              ...(props.workspaceSimulationContext.meta
                ? { meta: props.workspaceSimulationContext.meta }
                : {}),
            }
          : {}),
      };

      const { scope } = buildSimulatorScopeAndWindowFields(10_000);
      const runId = safeRandomUUID();
      const titleFields = comparisonRunTitlesFromPersistedTitle(title);
      const optimisticRun: ComparisonRun = {
        id: runId,
        label: titleFields.label,
        savedTitle: titleFields.savedTitle,
        ...(titleFields.titleParseMode ? { titleParseMode: titleFields.titleParseMode } : {}),
        createdAt: Date.now(),
        policy: policyForRun,
        scope,
        result: null,
        error: null,
        status: "running",
        ...(linkedStrategyIdForNewRuns?.trim()
          ? { linkedStrategyId: linkedStrategyIdForNewRuns.trim() }
          : {}),
        simulateLegs: legsForRun,
      };

      let accepted = true;
      setComparisonRuns((prev) => {
        if (prev.filter((run) => run.status === "running").length >= MAX_SIMULATE_INFLIGHT) {
          accepted = false;
          return prev;
        }
        return [optimisticRun, ...prev].slice(0, MAX_COMPARISON_RUNS);
      });
      if (!accepted) {
        throw new Error(
          `Wait for a running simulation to finish (max ${MAX_SIMULATE_INFLIGHT} at a time).`
        );
      }

      void (async () => {
        let start: ExitPlacementSimulationStart;
        try {
          start = await startExitPlacementSimulation(simParams);
        } catch (e) {
          const message =
            e instanceof Error ? e.message : "Could not start management simulation.";
          setComparisonRuns((prev) =>
            prev.map((r) =>
              r.id === runId ? { ...r, status: "failed", error: message } : r
            )
          );
          if (shouldSkipSimulateFailureToast(e)) return;
          presentMutationError(e, "Simulation failed");
          return;
        }

        setComparisonRuns((prev) =>
          prev.map((r) =>
            r.id === runId
              ? {
                  ...r,
                  jobId: start.jobId,
                  ...(start.backtestId
                    ? { backtestId: start.backtestId, isSavedBacktest: true }
                    : {}),
                  ...(start.simulatedLegs ? { simulatedLegs: start.simulatedLegs } : {}),
                  ...(start.exitPlacementSlices
                    ? { exitPlacementSlices: start.exitPlacementSlices }
                    : {}),
                  ...(exitPlacementSimMetricsReady(start.result) ? { result: start.result } : {}),
                }
              : r
          )
        );

        try {
          const result = await completeExitPlacementSimulation(simParams, start);
          setComparisonRuns((prev) =>
            prev.map((r) =>
              r.id === runId
                ? normalizeComparisonRunTitleFields({
                    ...r,
                    result: result.result,
                    status: "succeeded",
                    error: null,
                    stillRunning: false,
                    jobId: result.jobId,
                    backtestId: result.backtestId,
                    isSavedBacktest: Boolean(result.backtestId),
                    policy: result.policy,
                    ...(result.simulatedLegs ? { simulatedLegs: result.simulatedLegs } : {}),
                    ...(result.exitPlacementSlices
                      ? { exitPlacementSlices: result.exitPlacementSlices }
                      : {}),
                  })
                : r
            )
          );
          setLinkedBacktestsRefreshKey((k) => k + 1);
          props.onSimulateComplete?.(result);
          if (result.backtestId) {
            props.onBacktestSaved?.(result.backtestId);
          }
        } catch (e) {
          if (e instanceof SimulateTradePolicyJobStillRunningError) {
            setComparisonRuns((prev) =>
              prev.map((r) =>
                r.id === runId ? { ...r, stillRunning: true, status: "running", error: null } : r
              )
            );
            return;
          }
          const message = e instanceof Error ? e.message : "Simulation failed.";
          setComparisonRuns((prev) =>
            prev.map((r) =>
              r.id === runId
                ? {
                    ...r,
                    status: "failed",
                    error: message,
                    stillRunning: false,
                  }
                : r
            )
          );
          if (shouldSkipSimulateFailureToast(e)) return;
          presentMutationError(e, "Simulation failed");
        }
      })();

      return runId;
    },
    [
      buildSimulatorScopeAndWindowFields,
      picks,
      props,
      scopedReportId,
      symbol,
      linkedStrategyIdForNewRuns,
      commitV2PolicyDraft,
    ]
  );

  const runManagementV2Simulation = useCallback(() => {
    if (!scopedReportId) {
      toast.warning("Report required", { description: "Trade management simulation must run in a report context." });
      return;
    }
    const policyFromDraft = parseExitPlacementPolicyDraft(v2PolicyDraftRef.current);
    const migratedDraft = policyFromDraft
      ? migratePresetRulesToDetachedPolicy(policyFromDraft, picks)
      : null;
    if (migratedDraft && migratedDraft !== policyFromDraft) {
      commitV2PolicyDraft(migratedDraft);
    }
    if (!migratedDraft?.rules?.length) {
      toast.info("Policy tree is empty", {
        description: "Select picks and use Insert into policy tree, or run simulations directly from picks above.",
      });
      return;
    }

    const sliceIds = sliceIdsForPolicyTreeRules(migratedDraft, picks);
    if (sliceIds.length === 0) {
      toast.info("No matching picks for policy", {
        description: "Policy rules must come from simulator picks so slice ids can be resolved for simulation.",
      });
      return;
    }

    const runTitle = normalizeMandatorySimulatorRunTitle(builderRunTitleDraft);
    if (!runTitle) {
      toast.info("Enter a run title", {
        description: "Add a title for this run before simulating. It appears in results and is used when saving a simulation.",
      });
      return;
    }

    setExitPlacementSimLegError(null);
    try {
      appendManagementV2ComparisonRun(sliceIds, runTitle);
      toast.success("Simulation started", {
        description: "Added to comparison. Results appear when the job finishes.",
      });
      setBuilderRunTitleDraft("");
    } catch (e) {
      const description =
        e instanceof ReportsApiError
          ? e.detail?.trim() || e.message
          : e instanceof Error
            ? e.message
            : "Could not run management simulation.";
      if (e instanceof ReportsApiError && e.status === 400) {
        setExitPlacementSimLegError(description);
      }
      if (shouldSkipSimulateFailureToast(e)) return;
      presentMutationError(e, "Simulation failed");
    }
  }, [
    appendManagementV2ComparisonRun,
    builderRunTitleDraft,
    commitV2PolicyDraft,
    picks,
    scopedReportId,
  ]);

  const queueExitPlacementPickComparisonRuns = useCallback(
    (runTitleRaw: string) => {
    if (!scopedReportId) {
      toast.warning("Report required", { description: "Exit placement simulation must run in a report context." });
      return;
    }
    const separatePicks = selectedExitPlacementPicks.filter((p) => getSimulatorLane(p) !== "combined");
    if (separatePicks.length === 0) {
      toast.info("No picks selected", {
        description: "Select one or more contexts from the Single lane to run separately.",
      });
      return;
    }
    if (comparisonRuns.length >= MAX_COMPARISON_RUNS) {
      toastSimulatorComparisonAtLimit(scopedReportId);
      return;
    }

    const nextRunTitle = normalizeMandatorySimulatorRunTitle(runTitleRaw);
    if (!nextRunTitle) {
      toast.info("Enter a run title", {
        description: "Add a title for this run before simulating. It appears in results and is used when saving a simulation.",
      });
      return;
    }

    setExitPlacementSimLegError(null);
    let started = 0;
    const startedPicks: ReportPick[] = [];
    try {
      for (const pick of separatePicks) {
        if (comparisonRuns.length + started >= MAX_COMPARISON_RUNS) break;
        const sliceId = sliceIdFromExitPlacementPick(pick);
        if (!sliceId) continue;
        const pickLabel = exitPlacementPickDisplayLabel(pick);
        const titleForApi =
          separatePicks.length > 1 ? `${nextRunTitle} · ${pickLabel}` : nextRunTitle;
        const draftPolicyRaw = parseExitPlacementPolicyDraft(v2PolicyDraftRef.current);
        const draftPolicy = draftPolicyRaw
          ? migratePresetRulesToDetachedPolicy(draftPolicyRaw, picks)
          : null;
        const pickRule = simulationRuleFromExitPlacementPick(pick);
        const hasEditedRule =
          draftPolicy != null && Boolean(findPolicyTreeRuleForExitPlacementPick(draftPolicy, pick));
        const fallbackPolicy =
          !hasEditedRule && pickRule
            ? {
                schema_version: "policy_tree.v2" as const,
                rules: [detachExitPlacementRuleFromPick(pickRule, sliceId)],
              }
            : null;
        appendManagementV2ComparisonRun([sliceId], titleForApi, fallbackPolicy);
        started += 1;
        startedPicks.push(pick);
      }
      if (started > 0) {
        toast.success("Simulations started", {
          description: "Added pick runs to comparison. Results appear as each job finishes.",
        });
        markPicksRanAndClearSelection(startedPicks);
      }
    } catch (e) {
      const description =
        e instanceof ReportsApiError
          ? e.detail?.trim() || e.message
          : e instanceof Error
            ? e.message
            : "Could not run management simulation.";
      if (e instanceof ReportsApiError && e.status === 400) {
        setExitPlacementSimLegError(description);
      }
      if (shouldSkipSimulateFailureToast(e)) return;
      presentMutationError(e, "Simulation failed");
      throw e;
    }
  },
    [
      appendManagementV2ComparisonRun,
      comparisonRuns.length,
      scopedReportId,
      selectedExitPlacementPicks,
      picks,
      markPicksRanAndClearSelection,
    ]
  );

  const queueCombinedExitPlacementPickRun = useCallback(
    (runTitleRaw: string, opts?: { pickIds?: string[] }) => {
    if (!scopedReportId) {
      toast.warning("Report required", { description: "Exit placement simulation must run in a report context." });
      return;
    }
    const atomicLeafPicks = opts?.pickIds?.length
      ? importableExitPlacementPicks.filter((p) => opts.pickIds!.includes(p.id))
      : selectedExitPlacementPicks;
    const atomicChildBundles =
      opts?.pickIds?.length
        ? []
        : combinedBundles.filter((b) => selectedBundleIds.has(b.id));
    const atomicChildIds = [
      ...atomicLeafPicks.map((p) => p.id),
      ...atomicChildBundles.map((b) => b.id),
    ];

    if (atomicChildIds.length < 2) {
      toast.info("No picks selected", {
        description: "Select two or more cards (single picks, executed singles, or combined runs).",
      });
      return;
    }
    if (comparisonRuns.length >= MAX_COMPARISON_RUNS) {
      toastSimulatorComparisonAtLimit(scopedReportId);
      return;
    }

    const nextRunTitle = normalizeMandatorySimulatorRunTitle(runTitleRaw);
    if (!nextRunTitle) {
      toast.info("Enter a run title", {
        description: "Add a title for this run before simulating. It appears in results and is used when saving a simulation.",
      });
      return;
    }

    const pickByIdAll = new Map<string, ReportPick>();
    for (const p of picks) pickByIdAll.set(p.id, p);
    const bundleByIdAll = new Map<string, SimulatorPickBundleCard>();
    for (const b of combinedBundles) bundleByIdAll.set(b.id, b);

    const expandedPickIds = new Set<string>();
    const visitedCards = new Set<string>();
    const collectLeafPicks = (cardId: string) => {
      if (visitedCards.has(cardId)) return;
      visitedCards.add(cardId);
      const pick = pickByIdAll.get(cardId);
      if (pick) {
        expandedPickIds.add(pick.id);
        return;
      }
      const bundle = bundleByIdAll.get(cardId);
      if (!bundle) return;
      for (const childId of bundle.children) collectLeafPicks(childId);
    };

    // Expand only for policy/slice computation; combined usage counters apply only
    // to the immediate atomic children (lane stays Available Single Picks until run separately).
    for (const id of atomicChildIds) collectLeafPicks(id);

    const expandedLeafPicks: ReportPick[] = [];
    expandedPickIds.forEach((id) => {
      const p = pickByIdAll.get(id);
      if (p) expandedLeafPicks.push(p);
    });

    const sliceIds = sliceIdsFromExitPlacementPicks(expandedLeafPicks);
    if (sliceIds.length === 0) {
      toast.info("No slices", { description: "Selected picks do not include explorer slice ids." });
      return;
    }

    setExitPlacementSimLegError(null);
    try {
      const fallbackPolicy = buildDetachedPolicyFromExitPlacementPicks(expandedLeafPicks);
      const runId = appendManagementV2ComparisonRun(sliceIds, nextRunTitle, fallbackPolicy);

      // Persist bundle card + increment immediate children usage counts.
      const bundleCardId = genEditorId();
      addBundleCard({
        id: bundleCardId,
        createdAtMs: Date.now(),
        reportBucketId: bundlesBucketId,
        title: nextRunTitle,
        children: atomicChildIds,
        // Sibling EP rules are OR at rule level (either context can fire).
        combineOp: "or",
        action: picksSimAction ?? undefined,
        status: "running",
        comparisonRunId: runId,
        jobId: undefined,
        metrics: null,
        used_as_child_count: 0,
      });

      for (const p of atomicLeafPicks) {
        incrementPickUsedInCombinedCount(pickBucketId(p), p.id, 1);
      }
      for (const b of atomicChildBundles) {
        incrementBundleUsedAsChildCount(bundlesBucketId, b.id, 1);
      }

      toast.success("Simulation started", {
        description: "Added combined run to comparison. Results appear when the job finishes.",
      });
      markLeafPicksRanAndClearSelection(atomicLeafPicks);
    } catch (e) {
      const description =
        e instanceof ReportsApiError
          ? e.detail?.trim() || e.message
          : e instanceof Error
            ? e.message
            : "Could not run management simulation.";
      if (e instanceof ReportsApiError && e.status === 400) {
        setExitPlacementSimLegError(description);
      }
      if (shouldSkipSimulateFailureToast(e)) return;
      presentMutationError(e, "Simulation failed");
      throw e;
    }
  },
    [
      appendManagementV2ComparisonRun,
      comparisonRuns.length,
      importableExitPlacementPicks,
      scopedReportId,
      selectedExitPlacementPicks,
      picks,
      combinedBundles,
      selectedBundleIds,
      bundlesBucketId,
      addBundleCard,
      incrementPickUsedInCombinedCount,
      incrementBundleUsedAsChildCount,
      picksSimAction,
      markLeafPicksRanAndClearSelection,
    ]
  );

  const handleOverlapRunCombinedExitPlacement = useCallback(
    (pickIds: string[]) => {
      const idSet = new Set(pickIds);
      setSelectedPickIds(idSet);
      setSelectedBundleIds(new Set());
      onCloseOverlapDialog();
      const labels = pickIds
        .map((id) => importableExitPlacementPicks.find((p) => p.id === id))
        .filter((pick): pick is ReportPick => Boolean(pick))
        .map((pick) => exitPlacementPickDisplayLabel(pick));
      const title =
        normalizeMandatorySimulatorRunTitle(labels.join(" + ")) || "Combined exit placement";
      queueCombinedExitPlacementPickRun(title, { pickIds });
    },
    [importableExitPlacementPicks, onCloseOverlapDialog, queueCombinedExitPlacementPickRun]
  );

  const handleClearExitPlacementPicks = useCallback(() => {
    if (scopedReportId) clearPicksForReport(scopedReportId);
    else clearAllPicks();
    toast.info(scopedReportId ? "Cleared picks for this report" : "Cleared all picks");
  }, [scopedReportId]);

  const handleRemoveSimulatorPick = useCallback((p: ReportPick) => {
    removePick(pickBucketId(p), p.id);
    toast.info("Removed");
  }, []);

  const handleRemoveSimulatorBundle = useCallback(
    (b: SimulatorPickBundleCard) => {
      const bucket = b.reportBucketId;
      for (const childId of b.children) {
        incrementPickUsedInCombinedCount(bucket, childId, -1);
        incrementBundleUsedAsChildCount(bucket, childId, -1);
      }
      removeBundleCard(bucket, b.id);
      setSelectedBundleIds((prev) => {
        if (!prev.has(b.id)) return prev;
        const next = new Set(prev);
        next.delete(b.id);
        return next;
      });
      toast.info("Removed");
    },
    []
  );

  const handleMoveSimulatorPickLane = useCallback((p: ReportPick, lane: SimulatorPickLane) => {
    setPickSimulatorLane(pickBucketId(p), p.id, lane);
    const label =
      lane === "ran"
        ? "Executed single picks"
        : lane === "combined"
          ? "Executed combined picks"
          : "Available Single Picks";
    toast.success(`Moved to ${label}`);
  }, []);

  const v2PolicyRuleCount = v2PolicyParsed?.rules?.length ?? 0;

  const clearExitPlacementPolicyTree = useCallback(() => {
    commitV2PolicyDraft({ schema_version: "policy_tree.v2", rules: [] });
    toast.info("Cleared policy tree");
  }, [commitV2PolicyDraft]);

  const removeExitPlacementPolicyRule = useCallback((ruleId: string) => {
    const prev = v2PolicyDraftRef.current;
    let policy: PolicyTreeV2;
    try {
      policy = JSON.parse(prev) as PolicyTreeV2;
    } catch {
      return;
    }
    commitV2PolicyDraft(removePolicyTreeRule(policy, ruleId));
    toast.info("Removed from policy tree");
  }, [commitV2PolicyDraft]);

  const renderManagementV2Panel = () => (
    <ChakraVStack align="stretch" spacing={3} w="100%">
      <Field>
        <FieldLabel className={SIMULATOR_OPTION_FIELD_LABEL_CLASS}>Exit placement policy</FieldLabel>
        <FieldDescription className={SIMULATOR_OPTION_FIELD_DESC_CLASS}>
          Build your SL/TP policy from simulator picks — select contexts in the picks table, use{" "}
          <strong>Insert into policy tree</strong>, then review rules here. Removing a rule only edits this tree; your
          picks stay in the table above. Edit SL/TP distance (ATR) under each rule, then run to compare baseline vs
          managed exits.
          {props.tradeSideFilter && props.tradeSideFilter !== "both" ? (
            <> Trade side filter: <strong>{props.tradeSideFilter}</strong>.</>
          ) : null}
        </FieldDescription>
      </Field>

      <Flex
        align="flex-end"
        flexWrap="wrap"
        gap={3}
        w="100%"
        rowGap={3}
        justify="space-between"
        mb={4}
        data-simulator-run-toolbar=""
      >
        <HStack spacing={3} align="flex-end" flexWrap="wrap" flex="0 1 auto" minW={0}>
          <NextRunTitleField
            id="builder-run-title"
            value={builderRunTitleDraft}
            onChange={setBuilderRunTitleDraft}
          />
          <ExitPlacementSimLegSelector
            id="builder-run-simulate-legs"
            variant="builder"
            value={exitPlacementSimulateLegs}
            onChange={(v) => {
              setExitPlacementSimulateLegsSynced(v);
              setExitPlacementSimLegError(null);
            }}
            availability={simulateLegAvailability}
            disabled={importableExitPlacementPicks.length === 0 && v2PolicyRuleCount === 0}
            inlineError={exitPlacementSimLegError}
          />
        </HStack>

        <HStack spacing={2} flexWrap="wrap" align="flex-end" flexShrink={0}>
          <ShadcnButton
            type="button"
            variant="outline"
            size="sm"
            data-font="primary"
            data-trade-log-action="delete"
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={clearExitPlacementPolicyTree}
            disabled={v2PolicyRuleCount === 0}
          >
            Clear policy tree
          </ShadcnButton>
          {policyViewMode === "builder" ? (
            <ShadcnButton
              type="button"
              variant="outline"
              size="sm"
              data-font="primary"
              data-market-context-bulk-toggle=""
              className="h-8 min-w-8 shrink-0 px-3 text-[0.75rem] normal-case tracking-normal"
              title={v2AllPolicyGroupsExpanded ? "Collapse all policy items" : "Expand all policy items"}
              aria-label={v2AllPolicyGroupsExpanded ? "Collapse all policy items" : "Expand all policy items"}
              onClick={
                v2AllPolicyGroupsExpanded
                  ? () => setCollapsedGroupIds(new Set(v2AllPolicyNodeIds))
                  : () => setCollapsedGroupIds(new Set())
              }
              disabled={v2AllPolicyNodeIds.length === 0}
            >
              {v2AllPolicyGroupsExpanded ? "Hide all" : "Show all"}
            </ShadcnButton>
          ) : null}
          <Tabs
            data-trade-log-tabs=""
            value={policyViewMode}
            onValueChange={(next) => {
              if (next === "builder" || next === "graph") setPolicyViewMode(next);
            }}
            className="w-fit shrink-0"
          >
            <TabsList className="min-w-max">
              <TabsTrigger value="builder">Builder</TabsTrigger>
              <TabsTrigger value="graph">Graph</TabsTrigger>
            </TabsList>
          </Tabs>
          <ShadcnButton
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            disabled={
              simulateInflightCount >= MAX_SIMULATE_INFLIGHT ||
              v2PolicyRuleCount === 0 ||
              !builderNextRunTitleReady
            }
            onClick={() => void runManagementV2Simulation()}
          >
            Run & add to comparison
          </ShadcnButton>
        </HStack>
      </Flex>

      {v2PolicyParsed && (v2PolicyParsed.rules?.length ?? 0) > 0 ? (
        catalogLoading ? (
          <Text fontSize="sm" color={subtleText} fontFamily="'IBM Plex Sans', sans-serif">
            Loading policy builder…
          </Text>
        ) : (
          <ExitPlacementPolicyTreeEditor
            policy={v2PolicyParsed}
            ruleDisplayNodes={v2RuleConditionDisplayNodes}
            catalogFields={catalogFields}
            catalogFieldsForPicker={catalogFieldsForPicker}
            fieldsByPath={fieldsByPath}
            fieldExplanationFor={fieldExplanationFor}
            policyViewMode={policyViewMode}
            collapsedGroupIds={collapsedGroupIds}
            setCollapsedGroupIds={setCollapsedGroupIds}
            hideGroupAction
            ruleLabelsById={exitPlacementRuleLabels}
            onPolicyChange={commitV2PolicyDraft}
            onRemoveRule={removeExitPlacementPolicyRule}
          />
        )
      ) : (
        <Text fontSize="sm" color={subtleText} fontFamily="'IBM Plex Sans', sans-serif">
          Policy tree is empty. Select picks above and use <strong>Insert into policy tree</strong>, or run simulations
          directly from picks without loading them here.
        </Text>
      )}
    </ChakraVStack>
  );

  const renderPolicyBuilder = (includeRunInToolbar: boolean) =>
    isManagementV2Mode ? (
      renderManagementV2Panel()
    ) : catalogLoading ? (
      <Text color={subtleText}>Loading filter catalog…</Text>
    ) : catalogFields.length === 0 ? (
      <Text color={subtleText}>No catalog fields available.</Text>
    ) : (
      <>
        <Flex
          align="flex-end"
          flexWrap="wrap"
          gap={3}
          w="100%"
          rowGap={3}
          justify="space-between"
          mb={4}
          data-simulator-run-toolbar=""
        >
          <HStack spacing={3} align="flex-end" flexWrap="wrap" flex="0 1 auto" minW={0}>
            <NextRunTitleField
              id="builder-run-title"
              value={builderRunTitleDraft}
              onChange={setBuilderRunTitleDraft}
            />
            <SimulatorActionField
              id="builder-run-action"
              variant="builder"
              value={builderSimAction}
              onChange={setBuilderSimAction}
              mixedActions={builderActionsMixed}
            />
          </HStack>

          <HStack spacing={2} flexWrap="wrap" align="flex-end" flexShrink={0}>
              <ShadcnButton
                type="button"
                variant="outline"
                size="sm"
                data-font="primary"
                data-trade-log-action="delete"
                className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
                onClick={clearPolicyTree}
                disabled={!canClearPolicy}
              >
                {clearPolicyLabel}
              </ShadcnButton>
              {policyViewMode === "builder" ? (
                <ShadcnButton
                  type="button"
                  variant="outline"
                  size="sm"
                  data-font="primary"
                  data-market-context-bulk-toggle=""
                  className="h-8 min-w-8 shrink-0 px-3 text-[0.75rem] normal-case tracking-normal"
                  title={allPolicyGroupsExpanded ? "Collapse all policy items" : "Expand all policy items"}
                  aria-label={allPolicyGroupsExpanded ? "Collapse all policy items" : "Expand all policy items"}
                  onClick={allPolicyGroupsExpanded ? collapseAllGroups : expandAllGroups}
                  disabled={allPolicyNodeIds.length === 0}
                >
                  {allPolicyGroupsExpanded ? "Hide all" : "Show all"}
                </ShadcnButton>
              ) : null}
              <Tabs
                data-trade-log-tabs=""
                value={policyViewMode}
                onValueChange={(next) => {
                  if (next === "builder" || next === "graph") setPolicyViewMode(next);
                }}
                className="w-fit shrink-0"
              >
                <TabsList className="min-w-max">
                  <TabsTrigger value="builder">Builder</TabsTrigger>
                  <TabsTrigger value="graph">Graph</TabsTrigger>
                </TabsList>
              </Tabs>
              {includeRunInToolbar ? (
                <ShadcnButton
                  type="button"
                  variant="gradient"
                  size="sm"
                  data-font="primary"
                  className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
                  onClick={() => void runSimulation()}
                  disabled={builderRunDisabled}
                  aria-busy={simLoading}
                >
                  {simLoading ? (
                    <>
                      <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
                      Running…
                    </>
                  ) : simulateRateLimit.isCoolingDown ? (
                    `Wait ${simulateRateLimit.retryAfterSeconds}s`
                  ) : (
                    "Run & add to comparison"
                  )}
                </ShadcnButton>
              ) : null}
          </HStack>
        </Flex>
        <VStack align="stretch" spacing={4}>
          {policyViewMode === "builder" ? (
            <PolicyTreeNodesAccordion
              nodes={policyEditorDisplayRoots}
              catalogFields={catalogFields}
              catalogFieldsForPicker={catalogFieldsForPicker}
              fieldsByPath={fieldsByPath}
              ruleLeaves={ruleLeaves}
              collapsedNodeIds={collapsedGroupIds}
              setCollapsedNodeIds={setCollapsedGroupIds}
              canRemoveGroup
              onUpdateRule={onUpdateRule}
              onRulePathChange={onRulePathChange}
              onRuleOpChange={onRuleOpChange}
              onGroupOpChange={onGroupOpChange}
              onGroupActionChange={onGroupActionChange}
              defaultGroupAction={defaultGroupAction}
              onGroupLabelChange={onGroupLabelChange}
              onGoToSourcePick={goToSourcePick}
              onRemove={onRemove}
              onAddRule={onAddRule}
              draggedRuleId={draggedRuleId}
              onRuleDragStart={onRuleDragStart}
              onRuleDragEnd={onRuleDragEnd}
              onGroupDragOver={onGroupDragOver}
              onGroupDropRule={onGroupDropRule}
              numericHintForPath={numericGlossaryHelperForPath}
              fieldExplanationFor={fieldExplanationFor}
            />
          ) : (
            <PolicyGraphCanvas
              roots={policyEditorDisplayRoots}
              catalogFields={catalogFields}
              catalogFieldsForPicker={catalogFieldsForPicker}
              fieldsByPath={fieldsByPath}
              defaultGroupAction={defaultGroupAction}
              fieldExplanationFor={fieldExplanationFor}
              onAddGroupNode={onAddGroupToPolicyRoot}
              onConnectRuleToGroup={onConnectRuleToGroup}
              onDetachConnection={onDetachConnection}
              onRemoveNode={onRemove}
              onUpdateRule={onUpdateRule}
              onRulePathChange={onRulePathChange}
              onRuleOpChange={onRuleOpChange}
              onGroupOpChange={onGroupOpChange}
              onGroupActionChange={onGroupActionChange}
              onGroupLabelChange={onGroupLabelChange}
              onAddRuleToGroup={onAddRule}
            />
          )}
          {prefillManagementPolicy && (prefillManagementPolicy.rules?.length ?? 0) > 0 ? (
            <Box data-simulator-prefill-exit-placement="">
              <Text
                fontSize="xs"
                fontWeight={500}
                color="#38BBD1"
                mb={2}
                fontFamily="var(--font-primary)"
              >
                Exit placement
              </Text>
              <ExitPlacementPolicyTreeEditor
                policy={prefillManagementPolicy}
                ruleDisplayNodes={prefillManagementDisplayNodes}
                catalogFields={catalogFields}
                catalogFieldsForPicker={catalogFieldsForPicker}
                fieldsByPath={fieldsByPath}
                fieldExplanationFor={fieldExplanationFor}
                policyViewMode={policyViewMode}
                collapsedGroupIds={collapsedGroupIds}
                setCollapsedGroupIds={setCollapsedGroupIds}
                hideGroupAction
                readOnly
              />
            </Box>
          ) : null}
          {policyViewMode === "builder" ? (
            <HStack spacing={2} flexWrap="wrap">
              <SimulatorPolicyAddButton kind="group" onClick={onAddGroupToPolicyRoot} />
            </HStack>
          ) : null}
        </VStack>
      </>
    );

  const simulatorWizardReviewBlock = (
    <div className="flex w-full min-w-0 flex-col gap-5">
      {wizardMdGate.connectGate ? (
        <WizardMarketDataOhlcGateNotice
          gate={wizardMdGate.connectGate}
          history={history}
          onOpenMarketDataSetup={() => wizardMdGate.openMarketDataSetup()}
        />
      ) : null}
      <Field className="w-full">
        <FieldLabel className={SIMULATOR_OPTION_FIELD_LABEL_CLASS}>Review</FieldLabel>
        <FieldDescription className={SIMULATOR_OPTION_FIELD_DESC_CLASS}>
          Confirm your choices below, then generate the simulation.
        </FieldDescription>
      </Field>
      <div data-shadcn-data-table="" className="w-full min-w-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[38%] whitespace-nowrap text-[color:var(--brand-tropical-surf)]">
                Setting
              </TableHead>
              <TableHead className="text-[color:var(--brand-tropical-surf)]">Value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(
              [
                ["Broker account", brokerAccountMenuLabel],
                ["Date range", simulatorScopeWindowSummaryLabel],
                ["Symbol", symbol.trim() || "All symbols"],
                ["Candle timeframe", candleTimeframe],
                ["Run title", normalizeMandatorySimulatorRunTitle(builderRunTitleDraft) || "—"],
                ["Action", builderActionMenuLabel],
                ["Strategy", linkedStrategyMenuLabel],
                [
                  "Policy",
                  `${ruleLeaves} rule${ruleLeaves === 1 ? "" : "s"}, ${policyNodesBelowRootShell} policy node${
                    policyNodesBelowRootShell === 1 ? "" : "s"
                  } (${policyViewMode === "builder" ? "Builder" : "Graph"} view)`,
                ],
              ] as const
            ).map(([label, value]) => (
              <TableRow key={label}>
                <TableCell className="align-top whitespace-nowrap font-medium text-muted-foreground">
                  {label}
                </TableCell>
                <TableCell className="align-top whitespace-normal text-foreground">{value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const handleSaveBacktestForRun = useCallback(
    (id: string) => {
      void saveBacktestForRun(id);
    },
    [saveBacktestForRun]
  );

  const handleSaveStrategyVersionForRun = useCallback(
    (id: string) => {
      void saveStrategyVersionForRun(id);
    },
    [saveStrategyVersionForRun]
  );

  const handleComposeExitPlacementForRun = useCallback((id: string) => {
    setExitPlacementComposeRunId(id);
  }, []);

  const exitPlacementComposeRun = useMemo(
    () =>
      exitPlacementComposeRunId
        ? comparisonRuns.find((r) => r.id === exitPlacementComposeRunId) ?? null
        : null,
    [comparisonRuns, exitPlacementComposeRunId]
  );

  const exitPlacementComposeContext = useMemo(() => {
    if (!exitPlacementComposeRun || !scopedReportId) return null;
    const strategyId =
      exitPlacementComposeRun.linkedStrategyId?.trim() ||
      reportStrategyId?.trim() ||
      linkedStrategyIdForNewRuns?.trim() ||
      "";
    const sliceIds = sliceIdsFromComparisonRun(exitPlacementComposeRun);
    if (!strategyId || sliceIds.length === 0) return null;
    return {
      reportId: scopedReportId,
      strategyId,
      sliceIds,
      simulateLegs: exitPlacementComposeRun.simulateLegs ?? exitPlacementSimulateLegs,
      runLabel: getComparisonRunSavedTitle(exitPlacementComposeRun),
    };
  }, [
    exitPlacementComposeRun,
    exitPlacementSimulateLegs,
    linkedStrategyIdForNewRuns,
    reportStrategyId,
    scopedReportId,
  ]);

  const outerPadding =
    props.layout === "embedded"
      ? { pt: "0px", pb: "0px", px: "0px" }
      : shadcnInset
        ? { pt: 0, pb: "24px", px: 0 }
        : {
            pt: { base: "100px", md: "80px", lg: "40px" },
            pb: "24px",
            px: { base: "20px", xl: "40px" },
          };

  return (
    <Box
      data-simulator-standalone-page={embeddedInReport ? undefined : ""}
      data-simulator-results-page={!embeddedInReport && props.resultsOnlyLayout ? "" : undefined}
      w="100%"
      maxW="100%"
      minW={0}
      minH={isEmbeddedResultsOnly ? "100%" : embeddedInReport ? undefined : pageMinHeight}
      flex={isEmbeddedResultsOnly ? 1 : undefined}
      h={isEmbeddedResultsOnly ? "100%" : undefined}
      display={isEmbeddedResultsOnly ? "flex" : undefined}
      flexDirection={isEmbeddedResultsOnly ? "column" : undefined}
      pt={outerPadding.pt as any}
      pb={outerPadding.pb as any}
      px={outerPadding.px as any}
      sx={props.layout === "embedded" ? reportPageFieldFocusSx : undefined}
    >
      <SimulatorMainColumn layout={props.layout}>
        {props.resultsOnlyLayout ? (
          <Box
            w="100%"
            minW={0}
            display="flex"
            flexDirection="column"
            gap={4}
            flex={isEmbeddedResultsOnly ? 1 : undefined}
            minH={isEmbeddedResultsOnly ? "100%" : undefined}
            h={isEmbeddedResultsOnly ? "100%" : undefined}
          >
            {isEmbeddedResultsOnly ? (
              <SimulationResultsDrawerChrome
                onClose={props.onClose}
                run={workspaceResultsRun}
                loading={Boolean(props.initialBacktestId && initialBacktestLoading)}
                disabled={Boolean(initialBacktestError)}
                onRename={handleWorkspaceSimulationRename}
                onDelete={handleWorkspaceSimulationDelete}
                onExport={exportWorkspaceSimulationJson}
                onShowPolicy={handleWorkspaceShowPolicy}
                onSaveAsPolicyVersion={handleWorkspaceSaveAsPolicyVersion}
                canSaveAsPolicyVersion={canWorkspaceSaveAsPolicyVersion}
              />
            ) : null}
            {props.initialBacktestId && initialBacktestLoading ? (
              <div
                data-workspace-simulation-drawer-loading=""
                className={
                  isEmbeddedResultsOnly
                    ? "flex min-h-0 flex-1 flex-col items-center justify-center"
                    : "flex w-full flex-col items-center justify-center py-16"
                }
              >
                <StatisticsEmptyState
                  variant="loading"
                  fillParent={isEmbeddedResultsOnly}
                  title="Loading simulation"
                  description="Fetching simulation results and policy details."
                />
              </div>
            ) : (
              <MemoizedComparisonResultsSection
                hideRunTabs
                showEntityTitleCard={props.layout === "embedded"}
                hideShowPolicyButton={isEmbeddedResultsOnly}
                hideSaveAsStrategyVersionButton={isEmbeddedResultsOnly}
                onRegisterOpenPolicyModal={
                  isEmbeddedResultsOnly ? registerOpenPolicyModal : undefined
                }
                linkedStrategyName={linkedStrategyMenuLabel}
                initialBacktestId={props.initialBacktestId}
                catalogError={catalogError}
                initialBacktestError={props.initialBacktestId ? initialBacktestError : null}
                comparisonRuns={comparisonRuns}
                fieldsByPath={fieldsByPath}
                borderColor={borderColor}
                textColor={textColor}
                subtleText={subtleText}
                evidencePreBg={evidencePreBg}
                evidencePreBorder={evidencePreBorder}
                variant={variant}
                brokerAccountCode={effectiveBrokerAccountCode}
                reportingTimeZone={reportingTimeZone}
                initialSelectedRunId={props.initialSelectedRunId}
                onActiveRunLabelChange={props.onActiveSimulationTitleChange}
                onRemoveRun={removeComparisonRun}
                onSaveRunLabel={saveComparisonRunLabel}
                onRetryRun={retryComparisonRun}
                onSaveBacktest={handleSaveBacktestForRun}
                onSaveAsStrategyVersion={handleSaveStrategyVersionForRun}
                enableExitPlacementCompose={isManagementV2Mode}
                onComposeExitPlacementForRun={handleComposeExitPlacementForRun}
                linkedStrategyIdFallback={linkedStrategyIdForNewRuns}
                simulationOverlayMode={props.simulationOverlayMode}
                reportId={scopedReportId}
                reportCreatedAt={props.reportCreatedAt}
                prefillWindow={props.prefillWindow}
                defaultCandleTimeframe={candleTimeframe}
                linkedBacktests={props.linkedBacktests}
                linkedBacktestsRefreshKey={linkedBacktestsRefreshKey}
                onOpenCompareOverlap={scopedReportId ? openOverlapFromSavedSims : undefined}
              />
            )}
          </Box>
        ) : useSimulatorWizardLayout ? (
          <>
            <SimulatorRunWizardLayout
              subtleText={subtleText}
              borderColor={borderColor}
              cardBg={simulatorWizardCardBg}
              cardShadow={simulatorWizardCardShadow}
              step={simulatorWizardStep}
              onStepClick={simulatorWizardOnStepClick}
              onExit={exitSimulatorWizard}
              onCancel={exitSimulatorWizard}
              onFooterBack={simulatorWizardGoBack}
              onContinue={simulatorWizardGoNext}
              continueDisabled={simulatorWizardContinueDisabled}
              continueBlockedReason={simulatorWizardPolicyContinueBlockedReason}
              onGenerate={() => {
                if (simulatorWizardStep !== 2 || !wizardGenerateArmed) {
                  toast.info("Continue to Review first", {
                    description: "Open Review, then click Generate simulation.",
                  });
                  return;
                }
                void runSimulation();
              }}
              generating={simLoading}
              generateDisabled={
                !simulatorWizardPolicyStepComplete ||
                !wizardGenerateArmed ||
                wizardMdGate.runBlocked ||
                Boolean(props.workspaceSimulationBlockedReason?.trim())
              }
              embedded={props.layout === "embedded"}
            >
              {simulatorWizardStep === 0 ? scopeFieldsBox : null}
              {simulatorWizardStep === 1 ? renderPolicyBuilder(false) : null}
              {simulatorWizardStep === 2 ? simulatorWizardReviewBlock : null}
            </SimulatorRunWizardLayout>
            <CatalogErrorBanner message={catalogError} />
          </>
        ) : (
          <>
        <SimulatorPageHero
          textColor={textColor}
          subtleText={subtleText}
          borderColor={borderColor}
          catalogVersion={catalogVersion}
          policySchemaVersion={policySchemaVersion}
          showPageTitle={isStandalone}
          comparisonRunsUsed={isStandalone ? comparisonRuns.length : undefined}
          onWizardExit={
            isStandalone && (props.onWizardExit || props.wizardExitHref)
              ? () => {
                  if (props.onWizardExit) {
                    props.onWizardExit();
                    return;
                  }
                  history.push(props.wizardExitHref as string);
                }
              : undefined
          }
          wizardExitLabel={props.wizardExitLabel ?? "Back to simulator"}
          onOpenSavedBacktests={
            props.hideSavedBacktestsButton
              ? undefined
              : () => history.push(props.savedSimulationsHref ?? SIMULATOR_LIST_PATH)
          }
        />

        <CatalogErrorBanner message={catalogError} />

        {showPicksSection ? (
        <Box ref={picksCardRef} w="100%" minW={0}>
        <Card {...innerSectionCardProps} w="100%" minW={0}>
          <CardHeader pb={4}>
            <Field>
              <FieldLabel className={SIMULATOR_OPTION_FIELD_LABEL_CLASS}>
                {isManagementV2Mode ? "Simulator picks" : "Option 1: Run from Picks"}
              </FieldLabel>
              <FieldDescription className={SIMULATOR_OPTION_FIELD_DESC_CLASS}>
                {isManagementV2Mode
                  ? "Contexts you added from the Exit Placement explorer on the Market tab. Drag cards between Available Single Picks, Executed single, and Combined lanes, select picks, optionally insert them into the policy tree (Option 2), then run. Running separately moves picks to Executed single; combined runs keep picks in Available Single Picks and track usage on the card."
                  : "Use saved picks from condition analysis (buckets/situations/slices). Drag cards between Available Single Picks, Executed single, and Combined lanes, select convertible picks, then run individually or combined. Running separately moves picks to Executed single; combined runs keep picks in Available Single Picks and track usage on the card."}
              </FieldDescription>
            </Field>
          </CardHeader>
          <CardBody pt={0}>
            {!isManagementV2Mode ? (
            <Flex
              align="flex-end"
              flexWrap="wrap"
              gap={3}
              w="100%"
              rowGap={3}
              justify="space-between"
              mb={4}
              data-simulator-run-toolbar=""
            >
              <HStack spacing={3} align="flex-end" flexWrap="wrap" flex="0 1 auto" minW={0}>
                <NextRunTitleField
                  id="picks-run-title"
                  value={picksRunTitleDraft}
                  onChange={setPicksRunTitleDraft}
                />
                <SimulatorActionField
                  id="picks-run-action"
                  variant="picks"
                  value={picksSimAction}
                  onChange={setPicksSimAction}
                  disabled={selectedImportablePicks.length === 0 && selectedBundleIds.size === 0}
                />

                {picksCombinedAtomicCount > 1 ? (
                  <SimulatorCombineField
                    id="picks-run-combine"
                    value={combineExplicitlySelected ? combineSelectedOp : null}
                    onChange={(value) => {
                      setCombineSelectedOp(value);
                      setCombineExplicitlySelected(true);
                    }}
                    disabled={picksCombinedAtomicCount < 2}
                  />
                ) : null}
              </HStack>

              <HStack spacing={2} flexWrap="wrap" align="flex-end" flexShrink={0}>
                <ShadcnButton
                  type="button"
                  variant="outline"
                  size="sm"
                  data-font="primary"
                  data-trade-log-action="delete"
                  className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
                  onClick={() => {
                    if (scopedReportId) clearPicksForReport(scopedReportId);
                    else clearAllPicks();
                    toast.info(scopedReportId ? "Cleared picks for this report" : "Cleared all picks");
                  }}
                  disabled={picks.length === 0 || importingPicks}
                >
                  Clear picks
                </ShadcnButton>
                <ShadcnButton
                  type="button"
                  variant="outline"
                  size="sm"
                  data-font="primary"
                  data-market-context-bulk-toggle=""
                  className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
                  onClick={insertPicksIntoTree}
                  disabled={
                    importingPicks ||
                    (selectedImportablePicks.length === 0 && selectedBundleIds.size === 0)
                  }
                >
                  Insert into policy tree
                </ShadcnButton>
                {scopedReportId && picksCombinedAtomicCount > 1 ? (
                  <ShadcnButton
                    type="button"
                    variant="outline"
                    size="sm"
                    data-font="primary"
                    data-market-context-bulk-toggle=""
                    className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
                    onClick={openOverlapFromPicks}
                    disabled={picksCombinedAtomicCount < 2}
                  >
                    Check overlap
                  </ShadcnButton>
                ) : null}
                {selectedImportableLaneModes.hasSingleOrRan &&
                (!importingPicks || pickRunLoadingAction === "individual") ? (
                  <ShadcnButton
                    type="button"
                    variant="gradient"
                    size="sm"
                    data-font="primary"
                    className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
                    onClick={() => void queuePickComparisonRuns()}
                    disabled={picksSeparateRunDisabled}
                    aria-busy={importingPicks && pickRunLoadingAction === "individual"}
                  >
                    {importingPicks && pickRunLoadingAction === "individual" ? (
                      <>
                        <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
                        Running…
                      </>
                    ) : selectedImportablePicks.filter((p) => getSimulatorLane(p) !== "combined").length >
                      1 ? (
                      "Run picks separately"
                    ) : (
                      "Run selected pick"
                    )}
                  </ShadcnButton>
                ) : null}
                {picksCombinedAtomicCount > 1 &&
                (!importingPicks || pickRunLoadingAction === "combined") ? (
                  <ShadcnButton
                    type="button"
                    variant="gradient"
                    size="sm"
                    data-font="primary"
                    className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
                    onClick={() => void queueCombinedPickRun()}
                    disabled={picksCombinedRunDisabled}
                    aria-busy={importingPicks && pickRunLoadingAction === "combined"}
                  >
                    {importingPicks && pickRunLoadingAction === "combined" ? (
                      <>
                        <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
                        Running…
                      </>
                    ) : (
                      "Run picks combined"
                    )}
                  </ShadcnButton>
                ) : null}
                </HStack>
            </Flex>
            ) : (
              <ExitPlacementPicksRunToolbar
                simulateLegs={exitPlacementSimulateLegs}
                onSimulateLegsChange={setExitPlacementSimulateLegsSynced}
                legAvailability={simulateLegAvailability}
                simulateLegError={exitPlacementSimLegError}
                onClearSimulateLegError={() => setExitPlacementSimLegError(null)}
                selectedPickCount={selectedExitPlacementPicks.length}
                importablePickCount={importableExitPlacementPicks.length}
                multiPickSelected={
                  selectedExitPlacementPicks.filter((p) => getSimulatorLane(p) !== "combined").length > 1
                }
                showSeparateRun={selectedExitPlacementLaneModes.hasSingleOrRan}
                showCombinedRun={exitPlacementCombinedAtomicCount > 1}
                simulateAtInflightLimit={simulateInflightCount >= MAX_SIMULATE_INFLIGHT}
                separateRunDisabled={exitPlacementSeparateRunDisabled}
                combinedRunDisabled={exitPlacementCombinedRunDisabled}
                picksCount={picks.length}
                onClearPicks={handleClearExitPlacementPicks}
                onInsertIntoPolicyTree={insertExitPlacementPicksIntoPolicyTree}
                onRunSeparate={queueExitPlacementPickComparisonRuns}
                onRunCombined={queueCombinedExitPlacementPickRun}
                onCheckOverlap={scopedReportId ? openOverlapFromExitPlacementPicks : undefined}
                checkOverlapDisabled={
                  selectedExitPlacementPicks.length + selectedBundleIds.size < 2
                }
                importingPicks={false}
                pickRunLoadingAction={null}
              />
            )}

            {picks.length === 0 ? (
              <SimulatorAlert tone="info">
                {isManagementV2Mode
                  ? "No picks yet. Use + on the Market tab explorer to add SL/TP contexts."
                  : "No picks selected. Please select some from the Entry Analysis tables."}
              </SimulatorAlert>
            ) : (
              <MemoizedSimulatorPicksKanban
                picks={picks}
                combinedBundles={combinedBundles}
                selectedAtomicIds={selectedAtomicIds}
                onSelectedAtomicIdsChange={handleSelectedAtomicIdsChange}
                linkedPickId={linkedPickId}
                scopedReportId={scopedReportId}
                pickDiag={pickDiag}
                quickStatsForPick={quickStatsForPick}
                quickStatsForBundle={(b) => b.metrics ?? null}
                onRemovePick={handleRemoveSimulatorPick}
                onRemoveBundle={handleRemoveSimulatorBundle}
                onMovePickLane={handleMoveSimulatorPickLane}
                clearSelectionButton={
                  selectedAtomicIds.size > 0 ? (
                    <ShadcnButton
                      type="button"
                      variant="outline"
                      size="sm"
                      data-font="primary"
                      data-trade-log-action="delete"
                      className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
                      onClick={() => {
                        setSelectedPickIds(new Set());
                        setSelectedBundleIds(new Set());
                      }}
                    >
                      Clear selection
                    </ShadcnButton>
                  ) : null
                }
              />
            )}
            {picks.length > 0 && importablePicks.length === 0 && !catalogLoading && !isManagementV2Mode ? (
              <SimulatorAlert tone="info" className="mt-3">
                {picksWithMappedContent.length === 0
                  ? "These picks don’t yet include machine-readable simulator rules. You can keep collecting them, but they can’t be auto-imported until we add mapping support."
                  : "These picks include bucket/situation rules or segment slices, but none can be imported yet. Wait for the filter catalog to load, or check that catalog fields exist for regime / market candle direction / trade side."}
              </SimulatorAlert>
            ) : null}
          </CardBody>
        </Card>
        </Box>
        ) : null}

        <Card {...innerSectionCardProps} w="100%" minW={0}>
          <CardHeader pb={2}>
            <Field>
              <FieldLabel className={SIMULATOR_OPTION_FIELD_LABEL_CLASS}>
                {isManagementV2Mode
                  ? "Option 2: Run from policy"
                  : isStandalone
                    ? "Run from policy"
                    : "Option 2: Run from Policy"}
              </FieldLabel>
              <FieldDescription className={SIMULATOR_OPTION_FIELD_DESC_CLASS}>
                {isManagementV2Mode
                  ? "Review the SL/TP policy tree you built from picks (Builder / Graph). Rules run sequentially on this report cohort — compare baseline vs managed exits after you run."
                  : isStandalone
                    ? "Build your rule tree, set account and date scope below, then run and add results to the comparison."
                    : "Build your own rule tree manually, choose scope inputs (timeframe/date range, symbol, candle timeframe), then run and add to comparison. Use this when you want full control over exact policy logic."}
              </FieldDescription>
            </Field>
          </CardHeader>
          <CardBody pt={0}>
            <VStack align="stretch" spacing={4}>
              {!isManagementV2Mode ? scopeFieldsBox : null}

              {renderPolicyBuilder(true)}
            </VStack>
          </CardBody>
        </Card>

        <MemoizedComparisonResultsSection
          comparisonRuns={comparisonRuns}
          fieldsByPath={fieldsByPath}
          borderColor={borderColor}
          textColor={textColor}
          subtleText={subtleText}
          evidencePreBg={evidencePreBg}
          evidencePreBorder={evidencePreBorder}
          variant={variant}
          brokerAccountCode={effectiveBrokerAccountCode}
          reportingTimeZone={reportingTimeZone}
          initialSelectedRunId={props.initialSelectedRunId}
          onRemoveRun={removeComparisonRun}
          onSaveRunLabel={saveComparisonRunLabel}
          onRetryRun={retryComparisonRun}
          onSaveBacktest={handleSaveBacktestForRun}
          onSaveAsStrategyVersion={handleSaveStrategyVersionForRun}
          enableExitPlacementCompose={isManagementV2Mode}
          onComposeExitPlacementForRun={handleComposeExitPlacementForRun}
          linkedStrategyIdFallback={linkedStrategyIdForNewRuns}
          simulationOverlayMode={props.simulationOverlayMode}
          reportId={scopedReportId}
          reportCreatedAt={props.reportCreatedAt}
          prefillWindow={props.prefillWindow}
          defaultCandleTimeframe={candleTimeframe}
          linkedBacktests={props.linkedBacktests}
          linkedBacktestsRefreshKey={linkedBacktestsRefreshKey}
          onOpenCompareOverlap={scopedReportId ? openOverlapFromSavedSims : undefined}
        />
          </>
        )}
      </SimulatorMainColumn>
      {exitPlacementComposeContext ? (
        <ExitPlacementComposeDialog
          isOpen={Boolean(exitPlacementComposeRunId)}
          onClose={() => setExitPlacementComposeRunId(null)}
          reportId={exitPlacementComposeContext.reportId}
          strategyId={exitPlacementComposeContext.strategyId}
          sliceIds={exitPlacementComposeContext.sliceIds}
          simulateLegs={exitPlacementComposeContext.simulateLegs}
          runLabel={exitPlacementComposeContext.runLabel}
          subtleText={subtleText}
          onComposeSuccess={props.onStrategyVersionSaved}
        />
      ) : null}
      {scopedReportId ? (
        <PolicyPickOverlapDialog
          open={overlapDialogOpen}
          onOpenChange={(next) => {
            if (!next) onCloseOverlapDialog();
            else onOpenOverlapDialog();
          }}
          reportId={scopedReportId}
          reportPicks={picks}
          linkedBacktests={props.linkedBacktests ?? []}
          catalogFields={catalogFields}
          marginalEqPrefixes={marginalEqPrefixMap}
          defaultTargetAction={picksSimAction ?? "veto"}
          initialSelection={overlapInitialSelection}
          pickToBacktestId={overlapPickToBacktestId}
          mode={isManagementV2Mode ? "exit_placement" : "regime"}
          entry={overlapEntry}
          onRunCombinedSim={isManagementV2Mode ? undefined : handleOverlapRunCombinedSim}
          onRunSuggestionSims={isManagementV2Mode ? undefined : handleOverlapRunSuggestionSims}
          onRunCombinedExitPlacementSim={
            isManagementV2Mode ? handleOverlapRunCombinedExitPlacement : undefined
          }
        />
      ) : null}
    </Box>
  );
}
